import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { GCPanels } from "@/components/GCPanels/GCPanels";
import { publishRange } from "@/utils/chartSync";
import {
  createChart,
  TickMarkType,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type Logical,
  type LogicalRange,
  type Time,
  type UTCTimestamp,
  LineStyle,
} from "lightweight-charts";
import {
  computeSMA,
  computeEMA,
  computeRSI,
  computeAnchoredMA,
  computeAnchoredRSI,
} from "@/utils/indicators";
import {
  useAppDispatch,
  useAppSelector,
  useTabUi,
  useTabOhlc,
  useTabAnalysis,
  useTabReplay,
  useCan,
} from "@/state/hooks";
import { cancelReplaySelect, enterReplay } from "@/state/slices/replaySlice";
import { clampToBar } from "@/utils/replayCursor";
import { ReplayBar } from "./ReplayBar";
import { CAP } from "@/api/capabilities";
import { fetchOhlc, forceRefreshOhlc, loadMoreOhlc, retryOhlc } from "@/state/slices/ohlcSlice";
import { bucketStartSec } from "@/hooks/barBucket";
import {
  selectEffectiveFibDiagonal,
  selectEffectiveFibLabelSide,
  selectEffectiveFibLevels,
  selectEffectiveFibLineStyle,
  selectEffectiveFibLineWidth,
  selectEffectiveFibShowDiagonal,
  selectEffectiveTheme,
  selectChartChrome,
  selectHasModeThemeOverride,
  selectPriceRangeStyle,
} from "@/state/selectors/effectiveTheme";
import {
  selectEffectiveWeeklyBehaviour,
  sessionDayFromBoundaries,
  sessionWeekFromBoundaries,
} from "@/state/selectors/effectiveWeeklyBehaviour";
import { selectEffectiveIndicators } from "@/state/selectors/effectiveIndicators";
import { fetchSessionBoundaries } from "@/state/slices/sessionBoundariesSlice";
import { useTickStream, type Tick } from "@/hooks/useTickStream";
import { OneClickTradePanel } from "@/components/OneClickTradePanel/OneClickTradePanel";
import { pushToast } from "@/state/slices/notificationsSlice";
import { api, ApiError } from "@/api/client";
import {
  addDiagonalLine,
  addFib,
  addHorizontalLine,
  addPosition,
  addVerticalLine,
  appendPathVertex,
  commitPathDraft,
  removeFib,
  setDiagonalAnchor,
  setFibAnchor,
  setFibLevelsDialogOpen,
  setPositionAnchor,
  addPriceRangeBox,
  updatePriceRangeBox,
  setPriceRangeAnchor,
  setPriceRange,
  setScaleMargins,
  setChartColorsDialogOpen,
  toggleOneClickPanel,
  setTradingPanelOpen,
  setSelectedDrawing,
  setShowHistoryOrders,
  setShowTradingSlTp,
  setShowTradingTrades,
  setTradeVolume,
  setViewport,
  updateDiagonalLine,
  updateFib,
  updateHorizontalLine,
  updatePathVertex,
  updatePosition,
  updateVerticalLine,
  updateTextLabel,
  removeTextLabel,
  type DiagonalLine,
  type Path as PathDrawing,
  type PathPoint,
  type Position,
  type PositionType,
  type PriceRangeBox,
} from "@/state/slices/uiSlice";
import type {
  Candle,
  OHLCRequest,
  RawBin,
  PendingOrderBody,
  TradingPosition,
} from "@/api/types";
import {
  initialMaxFiles,
  isHtfTimeframe,
  TIMEFRAME_SECONDS,
  tfSeconds,
  tfShortLabel,
} from "@/utils/timeframe";
import {
  registerCapture,
  registerChartPan,
  unregisterCapture,
  unregisterChartPan,
} from "@/utils/chartCapture";
import { groupDealsToRoundTrips } from "@/components/PositionsPanel/PositionsPanel";
import { FibDrawingLevelsDialog } from "@/components/FibDrawingLevelsDialog/FibDrawingLevelsDialog";
import { DateTimePicker } from "@/components/DateTimePicker/DateTimePicker";
import { fmtPnl, fmtCountdown } from "@/utils/format";
import {
  fmtAxisYear,
  fmtAxisMonth,
  fmtAxisDay,
  fmtAxisTime,
  fmtCrosshair,
  fmtLineLabel,
  nowFileStamp,
} from "@/utils/chartTime";
import styles from "./Chart.module.css";
import replayStyles from "./ReplayBar.module.css";
import { type MenuGlyphName } from "./menuGlyphs";
import {
  priceLineStyleFromName,
  clampPriceLineWidth,
} from "./priceLineStyles";
import {
  type AnySeries,
  LAZY_LOAD_THRESHOLD,
  RIGHT_PADDING_BARS,
  INITIAL_VISIBLE_BARS,
  RESTORE_FUTURE_PAD_FRAC,
  toLineData,
  toCandleData,
  generateWhitespace,
  applyPriceRangeOrAutoFit,
  timeToLogical,
  logicalToTime,
} from "./chartData";
import { tradingGroupKeys } from "./tradingKeys";
import { renderWeeklyBands, renderHistoryMarker } from "./overlays";
import { renderNewsMarkers } from "./newsRenderer";
import {
  groupNewsMarkers,
  selectChartNewsEvents,
} from "@/state/selectors/chartNewsEvents";
import {
  renderHorizontalLine,
  renderVerticalLine,
  renderDiagonalLine,
  type LineRenderCtx,
} from "./lineRenderers";
import type { DragTarget, MenuItem } from "./types";
import { renderFib, type FibRenderCtx } from "./fibRenderer";
import { renderPosition, type PositionRenderCtx } from "./positionRenderer";
import {
  renderPriceRange,
  type PriceRangeRenderCtx,
} from "./priceRangeRenderer";
import { DRAWING_REMOVERS } from "@/tools/_shared/drawings";
import { bodyDragPartial, shiftTimeByLogical } from "@/tools/_shared/bodyDrag";
import {
  buildPriceRangeBox,
  priceRangePreview,
  priceRangeDragPartial,
  priceRangeMenuItems as prMenuItems,
} from "@/tools/priceRange/interaction";
import {
  buildDiagonalLine,
  diagonalPreview,
  diagonalDragPartial,
} from "@/tools/diagonal/interaction";
import {
  buildFib,
  fibPreview,
  fibDragPartial,
} from "@/tools/fibonacci/interaction";
import {
  buildPosition,
  positionPreview,
  positionDragPartial,
  positionBodyDragPartial,
  positionMenuItems as posMenuItems,
} from "@/tools/position/interaction";
import { pathPreview } from "@/tools/path/interaction";
import { placeTextLabel } from "@/tools/text/interaction";
import { renderPath, type PathRenderCtx } from "./pathRenderer";
import { renderTextLabel, type TextRenderCtx } from "./textLabelRenderer";
import {
  digitsFor,
  minMoveFor,
  pipSizeFor,
  sessionTypeFor,
} from "@/utils/symbolMeta";
import { renderTradingDragStrips, type TradingStripsCtx } from "./tradingOverlays";
import {
  renderDraftSlTp,
  renderPendingDraft,
  type DraftRenderCtx,
} from "./draftRenderer";
import { renderMenuItems } from "./contextMenu";
import {
  selectStrategyHistoryDeals,
  selectStrategyMetaByMagic,
  selectStrategyOrders,
  selectStrategyPositions,
} from "@/state/strategiesSelectors";

/** Per-tab chart instance. Mounted once per tab id and kept alive
 *  while the tab exists — CSS visibility toggling in <ChartHost>
 *  switches which one the user sees. Each chart owns its
 *  lightweight-charts instance, viewport, scaleMargins, priceRange,
 *  drawings, and live-tick subscription. Tab switching is reduced to
 *  a CSS class change; there is no longer any "tab switch capture
 *  and restore" dance because no chart instance is shared.
 *
 *  `isActive` is true when this chart's slot is currently visible.
 *  It gates the autoScale: false lock (see effect below) — hidden
 *  charts skip the lock so we don't freeze them against an
 *  uninitialized price range. */

export function Chart({
  tabId,
  isActive,
  signalMode = false,
  strategiesMode = false,
}: {
  tabId: string;
  isActive: boolean;
  /** Read-only "signal chart" mode: renders the shared bot_all reference —
   *  positions/orders/history sourced from the `signal` slice (a DIFFERENT
   *  account), overlays visible to every user, and ALL trade interactions
   *  disabled. See features/signal_chart.md. */
  signalMode?: boolean;
  /** With `signalMode`: the reference is the SELECTED strategies (the
   *  `strategies` slice) instead of bot_all. Same read-only chart; each level
   *  is drawn in its strategy's colour and prefixed with its label. See
   *  features/strategy_signals.md. */
  strategiesMode?: boolean;
}) {
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  // Outer wrapper that holds the chart canvas + SVG drawing overlay +
  // symbol/OHLC header. Used by html2canvas so "Copy chart image"
  // captures drawings, not just the lightweight-charts canvas.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<AnySeries | null>(null);
  const maSeriesRef = useRef<Record<string, ISeriesApi<"Line">>>({});
  const rsiSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const anchorRsiSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  // Cached linear y→price mapping (slope + intercept). Updated on every
  // successful coordinateToPrice call so drawing tools still work when the
  // user pans far enough right that only whitespace bars are visible (LWC's
  // coordinateToPrice returns null when no OHLC bars are in the visible range).
  const priceScaleCalibRef = useRef<{ slope: number; intercept: number } | null>(null);
  // Whether the current timeframe is daily-or-higher — read by the (once-
  // mounted) crosshair formatter to show date-only for daily UTC bars.
  const dailyTfRef = useRef(false);

  // --- Economic news overlay ---
  const newsPayload = useAppSelector((s) => s.news.payload);
  const newsShowOnChart = useAppSelector((s) => s.news.showOnChart);
  // Which news flag is hovered (unix seconds), so only one tooltip shows.
  const [hoveredNewsTime, setHoveredNewsTime] = useState<number | null>(null);
  // Plot height in px, kept in state by the chart's ResizeObserver so the
  // news flags can anchor to the bottom axis on the FIRST paint.
  const [plotHeight, setPlotHeight] = useState(0);

  const {
    symbol,
    timeframe,
    chartType,
    activeDrawingTool,
    fibAnchor,
    positionAnchor,
    diagonalAnchor,
    pathDraft,
    fibRetracements,
    horizontalLines,
    verticalLines,
    diagonalLines,
    paths,
    positions,
    priceRangeBoxes,
    priceRangeAnchor,
    textLabels,
    selectedDrawing,
    viewport,
    scaleMargins,
    priceRange,
    showTradingTrades,
    showTradingSlTp,
    showHistoryOrders,
  } = useTabUi(tabId);
  // Per-chart: tab override → built-in default (user scope is a template, it
  // does NOT cascade here — see selectEffectiveIndicators).
  const indicators = useAppSelector((s) => selectEffectiveIndicators(s, tabId));
  // Keep the crosshair formatter's daily check current (it's captured once at
  // chart mount; this ref lets it see the live timeframe).
  dailyTfRef.current = (TIMEFRAME_SECONDS[timeframe] ?? 60) >= 86400;
  const {
    candles,
    status,
    error,
    prependSerial,
    lastPrependCount,
    fetchSerial,
    isLoadingMore,
    loadedFor,
    failedFor,
    lastFetchAt,
  } = useTabOhlc(tabId);
  // Replay: when active, `candles` above is ALREADY truncated at the cursor
  // (useTabOhlc does it), so everything from here down — the series, the
  // indicators, the OHLC header, the drawings — is working on a chart whose
  // "now" is the cursor. What this component still has to do itself is stop
  // the live tick stream painting past it, and keep the cursor bar in view.
  const replay = useTabReplay(tabId);
  const replayActive = replay.active;
  const replaySelecting = replay.selecting;
  const { rawBins, liveBins, analyzedFor } = useTabAnalysis(tabId);
  // Effective fib levels via the cascade selector — tab-scope
  // wins, else workspace-scope, else user-scope (fibLevelsSlice).
  const fibLevels = useAppSelector((s) =>
    selectEffectiveFibLevels(s, tabId),
  );
  // Shared stroke for every fib level + the connecting trend line.
  const fibLineWidth = useAppSelector((s) =>
    selectEffectiveFibLineWidth(s, tabId),
  );
  const fibLineStyle = useAppSelector((s) =>
    selectEffectiveFibLineStyle(s, tabId),
  );
  // Diagonal/trend-line appearance + default visibility + default
  // label side (global user-scope fib settings). Per-fib drawings can
  // still override show/hide + label side individually.
  const fibDiagonal = useAppSelector(selectEffectiveFibDiagonal);
  const fibShowDiagonal = useAppSelector(selectEffectiveFibShowDiagonal);
  const fibLabelSide = useAppSelector(selectEffectiveFibLabelSide);
  // Full chart-color theme. Resolved via the cascade selector
  // (chart-scope → workspace-scope → built-in default) so a tab
  // with its own theme override gets it; otherwise it inherits. Replaces the hardcoded constants this
  // file used to ship with — every chart color (bg, candles,
  // pending/SL/TP) comes from here so the user can override any
  // of them via the "Chart colors…" dialog. The legacy toggles
  // above are kept for the quick-flip right-click items, but the
  // theme is what actually drives the colors at render time.
  const theme = useAppSelector((s) => selectEffectiveTheme(s, tabId));
  // Price Range styling is a per-user, per-mode setting (not per-tab) — read
  // from the user-scope theme slot regardless of any tab theme override.
  const priceRangeStyle = useAppSelector((s) =>
    selectPriceRangeStyle(s, tabId),
  );
  // Whole-app light/dark UI theme. The chart's non-data chrome (page
  // background, axis text/grid/border) tracks it so the chart re-skins
  // along with the rest of the UI; candle + line colors stay driven by
  // `theme` above. A user-customised chart bg overrides this.
  const appTheme = useAppSelector((s) => s.appearance.theme);
  // Whether THIS mode has its own chart-theme override. Drives the
  // light-mode fallback in selectChartChrome (no light override → white
  // chart, not the inherited dark base).
  const hasModeThemeOverride = useAppSelector((s) =>
    selectHasModeThemeOverride(s, tabId),
  );
  const chrome = selectChartChrome(theme.bg, appTheme, hasModeThemeOverride);
  // the trading data — overlaid as price lines on charts matching
  // the live account's symbols. Pushed by useTradingStream at the app
  // root (SSE); we just react to slice updates here.
  // Owner gate read here so the trading server/history overlay effects can
  // bail out for guests even if some other code path leaked
  // data into the slice. (tradingConnected is also false for
  // guests since the SSE doesn't open — but this guard is
  // defence-in-depth: trade data should never reach a guest's
  // chart regardless of slice state.)
  // Trading overlays/lines/history require trading.read to be shown — EXCEPT
  // in signal mode, where the overlays are the shared public bot_all reference
  // (visible to every signed-in user) and sourced from the `signal` slice, a
  // different account than the user's own `trading` slice.
  const ownerCanOverlay = useCan(CAP.tradingRead);
  const isOwnerForOverlay = signalMode ? true : ownerCanOverlay;
  const ownTradingConnected = useAppSelector((s) => s.trading.connected);
  const ownTradingPositions = useAppSelector((s) => s.trading.positions);
  const ownTradingOrders = useAppSelector((s) => s.trading.orders);
  const ownTradingHistoryDeals = useAppSelector((s) => s.trading.history.deals);
  const sigConnected = useAppSelector((s) => s.signal.connected);
  const sigPositions = useAppSelector((s) => s.signal.positions);
  const sigOrders = useAppSelector((s) => s.signal.orders);
  const sigHistoryDeals = useAppSelector((s) => s.signal.history.deals);
  const stratConnected = useAppSelector((s) => s.strategies.online);
  const stratPositions = useAppSelector(selectStrategyPositions);
  const stratOrders = useAppSelector(selectStrategyOrders);
  const stratHistoryDeals = useAppSelector(selectStrategyHistoryDeals);
  const stratMeta = useAppSelector(selectStrategyMetaByMagic);
  const refConnected = strategiesMode ? stratConnected : sigConnected;
  const refPositions = strategiesMode ? stratPositions : sigPositions;
  const refOrders = strategiesMode ? stratOrders : sigOrders;
  const refHistoryDeals = strategiesMode ? stratHistoryDeals : sigHistoryDeals;
  const tradingConnected = signalMode ? refConnected : ownTradingConnected;
  const tradingPositions = signalMode ? refPositions : ownTradingPositions;
  const tradingOrders = signalMode ? refOrders : ownTradingOrders;
  // Closed-trade deals — backed by `useTradingHistoryAutoFetch` (own account),
  // `fetchSignalHistory` (signal) or the strategies' trades. Used by the
  // entry/exit marker effect.
  const tradingHistoryDeals = signalMode ? refHistoryDeals : ownTradingHistoryDeals;
  // Weekly Behaviour session-band settings (Mode A + Mode B).
  // Cascade: tab override → user defaults → built-in.
  const weeklyBehaviour = useAppSelector((s) =>
    selectEffectiveWeeklyBehaviour(s, tabId),
  );
  // Cached pre-parsed session boundaries for this symbol. Empty
  // array (= "not loaded yet" OR "no data for this symbol") makes
  // the bucketing fall back to the UTC-rollover heuristic.
  const sessionBoundaries = useAppSelector(
    (s) => s.sessionBoundaries.bySymbol[symbol] ?? [],
  );
  // One-shot fetch per symbol. The thunk dedupes via its
  // `condition` callback, so re-firing here on tab changes is a
  // no-op once cached.
  useEffect(() => {
    if (!symbol) return;
    // crypto_24h symbols have no weekly close → no tick-derived session
    // boundaries. The chart buckets by plain UTC calendar (bucketStartSec
    // + the empty-boundary fallback), so skip the (always-empty) fetch.
    if (sessionTypeFor(symbol) === "crypto_24h") return;
    dispatch(fetchSessionBoundaries(symbol));
  }, [dispatch, symbol]);

  // Per-instance serials: this chart only ever sees its own tab's
  // fetch/prepend counters, so a single number suffices.
  const canLoadMoreRef = useRef(false);
  const seenPrependSerialRef = useRef(0);
  const seenFetchSerialRef = useRef(0);
  // Bumped whenever the price series is (re)created. The data layout effect
  // (setData + initial view) is a useLayoutEffect and can run on mount BEFORE
  // the series-creation useEffect has assigned seriesRef — it then early-returns
  // and, since its data deps (candles/fetchSerial) don't change afterwards on a
  // REMOUNT with data already cached (e.g. closing + reopening the signal tab),
  // never re-runs, leaving a blank chart until a manual "jump to latest". Adding
  // this epoch to its deps makes it re-run once the series exists, so the view
  // (fitContent / saved viewport) actually gets applied on remount.
  const [seriesEpoch, setSeriesEpoch] = useState(0);
  // Last visible time range observed while the chart was actually
  // showing data (updated from the range-change subscription, gated on
  // `viewReadyRef` so mid-transition/empty states never pollute it).
  // This is the user's real window — robust to capture-timing issues.
  const lastViewRef = useRef<{ from: Time; to: Time } | null>(null);
  // True only when the chart currently holds rendered data for the
  // active (symbol, TF). Gates lastViewRef updates so a TF-change
  // transition (setData with stale/empty data) can't overwrite the
  // good range we want to restore.
  const viewReadyRef = useRef(false);
  // When the user changes ONLY the timeframe (same symbol), we stash
  // the view as a midpoint TIME + visible BAR COUNT (not a raw range).
  // The fresh-load then rebuilds the window on the new TF keeping the
  // same bar count and midpoint, so the time span scales by the TF
  // ratio (H4→H1 = ÷4) and the chart stays centred on the same moment
  // at the same zoom — instead of snapping to latest or cramming bars.
  // Where the chart was looking during a replay, and the window to restore
  // once the post-exit refetch lands. Exiting replay keeps you where you were
  // replaying — you walked the cursor to a moment for a reason, and the point
  // of leaving is to see that same moment with the future filled back in.
  // (The replay camera is deliberately NOT persisted to the workspace while
  // replaying; normal viewport saving resumes after exit, by which point this
  // is an ordinary user-chosen view.)
  const replayViewRef = useRef<{ from: number; to: number } | null>(null);
  const pendingReplayRestoreRef = useRef<{ from: number; to: number } | null>(
    null,
  );
  const pendingTfRestoreRef = useRef<{ mid: number; bars: number } | null>(
    null,
  );

  // Mirror `isActive` for the long-lived range-change subscription, and
  // a debounce timer for persisting the viewport to Redux as the user
  // pans (so the normal workspace autosave saves the position reliably,
  // instead of depending on the fragile page-hide keepalive path).
  const isActiveRef = useRef(isActive);
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);
  // The signal chart is STATELESS — it never persists its viewport / scale /
  // priceRange, so every open initializes fresh to the latest candle (the
  // "Jump to latest" view). Ref so the long-lived range/page-hide handlers can
  // read it without re-subscribing.
  const signalModeRef = useRef(signalMode);
  useEffect(() => {
    signalModeRef.current = signalMode;
  }, [signalMode]);
  const viewportSaveTimerRef = useRef<number | null>(null);

  // rAF handles for re-asserting a restored visible range one/two frames after
  // setData. setVisibleRange right after setData lands on an unsettled time
  // scale and is silently overridden (proven: identical call gives different
  // getVisibleRange results on consecutive renders); re-applying once the
  // layout settles makes it stick. Tracked so we cancel stale re-asserts on the
  // next restore / unmount.
  const rangeReassertRafRef = useRef<number[]>([]);
  useEffect(
    () => () => {
      rangeReassertRafRef.current.forEach((id) => cancelAnimationFrame(id));
    },
    [],
  );

  // Stale-data tracking: keep lastFetchAt in a ref so the visibilitychange
  // handler below can read the current value without a stale closure.
  const lastFetchAtRef = useRef(lastFetchAt);
  useEffect(() => {
    lastFetchAtRef.current = lastFetchAt;
  }, [lastFetchAt]);

  useEffect(() => {
    // Skip refetch if this tab already has data for the current
    // (symbol, timeframe). Without this, every tab switch fired a
    // fresh network round-trip because the effect's deps include
    // tabId — exactly the behaviour that made tab switches look like
    // a reload.
    if (
      loadedFor &&
      loadedFor.symbol === symbol &&
      loadedFor.timeframe === timeframe
    ) {
      return;
    }
    if (status === "loading") return;
    // Don't auto-retry a request that just FAILED for this exact
    // (symbol, timeframe). Without this latch the effect re-runs on the
    // loading→failed transition (status is a dep) and re-dispatches
    // immediately → a tight loop that hammers the backend. A symbol/TF
    // change stops matching and refetches; the error overlay's Retry
    // button clears the latch for a manual re-attempt.
    if (
      status === "failed" &&
      failedFor &&
      failedFor.symbol === symbol &&
      failedFor.timeframe === timeframe
    ) {
      return;
    }
    // Timeframe-only change (same symbol): remember the current view so
    // the post-refetch fresh-load restores it instead of snapping to the
    // latest bar. The chart still holds the old TF's data at this point,
    // so getVisibleRange returns the user's actual window (in absolute
    // UTC time, which maps across timeframes). Clear it on a symbol
    // change / first load so a stale capture never leaks into those.
    if (loadedFor && loadedFor.symbol === symbol && loadedFor.timeframe !== timeframe) {
      // Prefer the tracked last-good window; fall back to a direct read
      // only if we never recorded one (e.g. TF changed before any pan).
      const r =
        lastViewRef.current ??
        chartRef.current?.timeScale().getVisibleRange() ??
        null;
      const from = r?.from as number | undefined;
      const to = r?.to as number | undefined;
      const oldTfSec = tfSeconds(loadedFor.timeframe);
      if (
        from != null &&
        to != null &&
        Number.isFinite(from) &&
        Number.isFinite(to) &&
        to > from
      ) {
        pendingTfRestoreRef.current = {
          mid: (from + to) / 2,
          bars: (to - from) / oldTfSec,
        };
      } else {
        pendingTfRestoreRef.current = null;
      }
    } else {
      pendingTfRestoreRef.current = null;
    }
    // Coarser timeframes have few bars per day, so fetch more days up front
    // to avoid a mostly-empty chart on first paint. Smaller initial chunks
    // keep each lazy-load batch proportionally meaningful (matches legacy).
    const maxFiles = initialMaxFiles(timeframe);
    let request: OHLCRequest = { symbol, timeframe, max_files: maxFiles };
    // If the restored view sits in HISTORY, load a CONTIGUOUS recent
    // block big enough to reach from that position all the way to the
    // live edge — NOT a detached month. That way the data runs from the
    // restored position through to "now", so scrolling right and
    // "Jump to latest" both have candles, while the saved position
    // still restores. The left edge to cover comes from:
    //   - TF change → the captured bar-count window's left edge
    //   - Initial load / page reload (loadedFor null) → the restored
    //     workspace viewport's left (earliest) edge.
    // We translate that into how many recent day-files to pull. HTF
    // (1D/1W/1M/1Y) loads the full htf.json series, so it never anchors.
    const isHtf = isHtfTimeframe(timeframe);
    if (!isHtf) {
      const tfr = pendingTfRestoreRef.current;
      let leftEdge: number | null = null;
      if (tfr) {
        // Cover BOTH the centred TF-change window AND the live-edge-clamped
        // window the restore may slide to (clampRangeToData below shifts the
        // window left when its `to` overshoots the last bar). The clamped
        // window's left can reach ~now - (1 - PAD)*span, so load far enough
        // left for whichever the restore picks.
        const tfrSpan = tfr.bars * tfSeconds(timeframe);
        leftEdge = Math.min(
          tfr.mid - tfrSpan / 2,
          Date.now() / 1000 - (1 - RESTORE_FUTURE_PAD_FRAC) * tfrSpan,
        );
      } else if (
        loadedFor == null &&
        viewport &&
        Number.isFinite(viewport.from)
      ) {
        leftEdge = viewport.from;
      }
      if (leftEdge != null) {
        // Day-files to cover [leftEdge → now]. Over-counting calendar
        // days (vs trading days) just loads a little extra older data —
        // harmless, and guarantees the position is covered.
        // Cap to guard against a corrupt/ancient leftEdge (e.g. a 1970
        // timestamp) triggering a load of the entire archive. ~2.5y of
        // day-files is plenty for any real saved position.
        const daysBack = Math.min(
          800,
          Math.ceil((Date.now() / 1000 - leftEdge) / 86400) + 5,
        );
        if (daysBack > maxFiles) {
          request = { symbol, timeframe, max_files: daysBack };
        }
      }
    }
    dispatch(fetchOhlc({ tabId, request }));
  }, [dispatch, tabId, symbol, timeframe, loadedFor, status, viewport, failedFor]);

  // Client-side bar builder: subscribes to the symbol's tick stream
  // and updates the chart's last bar via series.update() on each new
  // tick. lightweight-charts auto-decides "replace last bar" vs
  // "append new bar" based on the bar's time field. The current bar
  // state lives in a ref so React doesn't re-render per tick.
  const currentBarRef = useRef<
    | { time: number; open: number; high: number; low: number; close: number; volume: number }
    | null
  >(null);

  // Reset the bar tracker whenever the active timeframe or symbol
  // changes — otherwise the in-flight bar's bucket size would be wrong.
  useEffect(() => {
    currentBarRef.current = null;
  }, [timeframe, symbol]);

  // After a fresh OHLC fetch (or a lazy-load prepend), seed the bar
  // tracker from the latest historical bar so the very first tick
  // updates/extends it rather than spawning a stray bar.
  useEffect(() => {
    if (candles.length === 0) {
      currentBarRef.current = null;
      return;
    }
    const last = candles[candles.length - 1];
    currentBarRef.current = {
      time: last.time,
      open: last.open,
      high: last.high,
      low: last.low,
      close: last.close,
      volume: last.volume ?? 0,
    };
  }, [candles]);

  // Latest historical candles kept in a ref so `applyTicks` doesn't
  // need `candles` in its dep list (re-creating the callback per fetch
  // would still be fine because useTickStream holds handlers in a ref,
  // but skipping the rebuild is cheap insurance against churn).
  const candlesRef = useRef<Candle[]>(candles);
  useEffect(() => {
    candlesRef.current = candles;
  }, [candles]);

  // applyTicks is a useCallback that must not be rebuilt per replay toggle,
  // so it reads the flag through a ref (same pattern as candles/viewport).
  const replayActiveRef = useRef(replayActive);
  useEffect(() => {
    replayActiveRef.current = replayActive;
  }, [replayActive]);

  // Mirror of the user's pinned price range, so the replay-exit handler can
  // check it without taking `priceRange` as a dependency — that would re-run
  // the whole exit path (which also clears the bar builder) on every pin.
  const priceRangeRef = useRef(priceRange);
  useEffect(() => {
    priceRangeRef.current = priceRange;
  }, [priceRange]);

  // What the chart's series currently holds, so a replay step can append the
  // new bars rather than rebuild every bar the tab has loaded. See the
  // "Replay: append the new bars" block in the data layout effect.
  const replayRenderRef = useRef<{
    key: string;
    count: number;
    prependSerial: number;
    fetchSerial: number;
    firstTime: number;
  } | null>(null);

  // Same ref pattern for viewport — applyTicks needs the current
  // saved viewport to decide how many whitespace bars to generate
  // (so a saved `to` far past the data still fits within the time
  // scale and setVisibleRange can faithfully restore it).
  const viewportRef = useRef(viewport);
  useEffect(() => {
    viewportRef.current = viewport;
  }, [viewport]);

  // Bars that were live but whose bucket has already rolled over —
  // i.e. the live builder advanced past them, but the next server-side
  // OHLC fetch hasn't included them yet. Without this, every minute
  // roll on M1 dropped the just-completed candle until a reload.
  // Pruned in the seed effect once server hist catches up.
  type CommittedBar = {
    time: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
  };
  const committedLiveBarsRef = useRef<CommittedBar[]>([]);

  // Whenever server hist refreshes, drop any locally-committed live
  // bars whose timestamp is now covered by hist (avoid duplicate
  // entries when next /api/load_ohlc fetch sweeps them in).
  useEffect(() => {
    if (candles.length === 0) {
      committedLiveBarsRef.current = [];
      return;
    }
    const lastHistTime = candles[candles.length - 1].time;
    committedLiveBarsRef.current = committedLiveBarsRef.current.filter(
      (b) => b.time > lastHistTime,
    );
  }, [candles]);

  // Reset committed-live cache when the bucket size changes — bars
  // committed under one TF aren't meaningful under another.
  useEffect(() => {
    committedLiveBarsRef.current = [];
  }, [timeframe, symbol]);

  // Entering OR leaving replay invalidates the live bar builder: on entry its
  // half-built bar belongs to a "now" the chart has stopped showing, and on
  // exit it is stale by however long the replay lasted. Dropping both lets
  // the seed effect rebuild from whatever hist is current.
  //
  // And on EXIT, refetch. Replay mutes the tick stream, so for its whole
  // duration no bar was built and none was fetched: the newest candle in the
  // store is from before the replay began. Without this the chart comes back
  // showing history that stops minutes or hours short of now, then applyTicks
  // starts a fresh bar at the real "now" on top of it — old candles, a gap,
  // and a lone current bar adrift to the right. Exactly the stale-resume case
  // the visibility handler below exists for, reached a different way.
  // forceRefreshOhlc keeps the current candles on screen while the fetch is
  // in flight, so the chart doesn't blank.
  const wasReplayingRef = useRef(replayActive);
  useEffect(() => {
    currentBarRef.current = null;
    committedLiveBarsRef.current = [];
    const wasReplaying = wasReplayingRef.current;
    wasReplayingRef.current = replayActive;
    if (wasReplaying && !replayActive) {
      // Hand the replay camera to the refetch that's about to land, so the
      // fresh-load branch restores THAT window instead of the pre-replay
      // viewport. Read the live range as a fallback: the range-change
      // handler is debounced by nature, so a replay that ended immediately
      // after a scroll may not have recorded the final position.
      const tr = chartRef.current?.timeScale().getVisibleRange();
      pendingReplayRestoreRef.current =
        (tr ? { from: tr.from as number, to: tr.to as number } : null) ??
        replayViewRef.current;
      replayViewRef.current = null;
      // Drop any pinned price range on the way out. Replay deliberately
      // ignores the pin (it was set around the live edge and would put the
      // candles off-screen months back), and exit lands on the replayed
      // position rather than the live edge — so the pin is wrong for where we
      // now are. Leaving it in Redux would also desync state from the chart:
      // the re-fit/lock effect skips tabs that have a pin, believing it is
      // being honoured, while the axis is actually auto-fitting. Same action
      // the price-axis double-click uses to clear a pin.
      if (priceRangeRef.current) {
        dispatch(setPriceRange({ tabId, priceRange: null }));
      }
      dispatch(forceRefreshOhlc({ tabId }));
    }
  }, [replayActive, dispatch, tabId]);

  // Latest bid/ask seen on the tick stream — kept in a ref (no re-render)
  // so the right-click pending-order items can decide limit-vs-stop from
  // the cursor price relative to the live market.
  const liveBidAskRef = useRef<{ bid: number; ask: number } | null>(null);

  // Newest tick folded into the chart, so a reconnect snapshot can be deduped
  // against what has already been applied (the server's stated contract).
  const lastTickMscRef = useRef(0);

  const applyTicks = useCallback(
    (ticks: Tick[]) => {
      const last = ticks[ticks.length - 1];
      if (last) liveBidAskRef.current = { bid: last.bid, ask: last.ask };
      const series = seriesRef.current;
      if (!series || ticks.length === 0) return;
      // In replay the chart's "now" is the cursor, which is in the past. A
      // live tick would append a forming bar at the real now — thousands of
      // bars to the right of everything on screen — and re-setData the whole
      // series twice a second for the privilege. Keep recording the bid/ask
      // above (the right-click order items still want the live market) but
      // paint nothing. The SSE stays connected on purpose: it is one shared
      // pump per symbol, and churning it on every replay toggle would cost
      // more than it saves.
      if (replayActiveRef.current) return;
      const hist = candlesRef.current;
      if (hist.length === 0) return;
      if (last) {
        lastTickMscRef.current = Math.max(
          lastTickMscRef.current,
          last.time_msc,
        );
      }

      let bar = currentBarRef.current;
      const newlyCommitted: CommittedBar[] = [];
      for (const tick of ticks) {
        const mid = (tick.bid + tick.ask) / 2;
        const bucketSec = bucketStartSec(tick.time_msc, timeframe);

        if (!bar) {
          bar = {
            time: bucketSec,
            open: mid,
            high: mid,
            low: mid,
            close: mid,
            volume: 1,
          };
        } else if (bucketSec > bar.time) {
          // Bucket rolled over — the previous bar is *complete*; keep
          // it on the chart so the just-closed minute (or 5min, etc.)
          // doesn't visually disappear until the next server fetch.
          // Skip if this bar's bucket is already covered by hist
          // (defensive: shouldn't happen given the stale-ref guard
          // below, but cheap to be safe).
          newlyCommitted.push({ ...bar });
          bar = {
            time: bucketSec,
            open: mid,
            high: mid,
            low: mid,
            close: mid,
            volume: 1,
          };
        } else if (bucketSec === bar.time) {
          bar = {
            time: bar.time,
            open: bar.open,
            high: Math.max(bar.high, mid),
            low: Math.min(bar.low, mid),
            close: mid,
            volume: bar.volume + 1,
          };
        }
        // Older bucket: ignore (would violate lightweight-charts' "time
        // must be ≥ last bar" rule).
      }

      if (!bar) return;
      currentBarRef.current = bar;

      if (newlyCommitted.length > 0) {
        // Keep ALL newly-committed bars including ones whose time
        // matches hist's last bar — because hist's version of that
        // bar may be stale (the server fetch happened mid-minute and
        // captured a partial M1). The render code below overrides
        // hist entries at any time that exists in
        // `committedLiveBarsRef`, so the live-progressed close wins.
        committedLiveBarsRef.current = [
          ...committedLiveBarsRef.current,
          ...newlyCommitted,
        ];
      }

      // Why setData (not series.update)? RIGHT_PADDING_BARS of trailing
      // whitespace sit past the last real bar, so `update()` rejects
      // any live tick whose time is < the last whitespace item — every
      // single one of them. lightweight-charts v4.3 has a
      // `historicalUpdate` flag that bypasses the check, but 4.2.3
      // doesn't expose it in the typings. setData rebuilds the array
      // each batch (~2 Hz at the pump's 500ms cadence — trivial cost
      // for a few thousand items).
      const lastHist = hist[hist.length - 1];
      const tfSec = TIMEFRAME_SECONDS[timeframe] ?? 60;
      const liveTime = bar.time as UTCTimestamp;

      // Stale-ref guard: right after a TF switch, candlesRef may still
      // point at the old TF's candles while `bar.time` is already in
      // the new TF's bucket. For e.g. 1min→1D, lastHist would be a
      // recent intraday bar (today 15:30) and bar.time would be today
      // 00:00 — strictly older. setData would then receive
      // non-monotonic data and lightweight-charts would reject it.
      // Skip until the candles refresh catches up.
      if (bar.time < lastHist.time) return;

      // Whitespace must cover at least up to the saved viewport's
      // `to` time — otherwise setVisibleRange clamps and the view
      // appears "sliced to the right". RIGHT_PADDING_BARS is the
      // floor for fresh tabs; for tabs whose user panned far right
      // before saving, extend dynamically.
      const vp = viewportRef.current;
      let paddingBars = RIGHT_PADDING_BARS;
      if (vp && vp.to > bar.time) {
        const needed = Math.ceil((vp.to - bar.time) / tfSec) + 10;
        if (needed > paddingBars) paddingBars = needed;
      }
      // The signal chart is stateless (viewportRef stays null), so the branch
      // above never fires — but its "latest" view carries ~20% future padding
      // (INITIAL_VISIBLE_BARS * 0.2 bars). Without enough whitespace to cover
      // that, the position-preserving setVisibleRange below clamps the view
      // hard right on the first tick and the last candle jams against the
      // price axis. Pad enough for the padding to survive.
      if (signalModeRef.current) {
        const needed = Math.ceil(INITIAL_VISIBLE_BARS * 0.2) + 10;
        if (needed > paddingBars) paddingBars = needed;
      }
      const whitespace = generateWhitespace(bar.time, paddingBars, tfSec);

      // Build an override-time set: every bucket where live has fresher
      // data than hist. Includes both the in-progress current bar AND
      // every committed live bar. Any hist entry at one of these times
      // is dropped — its live counterpart wins.
      //
      // Why this is necessary: hist comes from /api/load_ohlc which
      // calls the trading server fetch_day. That fetch can run mid-minute and embed a
      // partial M1 bar (e.g. only the first 30s of close-tracking).
      // When the minute rolls over and we add the current bar at t+1,
      // the chart was previously falling back to that stale M1 → big
      // visible gap from the stale close to the live next-minute open.
      const overrideTimes = new Set<number>();
      committedLiveBarsRef.current.forEach((b) => overrideTimes.add(b.time));
      overrideTimes.add(bar.time);

      // Committed live bars that sit between hist and current — sorted
      // ascending so the final series stays monotonic.
      const committed = committedLiveBarsRef.current
        .filter((b) => b.time < bar!.time)
        .sort((a, b) => a.time - b.time);

      // Snapshot the visible TIME range before setData. Lightweight-
      // charts' setData preserves the visible LOGICAL range (bar
      // indexes); when we append a committed bar on bucket roll the
      // count grows by 1, so the logical position that previously
      // mapped to time T now maps to a later time → the visible
      // window appears to "slide right" over time. Capturing and
      // restoring the time range pins the view to the times the
      // user is actually looking at, regardless of bar-count changes.
      const savedRange = chartRef.current
        ? chartRef.current.timeScale().getVisibleRange()
        : null;
      try {
        if (chartType === "candle") {
          const histData = hist
            .filter((c) => !overrideTimes.has(c.time))
            .map<{
              time: UTCTimestamp;
              open: number;
              high: number;
              low: number;
              close: number;
            }>((c) => ({
              time: c.time as UTCTimestamp,
              open: c.open,
              high: c.high,
              low: c.low,
              close: c.close,
            }));
          const committedData = committed.map((c) => ({
            time: c.time as UTCTimestamp,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
          }));
          (series as ISeriesApi<"Candlestick">).setData([
            ...histData,
            ...committedData,
            {
              time: liveTime,
              open: bar.open,
              high: bar.high,
              low: bar.low,
              close: bar.close,
            },
            ...whitespace,
          ]);
        } else {
          const histData = hist
            .filter((c) => !overrideTimes.has(c.time))
            .map<{
              time: UTCTimestamp;
              value: number;
            }>((c) => ({
              time: c.time as UTCTimestamp,
              value: c.close,
            }));
          const committedData = committed.map((c) => ({
            time: c.time as UTCTimestamp,
            value: c.close,
          }));
          (series as ISeriesApi<"Line"> | ISeriesApi<"Area">).setData([
            ...histData,
            ...committedData,
            { time: liveTime, value: bar.close },
            ...whitespace,
          ]);
        }
        // Re-pin the time range we captured before setData. If the
        // chart's visible logical range happens to still map to the
        // same times (no bucket roll this tick), setVisibleRange is
        // a no-op visually. On rolls it cancels the right-slide.
        if (savedRange && chartRef.current) {
          try {
            chartRef.current.timeScale().setVisibleRange(savedRange);
          } catch {
            // Saved range may fall outside the new data after a TF
            // change race; drop silently — the next data-layout
            // effect will re-apply the proper viewport.
          }
        }
      } catch (err) {
        // Should be rare now that the stale-ref guard above runs
        // first. Surface to the console so any remaining edge case is
        // visible instead of silently dropped.
        // eslint-disable-next-line no-console
        console.warn("Live bar setData failed", { timeframe, barTime: bar.time, lastHistTime: lastHist.time, err });
      }
    },
    [timeframe, chartType],
  );

  // The SSE sends a `snapshot` of recently-buffered ticks on every connect —
  // including the silent reconnects EventSource does after an idle drop — and
  // the server's contract is that the client dedupes by time_msc. Until now
  // nothing consumed it: no onSnapshot handler at all. So after any
  // reconnection the ticks that arrived during the gap were thrown away and
  // the forming bar carried on from the next live tick, missing its open and
  // part of its range. That is the other half of "the current candle isn't
  // right after the tab was idle".
  const applySnapshot = useCallback(
    (ticks: Tick[]) => {
      const fresh = ticks.filter((t) => t.time_msc > lastTickMscRef.current);
      if (fresh.length > 0) applyTicks(fresh);
    },
    [applyTicks],
  );

  useTickStream(symbol, { onTicks: applyTicks, onSnapshot: applySnapshot });

  // useLiveAnalysis (analysis-side tick subscription) is mounted at
  // app root now — single subscription driven by analysis.liveTabId
  // and the owning tab's symbol. Chart no longer owns it because Live
  // can be running on a different tab than the one the user is
  // currently viewing.

  // Disable lightweight-charts' drag-to-pan in two situations:
  //   1. A two-anchor drawing tool is active (fib / long / short),
  //      otherwise the chart pans along with the placement gesture.
  //   2. A drawing is being dragged (line / edge / corner / body) —
  //      otherwise the chart pans simultaneously and the cursor's
  //      time/price coordinates shift mid-drag, causing the dragged
  //      object to behave erratically (especially the position body
  //      drag, which applies a delta to all five fields and inverts
  //      visibly when the underlying time scale moves).
  const [isDragging, setIsDragging] = useState(false);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const isTwoAnchorTool =
      activeDrawingTool === "fibonacci" ||
      activeDrawingTool === "diagonal" ||
      activeDrawingTool === "long" ||
      activeDrawingTool === "short" ||
      activeDrawingTool === "priceRange";
    chart.applyOptions({
      handleScroll: {
        pressedMouseMove: !isTwoAnchorTool && !isDragging,
      },
    });
  }, [activeDrawingTool, isDragging]);

  // Modifier-keys + mouse wheel:
  //   Shift          → pan horizontally (down = left, up = right)
  //   Ctrl / Cmd     → vertically expand/collapse the price area by
  //                    tweaking the right price scale's scaleMargins
  //                    (down = collapse, up = expand). Suppresses the
  //                    browser's default page-zoom on Ctrl+wheel.
  // The default wheel behavior (zoom) is preserved when no modifier
  // is held.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onWheel = (e: WheelEvent) => {
      const chart = chartRef.current;
      if (!chart) return;

      if (e.shiftKey) {
        e.preventDefault();
        const range = chart.timeScale().getVisibleLogicalRange();
        if (!range) return;
        const span = range.to - range.from;
        // With Shift held, browsers (notably on Windows) deliver the
        // wheel as horizontal scroll: deltaY is 0 and the magnitude
        // lands in deltaX. Reading deltaY alone made `shift` always
        // positive, so the chart panned right regardless of wheel
        // direction. Use whichever axis carries the delta.
        const delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
        const shift = (delta > 0 ? -1 : 1) * span * 0.1;
        chart.timeScale().setVisibleLogicalRange({
          from: range.from + shift,
          to: range.to + shift,
        });
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const priceScale = chart.priceScale("right");
        const opts = priceScale.options();
        const top = opts.scaleMargins?.top ?? 0.1;
        const bottom = opts.scaleMargins?.bottom ?? 0.1;
        // Wheel down → collapse (more padding, candles squished).
        // Wheel up   → expand (less padding, candles taller).
        const delta = e.deltaY > 0 ? 0.02 : -0.02;
        priceScale.applyOptions({
          scaleMargins: {
            top: Math.max(0, Math.min(0.45, top + delta)),
            bottom: Math.max(0, Math.min(0.45, bottom + delta)),
          },
        });
        return;
      }
    };
    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, []);

  // Mount chart + wire ResizeObserver + range-change subscription.
  useEffect(() => {
    if (!containerRef.current) return;

    // Render axis labels + crosshair tooltip in UTC, via the central
    // chartTime utility — matching the MT5/Exness data (which is UTC). The
    // underlying tick timestamps stay UTC seconds (lightweight-charts requires
    // that); these are display-only. See issue #90 — "Client display zone must
    // be UTC". (Was browser-local, which shifted a UTC trading day by the local
    // offset — most visible on daily candles.)
    const formatTickMark = (
      time: UTCTimestamp,
      tickMarkType: TickMarkType,
    ): string => {
      const ts = time as number;
      switch (tickMarkType) {
        case TickMarkType.Year:
          return fmtAxisYear(ts);
        case TickMarkType.Month:
          return fmtAxisMonth(ts);
        case TickMarkType.DayOfMonth:
          return fmtAxisDay(ts);
        case TickMarkType.Time:
          return fmtAxisTime(ts);
        case TickMarkType.TimeWithSeconds:
          return fmtAxisTime(ts, true);
        default:
          return fmtAxisDay(ts);
      }
    };
    // Daily+ timeframes: drop the time-of-day in the crosshair (a daily UTC
    // bar is 00:00, which reads as a meaningless time). Read the current
    // timeframe via a ref so the once-mounted formatter stays current.
    const formatCrosshairTime = (time: UTCTimestamp): string =>
      fmtCrosshair(time as number, { dateOnly: dailyTfRef.current });

    // Initial theme matches the workspace pref so the chart doesn't
    // flash the default theme for one frame before the live-apply
    // effect kicks in. The pref-watcher effect below reapplies on
    // every toggle.
    const chart = createChart(containerRef.current, {
      layout: { background: { color: chrome.bg }, textColor: chrome.textColor },
      grid: {
        vertLines: { color: chrome.gridColor, visible: theme.showGrid },
        horzLines: { color: chrome.gridColor, visible: theme.showGrid },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: chrome.axisBorder,
        shiftVisibleRangeOnNewBar: false,
        tickMarkFormatter: formatTickMark,
      },
      localization: {
        timeFormatter: formatCrosshairTime,
      },
      // No border on the price axis — it shares the chart background, so
      // the price panel blends seamlessly (no left separator line).
      rightPriceScale: { borderVisible: false },
      crosshair: { mode: 0 },
    });

    chartRef.current = chart;

    const ro = new ResizeObserver(() => {
      const el = containerRef.current;
      if (el && chart) chart.applyOptions({ width: el.clientWidth, height: el.clientHeight });
      // Mirror the height into state — the news overlay anchors its flags to
      // the plot bottom during RENDER, and a ref read there would be 0 on
      // first paint (flags would flash at the top until an unrelated
      // re-render). Same-value setState is a no-op, so this is cheap.
      if (el) setPlotHeight(el.clientHeight);
    });
    ro.observe(containerRef.current);
    setPlotHeight(containerRef.current.clientHeight);

    const onRangeChange = (range: LogicalRange | null) => {
      if (!range) return;
      // Keep any panes below this chart (GC's CVD / OI) on the same window.
      // Safe because those panes are built onto this chart's exact bar grid, so
      // a logical index means the same bar in all of them.
      publishRange(tabId, "price", range);
      // Record the user's current window whenever the chart is showing
      // real data, so a later timeframe change can restore it. Gated so
      // transition/empty states don't capture a degenerate range.
      if (viewReadyRef.current) {
        const tr = chart.timeScale().getVisibleRange();
        if (tr) lastViewRef.current = tr;
      }
      // Persist the ACTIVE chart's viewport to Redux (debounced) so the
      // normal workspace autosave reliably saves where the user is —
      // the page-hide keepalive path alone was lossy (active tab's
      // panned position often saved as null → reload snapped to latest).
      // Gated to the visible chart so hidden tabs' data-driven range
      // changes (ticks / lazy-load) don't clobber their saved viewport.
      // While replaying, remember the camera locally but do NOT persist it:
      // the keep-the-cursor-in-view scrolling is a range change like any
      // other, and writing it to the workspace would sync a replay position
      // to the user's other sessions. The local copy is what exit restores.
      if (replayActiveRef.current) {
        if (viewReadyRef.current) {
          const tr = chart.timeScale().getVisibleRange();
          if (tr) {
            replayViewRef.current = {
              from: tr.from as number,
              to: tr.to as number,
            };
          }
        }
      } else if (
        !signalModeRef.current &&
        isActiveRef.current &&
        viewReadyRef.current
      ) {
        if (viewportSaveTimerRef.current != null) {
          window.clearTimeout(viewportSaveTimerRef.current);
        }
        viewportSaveTimerRef.current = window.setTimeout(() => {
          if (!isActiveRef.current) return;
          const tr = chart.timeScale().getVisibleRange();
          if (tr) {
            dispatch(
              setViewport({
                tabId,
                viewport: { from: tr.from as number, to: tr.to as number },
              }),
            );
          }
        }, 400);
      }
      if (!canLoadMoreRef.current) return;
      if (Math.floor(range.from) < LAZY_LOAD_THRESHOLD) {
        // This chart instance belongs to one specific tab — its tabId
        // is stable for the lifetime of the component, so no ref
        // indirection is needed.
        dispatch(loadMoreOhlc({ tabId }));
      }
    };
    // Viewport / scaleMargins / priceRange are NOT dispatched on every
    // pan/zoom. They live on the chart instance and only need to round-
    // trip to Redux at moments where they must be persisted (page-hide
    // for workspace save). Each chart instance owns one tab, so there's
    // no tab-switch capture path anymore.
    chart.timeScale().subscribeVisibleLogicalRangeChange(onRangeChange);

    return () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onRangeChange);
      if (viewportSaveTimerRef.current != null) {
        window.clearTimeout(viewportSaveTimerRef.current);
      }
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      maSeriesRef.current = {};
      rsiSeriesRef.current = null;
    };
    // theme is intentionally NOT in deps: we only seed the chart
    // options with it at mount time (avoids recreating the chart
    // on every color tweak). The dedicated effect below picks up
    // live theme changes via applyOptions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, tabId]);

  // Live-apply chart chrome: swap background, axis text, grid + axis
  // border without recreating the chart instance. Runs on every chart-bg
  // tweak, grid toggle, AND app light/dark theme switch (chrome.* changes
  // with appTheme) — so flipping the UI theme re-skins the chart live. No-op
  // on initial mount since chart-init seeded the same values.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.applyOptions({
      layout: {
        background: { color: chrome.bg },
        textColor: chrome.textColor,
      },
      grid: {
        vertLines: { color: chrome.gridColor, visible: theme.showGrid },
        horzLines: { color: chrome.gridColor, visible: theme.showGrid },
      },
      timeScale: { borderColor: chrome.axisBorder },
    });
  }, [
    chrome.bg,
    chrome.textColor,
    chrome.gridColor,
    chrome.axisBorder,
    theme.showGrid,
  ]);

  // Page hide: capture the active tab's current viewport before the
  // browser tears the page down. Dispatching synchronously means
  // App's useWorkspaceFlushOnHide (registered later, so fires after
  // this child handler) reads the up-to-date viewport in the state
  // it sends to the server. Covers normal closes / reloads / OS
  // backgrounding; browser crashes are not recoverable here.
  useEffect(() => {
    const capture = () => {
      // Signal chart is stateless — never persist its position/scale.
      if (signalModeRef.current) return;
      const c = chartRef.current;
      if (!c) return;
      const r = c.timeScale().getVisibleRange();
      if (r) {
        dispatch(
          setViewport({
            tabId,
            viewport: { from: r.from as number, to: r.to as number },
          }),
        );
      }
      // Capture price-scale margins too so ctrl+wheel zoom survives
      // a reload. priceScale.options() is always defined; scaleMargins
      // may be missing on very old chart instances — fall back to the
      // lightweight-charts default the chart was created with.
      const margins = c.priceScale("right").options().scaleMargins;
      if (margins) {
        dispatch(
          setScaleMargins({
            tabId,
            scaleMargins: { top: margins.top, bottom: margins.bottom },
          }),
        );
      }
      // We DO NOT capture priceRange here. The intent was: save the
      // user's vertical right-axis drag so it survives a reload. The
      // trap: our visibility-gated lock flips autoScale to false on
      // every tab activation, indistinguishable from a real user
      // drag by inspecting `autoScale` alone. So a capture on
      // page-hide would happily save the auto-fit snapshot as if it
      // were a user-pinned range — and the next reload would restore
      // that as a fixed `autoscaleInfoProvider` range, freezing the
      // chart at whatever auto-fit happened to land at lock time
      // ("first tab stretched out after reload").
      //
      // To re-enable persistence we'd need a real signal that the
      // user dragged the price axis (lightweight-charts v4 doesn't
      // emit one; would have to detect via mousedown on the right-
      // axis x-band). Until then, accept the regression — drag-to-
      // pin doesn't survive reload — in exchange for tabs that
      // always load at the correct auto-fit. Logged as issue #86
      // ("Price-axis drag survives tab switch + workspace reload").
      //
      // (Previously cleared priceRange to null here as housekeeping
      // — that was correct when no drag-detection existed and any
      // priceRange in the slot was guaranteed stale. With the
      // mousedown/dblclick listener below now tracking real user
      // drags + resets, the slot's value is authoritative; clearing
      // it on every page-hide would wipe a pin the user explicitly
      // set right before closing the tab.)
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") capture();
    };
    window.addEventListener("pagehide", capture);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", capture);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dispatch, tabId]);

  // Stale-data refresh on browser-tab resume.
  //
  // When the OS/browser hides this page for a long time (user switched to
  // another OS window or browser tab), the tick stream may have had gaps
  // and — more importantly — the historical OHLC data is now hours old.
  // applyTicks only builds on top of that stale hist, so the chart shows
  // old candles → large empty gap → tiny current bar.  On the next
  // visibilitychange → visible, check how long we were hidden; if it's
  // past the threshold, dispatch forceRefreshOhlc so the fetch effect
  // re-runs and fills the gap.
  //
  // forceRefreshOhlc clears loadedFor/status but keeps candles, so the
  // chart continues to show (stale) data while the new fetch is in-flight
  // rather than going blank.
  // Trigger on the AGE OF THE DATA, not on how long the page was hidden.
  // Hidden-duration only catches one route to stale data and misses the
  // others: the page was never hidden but the machine slept or the tick
  // stream dropped and reconnected with a gap; the page was hidden only
  // briefly but the data was already old; or this app-tab sat in the
  // background (mounted, not "hidden" — the browser tab is still visible) and
  // its timers were throttled. What actually matters in every case is that
  // the newest candle predates now by more than a moment, which is what
  // `lastFetchAt` measures.
  useEffect(() => {
    const STALE_MS = 5 * 60 * 1000; // 5 minutes
    const refreshIfStale = () => {
      if (replayActiveRef.current) return; // replay wants stale-on-purpose data
      // Only the chart the user is actually looking at. Every tab keeps a
      // mounted Chart, and refetching all of them on one wake-up would fan a
      // single resume out into a request per tab.
      if (!isActive || document.visibilityState !== "visible") return;
      const at = lastFetchAtRef.current;
      if (at > 0 && Date.now() - at > STALE_MS) {
        dispatch(forceRefreshOhlc({ tabId }));
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshIfStale();
    };
    document.addEventListener("visibilitychange", onVisibility);
    // Regaining window focus covers walking away and coming back without ever
    // hiding the page — an OS screen lock or another window on top does not
    // reliably fire visibilitychange.
    window.addEventListener("focus", refreshIfStale);
    // A low-frequency safety net for the case that fires NO event at all: the
    // window stays focused and untouched while the machine idles. Only the
    // visible, focused tab checks, it only dispatches when the data is
    // genuinely old, and a successful refetch resets the age — so this is one
    // request per staleness episode, not a poll.
    const timer = window.setInterval(refreshIfStale, 60_000);
    // Becoming the focused app tab counts too: a background tab is mounted
    // and nominally still streaming, but a throttled or dropped stream leaves
    // the same hole, and this is the moment the user looks at it.
    if (isActive) refreshIfStale();
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", refreshIfStale);
      window.clearInterval(timer);
    };
  }, [dispatch, tabId, isActive]);

  // Signal-chart stale-cache refill. The signal chart is conditionally
  // rendered, so it UNMOUNTS while closed — unlike a real tab (which stays
  // mounted in the background and keeps building bars from the tick stream).
  // While it's closed it receives no ticks, and on reopen the fetch effect
  // skips the request (cached `loadedFor` still matches) so `applyTicks` just
  // extends stale history → old candles with a gap up to "now" ("candle not
  // reloaded"). Force a fresh fetch on (re)mount to fill the gap. Fires once
  // per mount (deps are stable for the signal instance); `forceRefreshOhlc`
  // keeps the old candles visible while the new fetch is in flight.
  useEffect(() => {
    if (!signalMode) return;
    dispatch(forceRefreshOhlc({ tabId }));
  }, [signalMode, tabId, dispatch]);

  // Price-axis drag detection. lightweight-charts v4 doesn't fire
  // any "price range changed" event; the right-axis drag state is
  // strictly internal (see issues #68 — `setVisiblePriceRange` doesn't
  // exist — and #69, the autoScale toggle). To pin the user's drag
  // across tab switches we sniff it externally: mousedown landing
  // in the price-scale x-band → on mouseup we sample the current
  // visible range and compare to the pre-drag sample. If it moved,
  // the user dragged → dispatch setPriceRange. If not, they just
  // clicked → no-op (don't pin the current autoFit snapshot, that
  // would freeze the chart against new-tick re-fits).
  //
  // Sampling uses coordinateToPrice at the DATA-AREA top + bottom
  // (h * scaleMargins.top and h * (1 - scaleMargins.bottom)),
  // excluding the padding bands — those bounds are what
  // autoscaleInfoProvider's `priceRange` writes back, so capturing
  // there round-trips faithfully.
  //
  // Double-click on the price axis clears the pin → resumes autoFit.
  useEffect(() => {
    const container = containerRef.current;
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!container || !chart || !series) return;
    const isInPriceAxis = (e: MouseEvent): boolean => {
      const rect = container.getBoundingClientRect();
      const w = chart.priceScale("right").width();
      return e.clientX >= rect.right - w && e.clientX <= rect.right;
    };
    const sampleVisibleRange = (): { from: number; to: number } | null => {
      const h = container.clientHeight;
      if (h <= 0) return null;
      const margins =
        chart.priceScale("right").options().scaleMargins ?? {
          top: 0.1,
          bottom: 0.1,
        };
      const topY = h * margins.top;
      const bottomY = h * (1 - margins.bottom);
      const top = series.coordinateToPrice(topY);
      const bottom = series.coordinateToPrice(bottomY);
      if (top == null || bottom == null) return null;
      const fromN = bottom as unknown as number;
      const toN = top as unknown as number;
      if (!Number.isFinite(fromN) || !Number.isFinite(toN)) return null;
      if (fromN === toN) return null;
      return { from: fromN, to: toN };
    };
    let dragStart: { from: number; to: number } | null = null;
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (!isInPriceAxis(e)) return;
      dragStart = sampleVisibleRange();
    };
    const onMouseUp = () => {
      if (!dragStart) return;
      const start = dragStart;
      dragStart = null;
      requestAnimationFrame(() => {
        const end = sampleVisibleRange();
        if (!end) return;
        // No movement = click only. Don't pin (would freeze autoFit).
        const PRICE_EPSILON = 1e-3;
        if (
          Math.abs(end.from - start.from) < PRICE_EPSILON &&
          Math.abs(end.to - start.to) < PRICE_EPSILON
        ) {
          return;
        }
        dispatch(
          setPriceRange({ tabId, priceRange: end }),
        );
      });
    };
    const onDblClick = (e: MouseEvent) => {
      if (!isInPriceAxis(e)) return;
      dispatch(setPriceRange({ tabId, priceRange: null }));
    };
    container.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);
    container.addEventListener("dblclick", onDblClick);
    return () => {
      container.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mouseup", onMouseUp);
      container.removeEventListener("dblclick", onDblClick);
    };
  }, [dispatch, tabId]);

  // Update the "can load more" mirror whenever the relevant state changes.
  useEffect(() => {
    canLoadMoreRef.current =
      status === "succeeded" &&
      !isLoadingMore &&
      candles.length > 0 &&
      // skip lazy load for HTF — htf.json already returns the full series
      !isHtfTimeframe(timeframe);
  }, [status, isLoadingMore, candles.length, timeframe]);

  // Mirror "the chart currently has rendered data" for the view tracker
  // above. Goes false while a refetch (e.g. timeframe change) is in
  // flight so the transition can't overwrite the last good window.
  useEffect(() => {
    viewReadyRef.current =
      status === "succeeded" && !isLoadingMore && candles.length > 0;
  }, [status, isLoadingMore, candles.length]);

  // Visibility-gated price-scale re-fit + lock.
  //
  // The data layout effect leaves the chart at `autoScale: true` with
  // an autoscaleInfoProvider installed (saved priceRange or pass-
  // through). We flip to `autoScale: false` AFTER lightweight-charts
  // had a paint cycle to apply the provider — locking before paint
  // freezes the default/uninitialized range.
  //
  // The decision gate is `priceRange` (the user's saved drag), NOT
  // the current autoScale flag:
  //   - priceRange set → user explicitly dragged the axis at some
  //     point; respect their pinned range, don't re-fit on
  //     activation.
  //   - priceRange null → no manual drag; we OWN the fit and should
  //     re-do it on every activation. The previous implementation
  //     bailed when `autoScale === false`, which trapped the
  //     initially-visible tab at its earliest fit (often computed
  //     against partial data) for the rest of the session — visible
  //     symptom: "tab 1 stays scaled-out forever, tabs activated
  //     later look tight".
  //
  // Forces a fresh measure (applyOptions(width/height) — kicks
  // lightweight-charts to re-layout even though visibility:hidden→
  // visible didn't change DOM dimensions, so ResizeObserver never
  // fired), re-arms autoScale: true to re-run the provider against
  // the current visible bars, then locks autoScale: false two rAFs
  // later so a subsequent right-axis drag sticks.
  //
  // Re-runs on:
  // - isActive transitions false → true (tab activated)
  // - fetchSerial bumps (fresh data may have shifted what's visible)
  // - priceRange changes from null → set (user just dragged, respect
  //   their range from here on)
  //
  // REPLAY is the exception to the lock. A pinned axis is fine on a live
  // chart, where price wanders inside the fitted range for hours. Replay
  // marches through days of bars in seconds and walks straight out of it —
  // price climbs off the top and the axis doesn't follow. So while replaying
  // the axis keeps auto-fitting to the visible bars, and a saved priceRange
  // is ignored: it was pinned around the live edge and means nothing several
  // months back, where it can leave the candles off-screen entirely.
  // Dragging the axis during a replay still sticks — lightweight-charts
  // clears autoScale itself on a price-scale drag, and this effect only
  // re-arms when one of its deps changes, so it won't fight the drag on
  // every step.
  useEffect(() => {
    if (!isActive) return;
    if (priceRange && !replayActive) return;
    const chart = chartRef.current;
    const container = containerRef.current;
    if (!chart || !container) return;
    chart.applyOptions({
      width: container.clientWidth,
      height: container.clientHeight,
    });
    const priceScale = chart.priceScale("right");
    priceScale.applyOptions({ autoScale: true });
    if (replayActive) return; // follow the bars; no lock
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        priceScale.applyOptions({ autoScale: false });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isActive, fetchSerial, priceRange, replayActive]);

  // Entering replay must also clear a pinned-range autoscaleInfoProvider.
  // autoScale: true alone isn't enough — the provider installed by
  // applyPriceRangeOrAutoFit returns a FIXED range, so the scale would keep
  // honouring the user's old pin no matter how the data moves. Swap in the
  // pass-through provider so the auto-fit above can actually see the bars.
  useEffect(() => {
    if (!replayActive) return;
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return;
    series.applyOptions({
      autoscaleInfoProvider: (base: () => unknown) => base() as never,
    });
    chart.priceScale("right").applyOptions({ autoScale: true });
  }, [replayActive, seriesEpoch]);

  // Recreate the series ONLY when chart-type changes. Tearing down and
  // re-adding on every candles update caused the chart to flash empty
  // for a frame whenever a lazy load arrived ("scroll a bit, then the
  // screen reloads").
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    if (seriesRef.current) {
      chart.removeSeries(seriesRef.current);
      seriesRef.current = null;
    }

    // Match the symbol's own tick precision (gold 3-dp, BTC 2-dp, …) from
    // the per-symbol metadata; the default precision=2 truncates the
    // right-axis labels and crosshair tooltip below the source granularity.
    const priceFormat = {
      type: "price" as const,
      precision: digitsFor(symbol),
      minMove: minMoveFor(symbol),
    };

    if (chartType === "candle") {
      // Two palettes:
      //   default — teal up / red down (TradingView-ish).
      //   mono   — black body up / white body down, all borders +
      //            wicks in the same green so the chart reads as a
      //            high-contrast green-outline silhouette.
      // Mono mode: bullish body is set to the chart background
      // colour so it visually "disappears" — only the green outline
      // remains, matching the trading server's hollow-bullish convention. We pass
      // the actual hex (not rgba(0,0,0,0)) because lightweight-
      // charts composites the body fill against the canvas first
      // and a fully-transparent body was rendering almost
      // indistinguishably from the default teal-filled body on the
      // Candle colors all come from the theme — every field
      // overridable via the "Chart colors…" dialog. Border + wick
      // share one color per direction (4 candle fields total) to
      // keep the dialog approachable.
      //
      // Live-price line styling pulls from the theme so the
      // Properties dialog can drive color / width / style. Pinning
      // the color (instead of inheriting from up/downColor) also
      // sidesteps the mono-mode bug where a bg-matching bull body
      // made the inherited price line vanish.
      const candleColors = {
        upColor: theme.bullBody,
        downColor: theme.bearBody,
        borderUpColor: theme.bullBorderWick,
        borderDownColor: theme.bearBorderWick,
        wickUpColor: theme.bullBorderWick,
        wickDownColor: theme.bearBorderWick,
        priceLineColor: theme.priceLineColor,
        priceLineStyle: priceLineStyleFromName(theme.priceLineStyle),
        priceLineWidth: clampPriceLineWidth(theme.priceLineWidth),
      };
      seriesRef.current = chart.addCandlestickSeries({
        ...candleColors,
        priceFormat,
      });
    } else if (chartType === "line") {
      seriesRef.current = chart.addLineSeries({
        color: "#3b82f6",
        priceFormat,
      });
    } else {
      seriesRef.current = chart.addAreaSeries({
        topColor: "rgba(59,130,246,0.4)",
        bottomColor: "rgba(59,130,246,0.0)",
        lineColor: "#3b82f6",
        priceFormat,
      });
    }
    // Signal to the data layout effect that a fresh series now exists, so it
    // re-runs and applies setData + the initial view even on a remount where
    // the candle data was already cached (its data deps wouldn't otherwise
    // change). See `seriesEpoch`.
    setSeriesEpoch((v) => v + 1);
    // Candle colors are read from theme at mount; live changes
    // are picked up by the dedicated effect below via applyOptions
    // (no series-recreate / chart-blink on every color tweak).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType]);

  // Live-apply theme color changes to the candle series via
  // applyOptions. No-op for line/area chartType.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series || chartType !== "candle") return;
    (series as ISeriesApi<"Candlestick">).applyOptions({
      upColor: theme.bullBody,
      downColor: theme.bearBody,
      borderUpColor: theme.bullBorderWick,
      borderDownColor: theme.bearBorderWick,
      wickUpColor: theme.bullBorderWick,
      wickDownColor: theme.bearBorderWick,
      priceLineColor: theme.priceLineColor,
      priceLineStyle: priceLineStyleFromName(theme.priceLineStyle),
      priceLineWidth: clampPriceLineWidth(theme.priceLineWidth),
    });
  }, [
    theme.bullBody,
    theme.bearBody,
    theme.bullBorderWick,
    theme.bearBorderWick,
    theme.priceLineColor,
    theme.priceLineStyle,
    theme.priceLineWidth,
    chartType,
  ]);

  // the trading server price-line overlay — draws entry / SL / TP for open
  // positions and the trigger price for pending limit/stop orders
  // matching this chart's symbol. Uses lightweight-charts'
  // createPriceLine API (built-in, native right-axis labels, free
  // updates on pan/zoom — no SVG bookkeeping needed). Cleared and
  // re-created on every Redux update; refs let us call
  // removePriceLine on previous handles.
  //
  // `chartType` is in deps because the series-init effect recreates
  // the series on chart-type change and the price lines were bound
  // to the old series — they vanish silently on recreate, so we
  // re-add them against the new series.
  const tradingLinesRef = useRef<IPriceLine[]>([]);
  // Map of price lines by `${kind}:${ticket}` (entry/sl/tp), with each
  // line's base width, so a chart drag can live-update via applyOptions
  // and the highlight can thicken/restore a whole order's lines.
  const tradingLineByKeyRef = useRef<
    Map<string, { line: IPriceLine; baseWidth: number }>
  >(new Map());
  // True while a trading line is being dragged — freezes the rebuild
  // below so an incoming SSE tick doesn't snap the line back mid-drag.
  const draggingTradingRef = useRef(false);
  // Whether the current trading drag actually moved — a bare click
  // (mousedown+up, no move) must NOT fire a redundant modify.
  const tradingMovedRef = useRef(false);
  // The position/order whose entry+SL+TP lines are currently highlighted
  // (thickened) so the group is easy to see + grab.
  const hlGroupRef = useRef<{ scope: "pos" | "order"; ticket: number } | null>(
    null,
  );
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    // Don't tear down + rebuild while the user is dragging a line — the
    // drag updates the native lines directly via applyOptions.
    if (draggingTradingRef.current) return;

    // Wipe whatever the previous run added — safer than diffing,
    // and the count is small (typically <10 lines per chart).
    for (const line of tradingLinesRef.current) {
      try {
        series.removePriceLine(line);
      } catch {
        // Series may have been recreated underneath us — line is
        // already gone; nothing to do.
      }
    }
    tradingLinesRef.current = [];
    tradingLineByKeyRef.current.clear();

    // Signal mode draws its (often hundreds of) order levels as SVG lines in
    // the drawing overlay instead of native price lines. Native `createPriceLine`
    // gives no control over dash phase, so stacked dotted lines fill each other's
    // gaps and saturate into a solid band. SVG lines all start at x=0 with the
    // same dash pattern, so their dots align into vertical columns (the MT5
    // "screen-door" look) and stay legible when many stack. See the signal-line
    // block in the overlay JSX below.
    if (signalMode) return;

    if (!isOwnerForOverlay || !tradingConnected || !showTradingTrades) return;

    // Create a price line, track it for teardown, and register it under
    // `key` for drag lookup.
    const add = (
      key: string,
      opts: Parameters<typeof series.createPriceLine>[0],
    ) => {
      const line = series.createPriceLine(opts);
      tradingLinesRef.current.push(line);
      tradingLineByKeyRef.current.set(key, {
        line,
        baseWidth: (opts.lineWidth as number) ?? 1,
      });
    };

    // Color convention:
    //   open position entry: solid, green/red by direction
    //   open position SL: red dashed (gated on `showTradingSlTp`)
    //   open position TP: green dashed (gated on `showTradingSlTp`)
    //   pending order (limit/stop): cyan, LargeDashed (matches
    //     the crosshair dash pattern) — reads as "watching this
    //     price", not as a confirmed fill.
    //   pending order SL/TP: red dashed, gated on `showTradingSlTp` —
    //     same red as open-position SL so risk levels look
    //     consistent across pending and live trades.
    //
    // Layer visibility is governed by two per-tab flags:
    //   `showTradingTrades` (this whole effect) — entry / trigger lines
    //   `showTradingSlTp` (per-block check) — SL / TP lines
    // So the user can keep entries visible while hiding SL/TP for
    // a cleaner view, or hide everything for an analysis chart.
    // Open-position entry uses the corresponding side's body color
    // from the theme — buys get the bull color, sells the bear
    // color. SL = theme.slColor, TP = theme.tpColor (unified across
    // open and pending trades — single source of truth).
    for (const p of tradingPositions) {
      if (p.symbol !== symbol) continue;
      // Entry line color/width/style by direction. Falls back to a
      // visible green/red if the saved theme predates these fields —
      // never the candle body color (which is black under Mono Black,
      // i.e. invisible). Editable in Chart Properties.
      const isLong = p.type === 0;
      const entryColor =
        (isLong ? theme.posLongColor : theme.posShortColor) ??
        (isLong ? "#26a69a" : "#ef5350");
      const entryWidth = (isLong ? theme.posLongWidth : theme.posShortWidth) ?? 1;
      const entryStyle =
        (isLong ? theme.posLongStyle : theme.posShortStyle) ?? "dotted";
      add(`pos-entry:${p.ticket}`, {
        price: p.price_open,
        color: entryColor,
        lineWidth: clampPriceLineWidth(entryWidth),
        lineStyle: priceLineStyleFromName(entryStyle),
        axisLabelVisible: true,
        // Running entry shows the live floating P&L (from MT5). Kept as
        // compact as the SL/TP labels (no volume in the label).
        title: `${p.type_label.toUpperCase()} ${fmtPnl(p.profit)}`,
      });
      if (showTradingSlTp && p.sl > 0) {
        add(`pos-sl:${p.ticket}`, {
          price: p.sl,
          color: theme.slColor,
          lineWidth: clampPriceLineWidth(theme.slWidth),
          lineStyle: priceLineStyleFromName(theme.slStyle),
          axisLabelVisible: true,
          title: p.sl_pnl != null ? `SL ${fmtPnl(p.sl_pnl)}` : "SL",
        });
      }
      if (showTradingSlTp && p.tp > 0) {
        add(`pos-tp:${p.ticket}`, {
          price: p.tp,
          color: theme.tpColor,
          lineWidth: clampPriceLineWidth(theme.tpWidth),
          lineStyle: priceLineStyleFromName(theme.tpStyle),
          axisLabelVisible: true,
          title: p.tp_pnl != null ? `TP ${fmtPnl(p.tp_pnl)}` : "TP",
        });
      }
    }

    for (const o of tradingOrders) {
      if (o.symbol !== symbol) continue;
      add(`order-entry:${o.ticket}`, {
        price: o.price_open,
        color: theme.pendingOrder,
        lineWidth: clampPriceLineWidth(theme.pendingOrderWidth),
        lineStyle: priceLineStyleFromName(theme.pendingOrderStyle),
        axisLabelVisible: true,
        // Compact label + expiry countdown when the order has one.
        title:
          o.expires_in != null
            ? `${o.type_label.toUpperCase()} · ${fmtCountdown(o.expires_in)}`
            : o.type_label.toUpperCase(),
      });
      if (showTradingSlTp && o.sl > 0) {
        add(`order-sl:${o.ticket}`, {
          price: o.sl,
          color: theme.slColor,
          lineWidth: clampPriceLineWidth(theme.slWidth),
          lineStyle: priceLineStyleFromName(theme.slStyle),
          axisLabelVisible: true,
          title: o.sl_pnl != null ? `SL ${fmtPnl(o.sl_pnl)}` : "SL",
        });
      }
      if (showTradingSlTp && o.tp > 0) {
        add(`order-tp:${o.ticket}`, {
          price: o.tp,
          color: theme.tpColor,
          lineWidth: clampPriceLineWidth(theme.tpWidth),
          lineStyle: priceLineStyleFromName(theme.tpStyle),
          axisLabelVisible: true,
          title: o.tp_pnl != null ? `TP ${fmtPnl(o.tp_pnl)}` : "TP",
        });
      }
    }

    // Re-apply the active highlight to the freshly-built lines so an
    // SSE-driven rebuild doesn't flicker the highlighted group off.
    const hg = hlGroupRef.current;
    if (hg) {
      for (const k of tradingGroupKeys(hg.scope, hg.ticket)) {
        const e = tradingLineByKeyRef.current.get(k);
        if (e) {
          try {
            e.line.applyOptions({
              lineWidth: clampPriceLineWidth(e.baseWidth + 1),
            });
          } catch {
            // line removed underneath us — ignore
          }
        }
      }
    }
  }, [
    isOwnerForOverlay,
    tradingConnected,
    tradingPositions,
    theme,
    tradingOrders,
    symbol,
    chartType,
    showTradingTrades,
    showTradingSlTp,
    signalMode,
  ]);

  // Closed-trade markers are rendered as SVG in the drawing
  // overlay below (see `renderHistoryMarker`) so each arrow can
  // anchor to the exact entry / exit PRICE, not the bar's vertical
  // midpoint. Lightweight-charts' native `setMarkers` only supports
  // `aboveBar` / `belowBar` / `inBar` placement — on a wide bar
  // `inBar` puts the arrow at the bar's midpoint, which can be
  // 20+ pips away from the actual fill price. SVG placement uses
  // `series.priceToCoordinate(price)` so it's pixel-accurate.
  //
  // This effect just clears any previously-set native markers in
  // case an older build left them on the series.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    series.setMarkers([]);
  }, []);

  // Memoised closed-trade round-trips matching this chart's
  // symbol — consumed by the SVG renderer below. Kept here (not
  // inside the renderer) so groupDealsToRoundTrips only runs when
  // the underlying deals change, not on every pan/zoom rangeTick.
  const historyTrips = useMemo(() => {
    if (!isOwnerForOverlay || !showHistoryOrders) return [];
    return groupDealsToRoundTrips(tradingHistoryDeals).filter(
      (t) => t.symbol === symbol,
    );
  }, [isOwnerForOverlay, tradingHistoryDeals, showHistoryOrders, symbol]);

  // Weekly Behaviour bands — Mode A (per-day) and Mode B (per-week).
  // We iterate the actual candles (not raw UTC time) and group them
  // by session-key. Each contiguous run becomes one band; the
  // boundary X falls at the midpoint of the gap between the LAST
  // candle of one group and the FIRST candle of the next. This
  // makes the colour change land cleanly between two candles (e.g.
  // Fri close → Mon open over the weekend) instead of inside the
  // interpolated weekend-gap region, which is what the raw-UTC
  // approach produced.
  // High-impact news markers for THIS chart's symbol. The events come from
  // the shared snapshot (`useNewsFeed` in App), so the overlay works whether
  // or not the News drawer has ever been opened. Empty when the user has the
  // overlay switched off — the render gate below then costs nothing.
  const newsMarkers = useMemo(() => {
    if (!newsShowOnChart) return [];
    const events = newsPayload?.events ?? [];
    if (events.length === 0) return [];
    // The overlay always follows the FTMO source: it's the superset that
    // carries the restricted marking, and the panel's source toggle is a
    // list-reading preference that shouldn't silently change what the chart
    // warns about.
    const ftmo = events.filter((e) => e.source === "ftmo");
    // With FTMO data the chart draws only the restricted (no-trade) releases.
    // Unrestricted high-impact prints next to one — the FOMC Press Conference
    // 30 min after the restricted rate decision — otherwise add a second line
    // for what reads as the same news. Without FTMO there is no marking, so
    // fall back to every high-impact release.
    const pool = ftmo.length > 0 ? ftmo.filter((e) => e.restricted) : events;
    return groupNewsMarkers(selectChartNewsEvents(pool, symbol));
  }, [newsShowOnChart, newsPayload, symbol]);

  const weeklyBandRuns = useMemo(() => {
    const wb = weeklyBehaviour;
    if (!wb) return [];
    // Render gate: only run the scanner for modes the user picked.
    // Within each picked mode the per-day / per-week enable flags
    // still apply — `mode` chooses WHICH track is on, the per-key
    // toggles choose what shows within that track. Older saves
    // pre-date the field, so default to "both".
    const renderMode = wb.mode ?? "both";
    const renderDay = renderMode === "day" || renderMode === "both";
    const renderWeek = renderMode === "week" || renderMode === "both";
    const dayModeOn =
      renderDay && wb.dayHighlights.some((d) => d.enabled);
    const weekModeOn =
      renderWeek &&
      wb.weekMode.enabled &&
      wb.weekMode.palette.length > 0;
    if (!dayModeOn && !weekModeOn) return [];
    if (candles.length === 0) return [];

    // Optional date range — filter candle indices, not raw time.
    const fromSec = wb.fromDate
      ? Math.floor(Date.parse(`${wb.fromDate}T00:00:00Z`) / 1000)
      : -Infinity;
    const toSec = wb.toDate
      ? Math.floor(Date.parse(`${wb.toDate}T23:59:59Z`) / 1000)
      : Infinity;

    type Run = {
      key: string;
      firstIdx: number;
      lastIdx: number;
      // Null means "session run exists but the user hasn't enabled
      // a band for this bucket". We still keep it in the list so
      // the seam math between adjacent enabled runs uses the
      // actual day/week boundary (instead of skipping disabled
      // days and bleeding the enabled colour across them).
      color: string | null;
    };
    const runs: Run[] = [];

    // Reusable scanner. `keyOf` returns a stable string for the
    // candle's bucket (day-mode or week-mode); `colorFor` looks up
    // the rendered colour from that key, or null when the user
    // hasn't enabled a band for it.
    const scan = (
      tag: "d" | "w",
      keyOf: (timeMs: number) => string | null,
      colorFor: (key: string) => string | null,
    ) => {
      const pushRun = (key: string, firstIdx: number, lastIdx: number) => {
        runs.push({
          key: `${tag}-${firstIdx}`,
          firstIdx,
          lastIdx,
          color: colorFor(key),
        });
      };
      let runStart = -1;
      let runKey: string | null = null;
      for (let i = 0; i < candles.length; i++) {
        const t = candles[i].time;
        if (t < fromSec || t > toSec) {
          if (runStart >= 0 && runKey) {
            pushRun(runKey, runStart, i - 1);
          }
          runStart = -1;
          runKey = null;
          continue;
        }
        const k = keyOf(t * 1000);
        if (k === runKey) continue;
        // Key changed (or we just entered range) — close the
        // previous run, open a new one.
        if (runStart >= 0 && runKey) {
          pushRun(runKey, runStart, i - 1);
        }
        runStart = i;
        runKey = k;
      }
      // Flush the trailing run.
      if (runStart >= 0 && runKey) {
        pushRun(runKey, runStart, candles.length - 1);
      }
    };

    // Boundary-driven bucketing. `sessionDayFromBoundaries` /
    // `sessionWeekFromBoundaries` already fall back to a calendar-
    // UTC heuristic for candles past the last known boundary (the
    // live session) and for symbols whose boundaries haven't been
    // detected yet, so we don't need a separate fallback path here.
    if (dayModeOn) {
      scan(
        "d",
        (timeMs) => {
          const idx = sessionDayFromBoundaries(timeMs, sessionBoundaries);
          return idx >= 0 ? `d${idx}` : null;
        },
        (k) => {
          const idx = Number(k.slice(1));
          const dh = wb.dayHighlights[idx];
          return dh?.enabled ? dh.color : null;
        },
      );
    }

    if (weekModeOn) {
      const palette = wb.weekMode.palette;
      const palLen = palette.length;
      scan(
        "w",
        (timeMs) => {
          const wi = sessionWeekFromBoundaries(timeMs, sessionBoundaries);
          return `w${wi}`;
        },
        (k) => {
          const wi = Number(k.slice(1));
          return palette[((wi % palLen) + palLen) % palLen];
        },
      );
    }

    return runs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyBehaviour, candles, sessionBoundaries]);

  // Push data to the existing series when candles change. Reuses the
  // series so lightweight-charts can do an internal diff/incremental
  // update instead of a full teardown.
  //
  // useLayoutEffect (not useEffect) so setData + range shift run *before*
  // the next browser paint — otherwise the user sees one paint with the
  // old chart, then another with the post-shift chart, which reads as a
  // brief "reload" flash on every lazy load.
  useLayoutEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return;

    const timeScale = chart.timeScale();
    const isPrepend = prependSerial > seenPrependSerialRef.current;
    const isFreshLoad = fetchSerial > seenFetchSerialRef.current;

    // For prepends, capture the current time range BEFORE setData so we
    // can restore it AFTER. Time-based restoration is robust to
    // lightweight-charts' internal range bookkeeping during setData,
    // unlike a logical-index shift which depends on assumptions about
    // whether the chart auto-shifts.
    const savedTimeRange = isPrepend ? timeScale.getVisibleRange() : null;

    // Append RIGHT_PADDING_BARS whitespace items so the time scale
    // extends past the last real bar — this makes clicks/hover/drag
    // work in the "future" empty area without any extrapolation hacks.
    // Whitespace must also cover the saved viewport's `to` time, or
    // setVisibleRange below would clamp and the restored view would
    // appear "sliced right" — same dynamic-padding logic as applyTicks.
    const tfSec = TIMEFRAME_SECONDS[timeframe] ?? 60;
    const lastTime = candles[candles.length - 1]?.time ?? 0;
    let paddingBars = RIGHT_PADDING_BARS;
    // Extend whitespace to cover whichever "to" we're about to restore
    // (the rebuilt TF-change window, else the saved viewport) when it
    // sits past the last real bar — otherwise setVisibleRange clamps and
    // the restored view looks sliced on the right.
    const tfr = pendingTfRestoreRef.current;
    // Signal mode always opens at the latest candle, so it only needs enough
    // whitespace for the standard right padding — NOT the signal tab's stale
    // saved viewport `to`, which can sit far in the future and would inflate
    // the whitespace so much that the chart's natural post-setData position
    // lands in the empty area on the right ("moved right").
    // Replay appends NO whitespace. Two reasons: the cursor bar is the right
    // edge by definition (there is no future to drag a drawing into), and
    // trailing whitespace is exactly what makes `series.update()` reject an
    // appended bar — which the incremental step path below depends on. The
    // view can still show empty space past the last bar; that is a visible
    // RANGE, not data.
    const restoreTo = replayActive
      ? undefined
      : signalMode
        ? lastTime +
          Math.min(candles.length, INITIAL_VISIBLE_BARS) * 0.2 * tfSec
        : tfr
          ? tfr.mid + (tfr.bars * tfSec) / 2
          : viewport && Number.isFinite(viewport.to)
            ? viewport.to
            : undefined;
    if (restoreTo != null && lastTime > 0 && restoreTo > lastTime) {
      const needed = Math.ceil((restoreTo - lastTime) / tfSec) + 10;
      if (needed > paddingBars) paddingBars = needed;
    }
    const whitespace =
      candles.length > 0 && !replayActive
        ? generateWhitespace(lastTime, paddingBars, tfSec)
        : [];

    // --- Replay: append the new bars instead of rebuilding the series ---
    //
    // A cursor step changes the candle array by ONE bar at the end, but the
    // code below re-maps every bar the tab has ever lazy-loaded and hands the
    // whole thing to setData. On an H1 tab that has been scrolled back a few
    // years that is tens of thousands of points per step, costing on the
    // order of a second — and since the play clock can only advance as fast
    // as the chart repaints, every speed collapsed onto the same rate. (That
    // is the bug behind "1x, 2x and 5x all replay one bar at a time".)
    //
    // So when the only difference is bars appended to the end, push just
    // those through `series.update()`, which is O(new bars). Anything else —
    // a step BACKWARD, a lazy-load prepend that shifts every index, a
    // symbol / timeframe / chart-type change, a recreated series — falls
    // through to the full setData below, which is always correct.
    const replayKey = `${symbol}|${timeframe}|${chartType}|${seriesEpoch}`;
    const rr = replayRenderRef.current;
    if (
      replayActive &&
      rr &&
      rr.key === replayKey &&
      rr.prependSerial === prependSerial &&
      rr.fetchSerial === fetchSerial &&
      rr.firstTime === candles[0]?.time &&
      candles.length > rr.count &&
      // A huge jump (⏭, or a drag of the cursor) is cheaper as one setData
      // than as thousands of update() calls.
      candles.length - rr.count <= 256
    ) {
      for (let i = rr.count; i < candles.length; i += 1) {
        const c = candles[i];
        if (chartType === "candle") {
          (series as ISeriesApi<"Candlestick">).update({
            time: c.time as UTCTimestamp,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
          });
        } else {
          (series as ISeriesApi<"Line"> | ISeriesApi<"Area">).update({
            time: c.time as UTCTimestamp,
            value: c.close,
          });
        }
      }
      replayRenderRef.current = { ...rr, count: candles.length };
      // Drawings still need their X coordinates recomputed against the
      // chart's new extent.
      setRangeTick((v) => v + 1);
      return;
    }

    if (chartType === "candle") {
      (series as ISeriesApi<"Candlestick">).setData([
        ...toCandleData(candles),
        ...whitespace,
      ]);
    } else {
      (series as ISeriesApi<"Line"> | ISeriesApi<"Area">).setData([
        ...toLineData(candles),
        ...whitespace,
      ]);
    }
    // Baseline for the incremental path above: what the series now holds.
    replayRenderRef.current = {
      key: replayKey,
      count: candles.length,
      prependSerial,
      fetchSerial,
      firstTime: candles[0]?.time ?? 0,
    };

    const fitToContent = () => {
      const realCount = candles.length;
      const visibleCount = Math.min(realCount, INITIAL_VISIBLE_BARS);
      const visibleStart = realCount - visibleCount - 0.5;
      const visibleEnd = realCount - 0.5 + visibleCount * 0.2;
      timeScale.setVisibleLogicalRange({
        from: visibleStart,
        to: visibleEnd,
      });
      // Clear any stale provider and turn autoScale on. Locking is
      // deferred to the visibility-gated effect below so hidden
      // tabs don't lock against an uninitialized price range.
      series.applyOptions({ autoscaleInfoProvider: (base: () => unknown) => base() as never });
      chart.priceScale("right").applyOptions({ autoScale: true });
    };

    if (candles.length === 0) return;

    // Signal fresh-load (mount / reopen / TF change) ALWAYS snaps to the latest
    // candle, and must be checked BEFORE isPrepend. A reopened signal instance
    // starts with seenPrependSerialRef=0 while the ohlc slice's prependSerial is
    // already >0 from an earlier in-session lazy-load, so isPrepend would be
    // true and (since it's checked first) the prepend branch would restore the
    // pre-setData range — the last candle jammed against the right axis with no
    // padding. We consume BOTH serials here so later REAL in-session prepends
    // (user scrolled into history) still fall through to the preserve-position
    // branch. Re-assert across frames until the target holds (see below).
    if (signalMode && isFreshLoad) {
      seenFetchSerialRef.current = fetchSerial;
      seenPrependSerialRef.current = prependSerial;
      pendingTfRestoreRef.current = null;
      const realCount = candles.length;
      const visibleCount = Math.min(realCount, INITIAL_VISIBLE_BARS);
      const target = {
        from: realCount - visibleCount - 0.5,
        to: realCount - 0.5 + visibleCount * 0.2,
      };
      series.applyOptions({
        autoscaleInfoProvider: (base: () => unknown) => base() as never,
      });
      chart.priceScale("right").applyOptions({ autoScale: true });
      rangeReassertRafRef.current.forEach((id) => cancelAnimationFrame(id));
      rangeReassertRafRef.current = [];
      let attempt = 0;
      let heldFrames = 0;
      const near = (a: number, b: number) => Math.abs(a - b) < 0.5;
      // Re-assert across frames until the target HOLDS: a single set right
      // after setData lands on an unsettled scale and is clobbered by the
      // live-tick applyTicks path. Read the SETTLED result on a later frame,
      // and only stop once the target has survived two consecutive checks.
      const step = () => {
        const ar = timeScale.getVisibleLogicalRange();
        const holding =
          ar != null && near(ar.from, target.from) && near(ar.to, target.to);
        heldFrames = holding ? heldFrames + 1 : 0;
        if (heldFrames >= 2 || attempt >= 40) return;
        timeScale.setVisibleLogicalRange(target);
        attempt += 1;
        const id = requestAnimationFrame(step);
        rangeReassertRafRef.current = [id];
      };
      timeScale.setVisibleLogicalRange(target);
      const id0 = requestAnimationFrame(step);
      rangeReassertRafRef.current = [id0];
      setRangeTick((v) => v + 1);
      return;
    }

    if (isPrepend) {
      if (savedTimeRange) {
        timeScale.setVisibleRange(savedTimeRange);
      }
      seenPrependSerialRef.current = prependSerial;
    } else if (isFreshLoad) {
      // Fresh load = first-ever fetch for this tab (or a refetch
      // after symbol/TF change, which resets candles + bumps
      // fetchSerial). Apply the saved per-tab state (viewport,
      // scaleMargins, priceRange) from Redux — this is how a tab
      // restored from workspace, OR a tab whose data just refetched
      // after a symbol change, gets its pan/scale/drag back. Falls
      // back to fitContent when no saved viewport exists or when
      // setVisibleRange throws (stale viewport from a previous TF
      // whose times don't intersect the new data).
      seenFetchSerialRef.current = fetchSerial;
      // (Signal mode is handled by the signal-first branch above.)

      // scaleMargins first — applyPriceRangeOrAutoFit below uses
      // autoScale: true which fits within the current margins.
      // Keep at least 0.22 bottom reserve when RSI is enabled so
      // the RSI strip stays below the main price data (the RSI
      // creation effect set this in the previous render, but its
      // deps don't include candles so it won't re-run here to
      // reclaim the space).
      const savedM = scaleMargins ?? { top: 0.1, bottom: 0.1 };
      const rsiBottom =
        indicators?.rsi.enabled || indicators?.anchorRsi?.enabled
          ? Math.max(savedM.bottom, 0.22)
          : savedM.bottom;
      chart.priceScale("right").applyOptions({
        scaleMargins: { top: savedM.top, bottom: rsiBottom },
      });

      // A timeframe-only change takes priority: restore the exact time
      // window the user was viewing (captured before the refetch) so the
      // chart stays put instead of snapping to the latest bar. Consume
      // the ref once.
      // Rebuild the window for a timeframe change: keep the same bar
      // count + midpoint, so the visible time span scales by the TF
      // ratio (H4→H1 = ÷4) and the chart stays centred on the same
      // moment at the same zoom. Sanity-gate on a positive bar count.
      // (`tfr` was captured above, before setData.)
      pendingTfRestoreRef.current = null;
      const tfRestoreRange =
        tfr && Number.isFinite(tfr.mid) && tfr.bars > 0
          ? (() => {
              const span = tfr.bars * tfSec;
              return {
                from: (tfr.mid - span / 2) as Time,
                to: (tfr.mid + span / 2) as Time,
              };
            })()
          : null;

      // Keep a restored window from parking in the trailing whitespace. A
      // TF-change window is rebuilt centred on the OLD window's midpoint, and a
      // saved viewport's `to` can sit at the live edge — either can leave the
      // window's right side in empty future space with candles bunched on the
      // left. If `to` overshoots the last real bar by more than
      // RESTORE_FUTURE_PAD_FRAC of the span, slide the whole window left by the
      // overshoot (span — i.e. zoom/bar-count — unchanged). Only triggers when
      // `to` actually overshoots, so a window panned into history is untouched.
      const clampRangeToData = (range: { from: Time; to: Time }) => {
        if (lastTime <= 0) return range;
        const from = range.from as number;
        const to = range.to as number;
        const maxTo = lastTime + (to - from) * RESTORE_FUTURE_PAD_FRAC;
        if (to > maxTo) {
          const shift = to - maxTo;
          return { from: (from - shift) as Time, to: (to - shift) as Time };
        }
        return range;
      };

      // Apply a restored visible range so it actually sticks. setVisibleRange
      // right after setData lands on an unsettled time scale and gets silently
      // overridden — the chart is still reindexing the new data (logs show the
      // logical range pointing at the OLD dataset's indices for a few frames,
      // longer for bigger datasets). So: apply, then re-apply every animation
      // frame UNTIL the chart honours it (within one bar of snap tolerance) or a
      // frame cap, which converges exactly when reindexing finishes regardless
      // of dataset size — then stops. Cancel any prior run's loop first.
      const applyRangeReliably = (range: { from: Time; to: Time }) => {
        rangeReassertRafRef.current.forEach((id) => cancelAnimationFrame(id));
        rangeReassertRafRef.current = [];
        const tol = Math.max(tfSec, 1); // getVisibleRange snaps to bar edges
        const reqFrom = range.from as number;
        const reqTo = range.to as number;
        const MAX_FRAMES = 20;
        let attempt = 0;
        // Previous frame's applied range — used to detect when the time scale
        // has SETTLED (stopped changing). We stop on settle, not on exact match,
        // because the chart legitimately snaps the target across data gaps
        // (weekends) to the nearest real bar — that snapped result is correct
        // and will never equal the request, so matching exactly would spin to
        // the cap for any window whose edge lands in a market-closed gap.
        let prevFrom = NaN;
        let prevTo = NaN;
        const step = () => {
          try {
            timeScale.setVisibleRange(range);
          } catch {
            /* range may not intersect data on an interim frame; ignore */
          }
          attempt += 1;
          const ar = timeScale.getVisibleRange();
          const af = ar?.from as number;
          const at = ar?.to as number;
          const matchedReq =
            ar != null &&
            Math.abs(af - reqFrom) <= tol &&
            Math.abs(at - reqTo) <= tol;
          const settled =
            attempt > 1 &&
            ar != null &&
            Math.abs(af - prevFrom) <= tol &&
            Math.abs(at - prevTo) <= tol;
          if (matchedReq || settled || attempt >= MAX_FRAMES) {
            return;
          }
          prevFrom = af;
          prevTo = at;
          const id = requestAnimationFrame(step);
          rangeReassertRafRef.current = [id];
        };
        step();
      };

      const hasViewport =
        viewport &&
        Number.isFinite(viewport.from) &&
        Number.isFinite(viewport.to);

      // Leaving replay outranks the saved viewport: this refetch exists only
      // to fill the bars replay muted, and the user should come back to the
      // moment they replayed TO — now with the future filled in — not be
      // thrown to wherever the camera was before they rewound. Consumed once.
      const replayRestore = pendingReplayRestoreRef.current;
      pendingReplayRestoreRef.current = null;

      if (replayRestore) {
        applyRangeReliably(
          clampRangeToData({
            from: replayRestore.from as Time,
            to: replayRestore.to as Time,
          }),
        );
        // Re-fit the price axis to what is now in view: the revealed bars to
        // the right of the cursor can sit well outside the replayed range.
        series.applyOptions({
          autoscaleInfoProvider: (base: () => unknown) => base() as never,
        });
        chart.priceScale("right").applyOptions({ autoScale: true });
        seenFetchSerialRef.current = fetchSerial;
        setRangeTick((v) => v + 1);
        return;
      }

      if (tfRestoreRange) {
        // Rebuilt TF window, clamped away from the trailing whitespace, applied
        // reliably (re-asserted past the unsettled-layout frame).
        applyRangeReliably(clampRangeToData(tfRestoreRange));
        // Price: auto-fit to the candles now in view (the saved priceRange drag
        // belonged to the old window). autoScale + pass-through provider lets
        // lightweight-charts fit to the visible high/low.
        series.applyOptions({
          autoscaleInfoProvider: (base: () => unknown) => base() as never,
        });
        chart.priceScale("right").applyOptions({ autoScale: true });
        // Skip the saved-priceRange apply below for this branch.
        seenFetchSerialRef.current = fetchSerial;
        setRangeTick((v) => v + 1);
        return;
      } else if (hasViewport) {
        applyRangeReliably(
          clampRangeToData({
            from: viewport.from as Time,
            to: viewport.to as Time,
          }),
        );
      } else {
        fitToContent();
      }
      applyPriceRangeOrAutoFit(chart, series, priceRange);
    }
    // else: data unchanged — nothing to do.

    // Force a synchronous re-render so the SVG drawings (fibs, lines)
    // recompute their X coordinates against the chart's NEW data state.
    // Without this, the SVG renders during React commit using new
    // candles selector data but the chart's series still has the OLD
    // data — logicalToCoordinate then returns positions calibrated to
    // the wrong dataset, producing visibly-misaligned drawings on
    // timeframe change.
    setRangeTick((v) => v + 1);
  }, [
    candles,
    chartType,
    prependSerial,
    lastPrependCount,
    fetchSerial,
    timeframe,
    seriesEpoch,
    signalMode,
    replayActive,
  ]);

  // Replay: keep the cursor bar on screen as it advances.
  //
  // setData preserves the visible LOGICAL range, so appending the next bar
  // doesn't move the view — which is right for a step or two, and wrong once
  // Play has walked the cursor off the right edge. When the newest bar
  // crosses the right edge, slide the window (same zoom) so the cursor sits
  // ~75% across, leaving a quarter-screen of context ahead of it.
  //
  // Deliberately NOT keyed on the cursor time: the bar has to actually be in
  // the series before it can be scrolled to, and `candles.length` changes on
  // exactly the render where that happened.
  useEffect(() => {
    if (!replayActive || candles.length === 0) return;
    const chart = chartRef.current;
    if (!chart) return;
    const ts = chart.timeScale();
    const range = ts.getVisibleLogicalRange();
    if (!range) return;
    const lastIdx = candles.length - 1;
    if (lastIdx <= range.to - 1) return; // still comfortably in view
    const span = range.to - range.from;
    ts.setVisibleLogicalRange({
      from: lastIdx - span * 0.75,
      to: lastIdx + span * 0.25,
    });
  }, [candles.length, replayActive]);

  // Volume profile overlay (porting renderTickProfile from legacy).
  // Filters bins with tick_count > 50, draws horizontal bars on the left
  // of the chart with width proportional to tick_count. The POC bin
  // (highest tick_count) is yellow; others are blue.
  useEffect(() => {
    const chart = chartRef.current;
    const container = containerRef.current;
    if (!chart || !container) return;

    // Belt-and-suspenders cleanup: drop any stale overlay that survived
    // the previous render cycle (rare, but possible if the cleanup
    // function threw or ran out of order). Clear Analysis → rawBins=[]
    // must visually drop the volume profile, even if React's cleanup
    // misfires.
    container
      .querySelectorAll(`.${styles.volumeProfileOverlay}`)
      .forEach((el) => el.remove());

    if (rawBins.length === 0) return;
    // No threshold: render every non-zero bin. Important for spotting
    // wicks/spikes to session extremes — those bins have very few
    // ticks (price visited briefly and bounced) and were previously
    // hidden under an adaptive 5 %-of-peak floor. The 10 px min width
    // below ensures sub-1 % bins stay clickable.
    const significant = rawBins.filter((b: RawBin) => b.tick_count > 0);
    if (significant.length === 0) return;

    const maxTicks = Math.max(...significant.map((b) => b.tick_count));
    const pocBin = significant.reduce(
      (best, b) => (b.tick_count > best.tick_count ? b : best),
      significant[0],
    );

    const overlay = document.createElement("div");
    overlay.className = styles.volumeProfileOverlay;
    container.appendChild(overlay);

    const bars: { el: HTMLDivElement; price: number }[] = significant.map((bin) => {
      const el = document.createElement("div");
      el.className = styles.volumeProfileBar;
      const width = Math.max(10, (bin.tick_count / maxTicks) * 400);
      el.style.width = `${width}px`;
      el.style.background = bin === pocBin ? "#FFC107" : "#2196F3";
      el.title = `Price: ${bin.price.toFixed(digitsFor(symbol))}, Volume: ${bin.tick_count.toLocaleString()}`;
      overlay.appendChild(el);
      return { el, price: bin.price };
    });

    // Live-only overlay — bars rendered on top with their own scale
    // so a few-tick additions become visible even when the prefetched
    // profile dominates the absolute counts. Min-width 4px keeps tiny
    // bins detectable; bright orange/red plus left-offset keeps it
    // distinguishable from the underlying blue/yellow profile.
    const liveSignificant = liveBins.filter((b) => b.tick_count > 0);
    if (liveSignificant.length > 0) {
      const liveMax = Math.max(...liveSignificant.map((b) => b.tick_count));
      const livePoc = liveSignificant.reduce(
        (best, b) => (b.tick_count > best.tick_count ? b : best),
        liveSignificant[0],
      );
      liveSignificant.forEach((bin) => {
        const el = document.createElement("div");
        el.className = styles.volumeProfileLiveBar;
        // Cap width so we don't overshoot the prefetched bars; same
        // visual budget (400px) but scaled to live's own max.
        const width = Math.max(4, (bin.tick_count / liveMax) * 120);
        el.style.width = `${width}px`;
        el.style.background = bin === livePoc ? "#FF6D00" : "#FF8A65";
        el.title = `Live · Price: ${bin.price.toFixed(digitsFor(symbol))}, Volume: ${bin.tick_count.toLocaleString()}`;
        overlay.appendChild(el);
        bars.push({ el, price: bin.price });
      });
    }

    let frame = 0;
    const reposition = () => {
      const series = seriesRef.current;
      if (!series) return;
      bars.forEach(({ el, price }) => {
        const y = series.priceToCoordinate(price);
        if (y === null) {
          el.style.display = "none";
        } else {
          el.style.display = "block";
          el.style.top = `${y - 1}px`;
        }
      });
    };
    const scheduleReposition = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(reposition);
    };

    reposition();

    // Horizontal pan/zoom — fires on every visible-range change.
    chart.timeScale().subscribeVisibleLogicalRangeChange(scheduleReposition);

    // Price-scale drags don't emit a dedicated event, and crosshairMove
    // only fires when the cursor is over the chart canvas (not the price
    // axis you're dragging). Legacy's solution: poll while a mouse button
    // is held down anywhere on the chart container, plus catch wheel
    // (zoom) and ResizeObserver (layout). Same approach here.
    let pollHandle: number | null = null;
    const startPoll = () => {
      if (pollHandle != null) return;
      pollHandle = window.setInterval(scheduleReposition, 100);
    };
    const stopPoll = () => {
      if (pollHandle != null) {
        clearInterval(pollHandle);
        pollHandle = null;
      }
      // Final settle pass after the drag ends.
      window.setTimeout(scheduleReposition, 50);
    };
    container.addEventListener("mousedown", startPoll);
    window.addEventListener("mouseup", stopPoll);
    container.addEventListener("mouseleave", stopPoll);

    const onWheel = () => window.setTimeout(scheduleReposition, 25);
    container.addEventListener("wheel", onWheel, { passive: true });

    const ro = new ResizeObserver(() => window.setTimeout(scheduleReposition, 50));
    ro.observe(container);

    return () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(scheduleReposition);
      container.removeEventListener("mousedown", startPoll);
      window.removeEventListener("mouseup", stopPoll);
      container.removeEventListener("mouseleave", stopPoll);
      container.removeEventListener("wheel", onWheel);
      ro.disconnect();
      if (pollHandle != null) clearInterval(pollHandle);
      if (frame) cancelAnimationFrame(frame);
      overlay.remove();
    };
  }, [rawBins, liveBins, chartType]);

  // Capture clicks to place fib / position anchors. Two clicks define
  // a drawing; the first stores the start anchor, the second creates
  // the drawing and exits drawing mode. Press-drag is an alternative
  // single-gesture path (mousedown = anchor, mouseup past 5 px = drag-
  // place). Same flow for fib and long/short positions.
  //
  // Stable handler — read anchors from refs so we don't have to
  // re-subscribe on every state change (which can race with
  // lightweight-charts' internal click dispatch).
  const fibAnchorRef = useRef(fibAnchor);
  useEffect(() => {
    fibAnchorRef.current = fibAnchor;
  }, [fibAnchor]);
  const positionAnchorRef = useRef(positionAnchor);
  useEffect(() => {
    positionAnchorRef.current = positionAnchor;
  }, [positionAnchor]);
  const diagonalAnchorRef = useRef(diagonalAnchor);
  useEffect(() => {
    diagonalAnchorRef.current = diagonalAnchor;
  }, [diagonalAnchor]);
  const priceRangeAnchorRef = useRef(priceRangeAnchor);
  useEffect(() => {
    priceRangeAnchorRef.current = priceRangeAnchor;
  }, [priceRangeAnchor]);

  // Distance (in CSS pixels) past which a mousedown→mouseup is treated
  // as a drag-place rather than a click. ~5px feels right: anything
  // less is a user clicking-with-a-twitch.
  const FIB_DRAG_THRESHOLD_PX = 5;

  // Pending fib drag — set on mousedown, cleared on mouseup. Stores
  // both the (time, price) of the mousedown AND the raw client
  // coordinates so we can measure travel distance.
  const [fibDragStart, setFibDragStart] = useState<{
    time: number;
    price: number;
    clientX: number;
    clientY: number;
  } | null>(null);

  // Ref mirror so the stable mouseup listener can read the latest
  // value without re-subscribing the listener every state change.
  const fibDragStartRef = useRef(fibDragStart);
  useEffect(() => {
    fibDragStartRef.current = fibDragStart;
  }, [fibDragStart]);

  // Inline text-label editor — when set, an HTML <input> is overlaid on
  // the chart at the label's anchor so the user can type. Opened right
  // after placing a text annotation and on double-click of an existing one.
  const [editingText, setEditingText] = useState<
    { id: string; value: string } | null
  >(null);

  useEffect(() => {
    if (activeDrawingTool == null) return;
    const container = containerRef.current;
    if (!container) return;

    const pointAt = (clientX: number, clientY: number) => {
      const chart = chartRef.current;
      const series = seriesRef.current;
      if (!chart || !series) return null;
      const rect = container.getBoundingClientRect();
      const yCoord = clientY - rect.top;
      let price = series.coordinateToPrice(yCoord) as number | null;
      if (price != null) {
        // Keep the price-scale calibration fresh while OHLC bars are visible.
        const y2 = yCoord < rect.height * 0.5 ? yCoord + 50 : yCoord - 50;
        const p2 = series.coordinateToPrice(y2);
        if (p2 != null) {
          const slope = (p2 - price) / (y2 - yCoord);
          priceScaleCalibRef.current = { slope, intercept: price - slope * yCoord };
        }
      } else {
        // coordinateToPrice returns null when the visible range is entirely
        // whitespace (all OHLC bars panned off-screen left). Use the last
        // cached linear scale so anchors can still be placed in the future area.
        const calib = priceScaleCalibRef.current;
        if (calib) price = calib.slope * yCoord + calib.intercept;
      }
      if (price == null) return null;
      const x = clientX - rect.left;
      // Anchor times ALWAYS come from the logical axis, never coordinateToTime.
      // coordinateToTime walks the appended whitespace, which is spaced by the
      // nominal timeframe, while the renderer (timeToLogical) extrapolates the
      // future area by the LAST bar spacing. Just after a session open the
      // newest bar straddles the gap, the two grids diverge by ~gap/tf, and
      // anchors placed in the empty area land far from the cursor (issue #109).
      // logicalToTime is the exact inverse of timeToLogical, so place ↔ render
      // round-trips exactly at any timeframe, gap or no gap.
      // Anchors still can't be placed over the right price-scale strip.
      const psWidth = chart.priceScale("right").width();
      if (x > rect.width - psWidth) return null;
      const logical = chart.timeScale().coordinateToLogical(x);
      if (logical == null) return null;
      const time = logicalToTime(logical as number, candlesRef.current);
      if (time == null) return null;
      return { time, price };
    };

    // Two-anchor tools — fib + diagonal + long/short positions —
    // share the mousedown/mouseup commit flow. The click handler only
    // fires for true single-click tools (horizontal/vertical line).
    const isTwoAnchorTool =
      activeDrawingTool === "fibonacci" ||
      activeDrawingTool === "diagonal" ||
      activeDrawingTool === "long" ||
      activeDrawingTool === "short" ||
      activeDrawingTool === "priceRange";

    const onClick = (e: MouseEvent) => {
      // Two-anchor tools commit in mouseup so they can distinguish
      // click-1 / click-2 / drag.
      if (isTwoAnchorTool) return;
      const pt = pointAt(e.clientX, e.clientY);
      if (!pt) return;
      if (activeDrawingTool === "horizontal") {
        dispatch(
          addHorizontalLine({
            tabId,
            line: { id: `hline_${Date.now()}`, price: pt.price },
          }),
        );
      } else if (activeDrawingTool === "vertical") {
        dispatch(
          addVerticalLine({
            tabId,
            line: { id: `vline_${Date.now()}`, time: pt.time },
          }),
        );
      } else if (activeDrawingTool === "path") {
        // Click adds a vertex to the in-progress polyline. Commit
        // happens on dblclick / Esc (handled below).
        dispatch(
          appendPathVertex({
            tabId,
            point: { time: pt.time, price: pt.price },
          }),
        );
      } else if (activeDrawingTool === "text") {
        const id = placeTextLabel(pt.time, pt.price, tabId, dispatch);
        // Open the inline editor immediately so the user can type.
        setEditingText({ id, value: "" });
      }
    };

    // Path-tool only: double-click finalises the polyline. The
    // trailing click of the dblclick gesture already appended a
    // near-duplicate vertex — `dropLast: true` strips it before
    // committing so the path ends at the intentional click position.
    const onDblClick = (e: MouseEvent) => {
      if (activeDrawingTool !== "path") return;
      e.preventDefault();
      e.stopPropagation();
      dispatch(
        commitPathDraft({
          tabId,
          id: `path_${Date.now()}`,
          dropLast: true,
        }),
      );
    };

    // Two-anchor tools — mousedown records the start; mouseup decides
    // whether this was a click or a drag and commits accordingly.
    const onMouseDown = (e: MouseEvent) => {
      if (!isTwoAnchorTool) return;
      if (e.button !== 0) return; // left button only
      const pt = pointAt(e.clientX, e.clientY);
      if (!pt) return;
      // Suppress lightweight-charts' pan-on-drag while we own the
      // gesture (preventDefault on mousedown blocks the chart's
      // internal handler — re-enabled implicitly on mouseup).
      e.preventDefault();
      setFibDragStart({
        time: pt.time,
        price: pt.price,
        clientX: e.clientX,
        clientY: e.clientY,
      });
    };

    const onMouseUp = (e: MouseEvent) => {
      if (!isTwoAnchorTool) return;
      if (e.button !== 0) return;
      const start = fibDragStartRef.current;
      setFibDragStart(null);
      if (!start) return;
      const endPt = pointAt(e.clientX, e.clientY);
      if (!endPt) return;

      const dx = e.clientX - start.clientX;
      const dy = e.clientY - start.clientY;
      const moved =
        Math.sqrt(dx * dx + dy * dy) >= FIB_DRAG_THRESHOLD_PX;

      if (activeDrawingTool === "fibonacci") {
        if (moved) {
          // Drag — mousedown is the start anchor.
          dispatch(addFib({ tabId, fib: buildFib(start, endPt) }));
        } else if (fibAnchorRef.current) {
          // Click 2 — finalize with the previously-set anchor.
          dispatch(addFib({ tabId, fib: buildFib(fibAnchorRef.current, endPt) }));
        } else {
          // Click 1 — set anchor and wait for click 2 / drag.
          dispatch(setFibAnchor({ tabId, anchor: endPt }));
        }
        return;
      }

      if (activeDrawingTool === "diagonal") {
        // Same two-anchor flow as fib, but commits a single trend segment.
        if (moved) {
          dispatch(addDiagonalLine({ tabId, line: buildDiagonalLine(start, endPt) }));
        } else if (diagonalAnchorRef.current) {
          dispatch(
            addDiagonalLine({
              tabId,
              line: buildDiagonalLine(diagonalAnchorRef.current, endPt),
            }),
          );
        } else {
          dispatch(setDiagonalAnchor({ tabId, anchor: endPt }));
        }
        return;
      }

      if (activeDrawingTool === "priceRange") {
        // Same two-anchor flow as fib/diagonal; commits a measurement box.
        const tfSec = TIMEFRAME_SECONDS[timeframe] ?? 3600;
        if (moved) {
          dispatch(
            addPriceRangeBox({ tabId, box: buildPriceRangeBox(start, endPt, tfSec) }),
          );
        } else if (priceRangeAnchorRef.current) {
          dispatch(
            addPriceRangeBox({
              tabId,
              box: buildPriceRangeBox(priceRangeAnchorRef.current, endPt, tfSec),
            }),
          );
        } else {
          dispatch(setPriceRangeAnchor({ tabId, anchor: endPt }));
        }
        return;
      }

      // Long or short — same anchor flow, position commit shape.
      const type: PositionType =
        activeDrawingTool === "long" ? "long" : "short";
      const tfSec = TIMEFRAME_SECONDS[timeframe] ?? 3600;
      if (moved) {
        dispatch(
          addPosition({ tabId, position: buildPosition(type, start, endPt, tfSec) }),
        );
      } else if (positionAnchorRef.current) {
        dispatch(
          addPosition({
            tabId,
            position: buildPosition(type, positionAnchorRef.current, endPt, tfSec),
          }),
        );
      } else {
        dispatch(setPositionAnchor({ tabId, anchor: endPt }));
      }
    };

    // Escape commits the path draft (matches dblclick behaviour;
    // commitPathDraft silently discards drafts with < 2 vertices).
    const onKey = (e: KeyboardEvent) => {
      if (activeDrawingTool !== "path") return;
      if (e.key !== "Escape") return;
      const active = document.activeElement as HTMLElement | null;
      if (
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      dispatch(commitPathDraft({ tabId, id: `path_${Date.now()}` }));
    };

    container.addEventListener("click", onClick);
    container.addEventListener("mousedown", onMouseDown);
    container.addEventListener("dblclick", onDblClick);
    // mouseup on window (not container) so dragging off the chart
    // still commits — TradingView does the same.
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("keydown", onKey);
    return () => {
      container.removeEventListener("click", onClick);
      container.removeEventListener("mousedown", onMouseDown);
      container.removeEventListener("dblclick", onDblClick);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("keydown", onKey);
    };
  }, [activeDrawingTool, dispatch, tabId, timeframe]);

  // Track a "tick" that increments on every chart range change so the
  // SVG fib overlay re-renders with up-to-date pixel coordinates as the
  // user pans/zooms or resizes the price axis. rAF-throttled so rapid
  // events collapse into one render per frame.
  //
  // Price-scale drags don't emit a dedicated lightweight-charts event,
  // so we fall back to polling at 100ms while a mouse button is held
  // anywhere on the chart container — same trick used by the volume
  // profile overlay.
  const [, setRangeTick] = useState(0);
  useEffect(() => {
    const chart = chartRef.current;
    const container = containerRef.current;
    if (!chart || !container) return;

    let frame = 0;
    const schedule = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setRangeTick((v) => v + 1));
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(schedule);

    // While the mouse is held down anywhere on the chart, run a 60fps
    // rAF loop that keeps re-ticking — covers price-scale drags which
    // have no dedicated event. Stops on mouseup / mouseleave.
    let dragRaf: number | null = null;
    const dragLoop = () => {
      setRangeTick((v) => v + 1);
      dragRaf = requestAnimationFrame(dragLoop);
    };
    const startDragLoop = () => {
      if (dragRaf != null) return;
      dragRaf = requestAnimationFrame(dragLoop);
    };
    const stopDragLoop = () => {
      if (dragRaf != null) {
        cancelAnimationFrame(dragRaf);
        dragRaf = null;
      }
      schedule();
    };
    container.addEventListener("mousedown", startDragLoop);
    window.addEventListener("mouseup", stopDragLoop);
    container.addEventListener("mouseleave", stopDragLoop);

    const onWheel = () => window.setTimeout(schedule, 25);
    container.addEventListener("wheel", onWheel, { passive: true });

    const ro = new ResizeObserver(() => window.setTimeout(schedule, 50));
    ro.observe(container);

    return () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(schedule);
      container.removeEventListener("mousedown", startDragLoop);
      window.removeEventListener("mouseup", stopDragLoop);
      container.removeEventListener("mouseleave", stopDragLoop);
      container.removeEventListener("wheel", onWheel);
      ro.disconnect();
      if (dragRaf != null) cancelAnimationFrame(dragRaf);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Keyboard actions on the selected drawing — standardized for EVERY kind:
  // Delete/Backspace removes it (via the shared DRAWING_REMOVERS registry, so
  // a new tool can't forget its delete wiring), Escape deselects. Ignored
  // while focus is in a form field (so the user can still edit text / settings
  // inputs).
  useEffect(() => {
    if (!selectedDrawing) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const isDelete = e.key === "Delete" || e.key === "Backspace";
      const isEsc = e.key === "Escape";
      if (!isDelete && !isEsc) return;
      const active = document.activeElement as HTMLElement | null;
      if (
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.tagName === "SELECT" ||
          active.isContentEditable)
      ) {
        return;
      }
      if (isEsc) {
        dispatch(setSelectedDrawing({ tabId, selected: null }));
        return;
      }
      e.preventDefault();
      const { kind, id } = selectedDrawing;
      DRAWING_REMOVERS[kind]?.(dispatch, tabId, id);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedDrawing, dispatch, tabId]);

  // Clear selection when the user clicks the empty chart area (not on
  // a drawing handle). Drawing-handle mousedowns stopPropagation, so
  // those don't reach this listener. Skipped while a drawing tool is
  // active because chart clicks there mean "place new drawing".
  useEffect(() => {
    if (activeDrawingTool != null) return;
    const container = containerRef.current;
    if (!container) return;
    const onMouseDown = (e: MouseEvent) => {
      if ((e.target as Element).closest?.('[data-drawing-hit]')) return;
      if (selectedDrawing) dispatch(setSelectedDrawing({ tabId, selected: null }));
    };
    container.addEventListener("mousedown", onMouseDown);
    return () => container.removeEventListener("mousedown", onMouseDown);
  }, [activeDrawingTool, selectedDrawing, dispatch, tabId]);

  // Right-click on empty chart area opens a context menu — currently
  // just "Jump to latest candle". Drawing handles set their own
  // onContextMenu with stopPropagation, so right-clicks on them never
  // reach here.
  //
  // Instead of calling lightweight-charts' `scrollToRealTime()` (which
  // pins the last candle to the right edge with no padding), we
  // re-apply the same logical-range formula used on initial load:
  // last N bars + 20% right-padding. Matches the user's initial view.
  //
  // Candle count is read via a ref so the listener doesn't need to
  // re-register on every candle update (live tick stream would
  // otherwise churn it constantly).
  const candleCountRef = useRef(candles.length);
  useEffect(() => {
    candleCountRef.current = candles.length;
  }, [candles.length]);
  // (Legacy plainDarkChart / monoCandles refs lived here so the
  // long-lived context-menu builder could read their latest values.
  // Both toggles are now subsumed by the Chart colors… dialog, so
  // the refs are no longer needed — the dialog reads from the live
  // selector on every render.)
  // Ref mirrors of the trading server visibility flags so the long-lived
  // contextmenu handler can read the latest values without re-
  // registering (which would tear down + re-add the listener on
  // every toggle).
  const showTradingTradesRef = useRef(showTradingTrades);
  useEffect(() => {
    showTradingTradesRef.current = showTradingTrades;
  }, [showTradingTrades]);
  const showTradingSlTpRef = useRef(showTradingSlTp);
  useEffect(() => {
    showTradingSlTpRef.current = showTradingSlTp;
  }, [showTradingSlTp]);
  const showHistoryOrdersRef = useRef(showHistoryOrders);
  useEffect(() => {
    showHistoryOrdersRef.current = showHistoryOrders;
  }, [showHistoryOrders]);
  // Signal chart should default to showing running trades AND SL/TP (a
  // reference is useless without them). The per-tab defaults are `false`
  // (owner tabs open with the trading overlay hidden), so seed the signal
  // tab ON — but only ONCE, before any tab state exists, so a user who later
  // toggles a layer off keeps their choice across open/close. `setShow*`
  // creates the tab entry via getTab, so after the first run `signalTabSeeded`
  // is true and this never re-fires.
  const signalTabSeeded = useAppSelector(
    (s) => signalMode && !!s.ui.byTab[tabId],
  );
  useEffect(() => {
    if (!signalMode || signalTabSeeded) return;
    dispatch(setShowTradingTrades({ tabId, show: true }));
    dispatch(setShowTradingSlTp({ tabId, show: true }));
  }, [signalMode, signalTabSeeded, tabId, dispatch]);
  // RBAC trading gates: `read` shows the trading surface (lines, panel,
  // positions); `write` enables order placement / modify / close. Refs so the
  // context-menu builder (which runs outside render) sees current values.
  // In signal mode the chart is a READ-ONLY reference of ANOTHER account, so
  // read is forced on (show the overlays for everyone) and write is forced OFF
  // (no trade panel, drag-to-modify, right-click trade, or hotkeys — this is a
  // single gate that every trade interaction already routes through).
  const rawCanTradeRead = useCan(CAP.tradingRead);
  const rawCanTradeWrite = useCan(CAP.tradingWrite);
  const canTradeRead = signalMode ? true : rawCanTradeRead;
  const canTradeWrite = signalMode ? false : rawCanTradeWrite;
  const canTradeReadRef = useRef(canTradeRead);
  const canTradeWriteRef = useRef(canTradeWrite);
  useEffect(() => {
    canTradeReadRef.current = canTradeRead;
    canTradeWriteRef.current = canTradeWrite;
  }, [canTradeRead, canTradeWrite]);

  // One-click trading panel state + shared default volume. The volume is
  // held in a ref too so the right-click instant/pending trade items
  // (built inside the [symbol]-keyed context-menu effect) read the live
  // value without rebuilding the menu on every volume tweak.
  const oneClickPanelOpen = useAppSelector((s) => s.ui.oneClickPanelOpen);
  const oneClickPanelOpenRef = useRef(oneClickPanelOpen);
  useEffect(() => {
    oneClickPanelOpenRef.current = oneClickPanelOpen;
  }, [oneClickPanelOpen]);
  const tradeVolume = useAppSelector((s) => s.ui.tradeVolume);
  const tradeVolumeRef = useRef(tradeVolume);
  useEffect(() => {
    tradeVolumeRef.current = tradeVolume;
  }, [tradeVolume]);
  // Live positions/orders in refs so the [symbol]-keyed context-menu
  // effect can find the trade under the cursor on right-click.
  const tradingPositionsRef = useRef(tradingPositions);
  useEffect(() => {
    tradingPositionsRef.current = tradingPositions;
  }, [tradingPositions]);
  const tradingOrdersRef = useRef(tradingOrders);
  useEffect(() => {
    tradingOrdersRef.current = tradingOrders;
  }, [tradingOrders]);

  // Quick actions from a right-click on a trading line. Each toasts the
  // outcome; the SSE stream reconciles the chart afterward.
  const runQuickAction = useCallback(
    async (
      fn: () => Promise<{ ok: boolean; human_msg: string }>,
      okTitle: string,
    ) => {
      try {
        const res = await fn();
        dispatch(
          res.ok
            ? pushToast({ kind: "success", title: okTitle })
            : pushToast({
                kind: "error",
                title: "Action failed",
                message: res.human_msg,
              }),
        );
      } catch (e) {
        dispatch(
          pushToast({
            kind: "error",
            title: "Action failed",
            message: e instanceof ApiError ? e.message : "Network error",
          }),
        );
      }
    },
    [dispatch],
  );

  // Market-close every open position (panic "close all"). Closes are issued
  // sequentially — the backend serializes order_send under its poll lock
  // anyway — and the outcome is summarized in one toast.
  const closeAllPositions = useCallback(async () => {
    const positions = tradingPositionsRef.current.slice();
    if (!positions.length) return;
    let ok = 0;
    let failed = 0;
    let lastErr = "";
    for (const p of positions) {
      try {
        const res = await api.tradingCloseOrder({ ticket: p.ticket });
        if (res.ok) ok++;
        else {
          failed++;
          lastErr = res.human_msg;
        }
      } catch (e) {
        failed++;
        lastErr = e instanceof ApiError ? e.message : "Network error";
      }
    }
    if (failed === 0) {
      dispatch(
        pushToast({
          kind: "success",
          title: `Closed ${ok} position${ok === 1 ? "" : "s"}`,
        }),
      );
    } else {
      dispatch(
        pushToast({
          kind: ok ? "warning" : "error",
          title: `Closed ${ok}, ${failed} failed`,
          message: lastErr,
        }),
      );
    }
  }, [dispatch]);

  // Decimal places of a price, so a default SL/TP rounds to the symbol's
  // precision (the broker rejects off-tick prices).
  const priceDecimals = (ref: number): number => {
    const s = String(ref);
    const i = s.indexOf(".");
    return i < 0 ? 0 : s.length - i - 1;
  };

  // `O` hotkey → toggle the one-click trading panel for the active chart.
  // Gated to the single active tab so the background (hidden) chart
  // instances — which are all mounted — don't each fire on one keypress,
  // and skipped while typing in a field. Needs trading.write (the panel
  // places orders).
  const activeTabId = useAppSelector((s) => s.tabs.activeTabId);
  useEffect(() => {
    if (!isActive || tabId !== activeTabId || !canTradeWrite) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "o" && e.key !== "O") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      )
        return;
      dispatch(toggleOneClickPanel());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isActive, tabId, activeTabId, canTradeWrite, dispatch]);

  // Expose this tab's chart capture to non-Chart consumers (the
  // TradeJournal panel calls captureChart(tabId) to attach a
  // screenshot to a journal entry). The capture mirrors the
  // context-menu "Save/Copy chart as image" path — same html2canvas
  // config, same wrapper target — so journal screenshots look
  // identical to the ones a user could capture via right-click.
  useEffect(() => {
    registerChartPan(tabId, (from, to) => {
      const chart = chartRef.current;
      const series = seriesRef.current;
      if (!chart || !series) return;
      try {
        chart.timeScale().setVisibleRange({
          from: from as Time,
          to: to as Time,
        });
        // Release any locked price range and re-fit to the bars
        // we just panned to. Otherwise the price axis stays glued
        // to "current price" and the historical trade is off-screen.
        //
        // Mirrors the visibility-gated lock effect: pass-through
        // provider + autoScale TRUE makes lightweight-charts re-run
        // the fit; the 2-rAF delayed autoScale FALSE then unlocks
        // the price axis so the user can drag it manually. Without
        // the unlock the axis stays in auto mode and rejects drag
        // input — same bug as the original lock effect.
        series.applyOptions({
          autoscaleInfoProvider: (base: () => unknown) => base() as never,
        });
        const priceScale = chart.priceScale("right");
        priceScale.applyOptions({ autoScale: true });
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            priceScale.applyOptions({ autoScale: false });
          });
        });
      } catch {
        // setVisibleRange throws if the range is outside the
        // loaded data; the caller will fall back to a Redux
        // viewport+symbol dispatch (data refetch then applies it).
      }
    });
    registerCapture(tabId, async () => {
      const target = wrapperRef.current;
      if (!target) return { blob: null, symbol };
      const canvas = await html2canvas(target, {
        backgroundColor: null,
        scale: window.devicePixelRatio || 1,
        logging: false,
        useCORS: true,
        ignoreElements: (el) =>
          el.classList?.contains(styles.contextMenu) ?? false,
      });
      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      return { blob, symbol };
    });
    return () => {
      unregisterCapture(tabId);
      unregisterChartPan(tabId);
    };
  }, [tabId, symbol]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // Right-click listener goes on the WRAPPER (canvas + SVG overlay), not
    // the canvas alone — otherwise a right-click on a trading-line drag
    // strip (which lives in the overlay, a sibling of the canvas) never
    // bubbles to this listener and the menu wouldn't open on a line.
    const ctxTarget = wrapperRef.current ?? container;
    // Capture the entire chart container (chart canvas + SVG drawing
    // overlay + HTML overlays like the volume profile and the
    // symbol+OHLC header) into a single PNG blob via html2canvas.
    //
    // Previously we tried to composite the SVG manually by
    // serialising it and loading via `Image`, but browsers render
    // SVG-as-Image inconsistently — drawings often came out blank.
    // html2canvas walks the DOM and rasterises everything visually
    // present, so we get a faithful screenshot regardless of which
    // element renders which part.
    //
    // `scale: window.devicePixelRatio` so the export matches what
    // the user sees on their screen at high-DPI; `backgroundColor:
    // null` preserves the chart's own dark background; `logging:
    // false` silences the library's verbose console output.
    const captureAsBlob = async (): Promise<Blob | null> => {
      // Capture the outer wrapper, not the chart-canvas-only div —
      // the SVG drawing overlay and symbol/OHLC header are siblings of
      // the chart div, so capturing containerRef misses them.
      const target = wrapperRef.current;
      if (!target) return null;
      const canvas = await html2canvas(target, {
        backgroundColor: null,
        scale: window.devicePixelRatio || 1,
        logging: false,
        useCORS: true,
        // Skip the right-click context menu — it's the very element
        // the user clicked to trigger this capture, so it would
        // otherwise be baked into the screenshot.
        ignoreElements: (el) =>
          el.classList?.contains(styles.contextMenu) ?? false,
      });
      return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    };
    const saveAsImage = async () => {
      const blob = await captureAsBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Filename: tickview_SYMBOL_YYYY-MM-DDTHH-MM-SS.png (UTC, via chartTime).
      a.download = `tickview_${symbol}_${nowFileStamp()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };
    const copyToClipboard = async () => {
      const blob = await captureAsBlob();
      if (!blob) return;
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": blob }),
        ]);
      } catch (err) {
        // Clipboard API needs a secure context + user gesture. Fall
        // back to a download so the user still gets the image.
        console.warn("clipboard write failed, falling back to download:", err);
        await saveAsImage();
      }
    };
    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      // Price under the cursor — used by the right-click pending-order
      // items (the clicked level becomes the trigger price).
      const ccRect = container.getBoundingClientRect();
      const ccPrice = seriesRef.current
        ? (seriesRef.current.coordinateToPrice(e.clientY - ccRect.top) as
            | number
            | null)
        : null;
      const vol = tradeVolumeRef.current;
      const q = liveBidAskRef.current;
      const buyKind: "limit" | "stop" =
        ccPrice != null && q ? (ccPrice <= q.ask ? "limit" : "stop") : "limit";
      const sellKind: "limit" | "stop" =
        ccPrice != null && q ? (ccPrice >= q.bid ? "limit" : "stop") : "limit";

      // Quick actions when right-clicking ON a trading line: close a
      // running position, or add SL/TP / cancel a pending order. We match
      // the line under the cursor by pixel proximity to its entry/SL/TP.
      const ccY = e.clientY - ccRect.top;
      const series = seriesRef.current;
      const nearY = (price: number): boolean => {
        if (!series || !(price > 0)) return false;
        const y = series.priceToCoordinate(price);
        return y != null && Math.abs(y - ccY) <= 7;
      };
      const tradeItems: MenuItem[] = [];
      if (canTradeWriteRef.current && showTradingTradesRef.current) {
        const posUnder = tradingPositionsRef.current.find(
          (p) =>
            p.symbol === symbol &&
            (nearY(p.price_open) || nearY(p.sl) || nearY(p.tp)),
        );
        const ordUnder = posUnder
          ? undefined
          : tradingOrdersRef.current.find(
              (o) =>
                o.symbol === symbol &&
                (nearY(o.price_open) || nearY(o.sl) || nearY(o.tp)),
            );
        if (posUnder) {
          const t = posUnder.ticket;
          const half =
            Math.max(0.01, Math.round((posUnder.volume / 2) * 100) / 100);
          tradeItems.push({
            label: `Close ${posUnder.type_label.toUpperCase()} ${posUnder.volume}`,
            icon: "running" as MenuGlyphName,
            danger: true,
            action: () =>
              void runQuickAction(
                () => api.tradingCloseOrder({ ticket: t }),
                `Closed #${t}`,
              ),
          });
          if (posUnder.volume - half >= 0.01 - 1e-9) {
            tradeItems.push({
              label: `Close 50% (${half})`,
              icon: "running" as MenuGlyphName,
              action: () =>
                void runQuickAction(
                  () => api.tradingCloseOrder({ ticket: t, volume: half }),
                  `Closed ${half} of #${t}`,
                ),
            });
          }
          // A one-click market order has no SL/TP — let the user add them
          // here too (same flow as pending), then drag to finalize. The
          // default is relative to the CURRENT price (not entry): the
          // position is live and price has moved, so an entry-based level
          // could be on the wrong side of the market and reject.
          const pe = posUnder.price_current || posUnder.price_open;
          const pBuy = posUnder.type === 0;
          const pDec = priceDecimals(pe);
          const pDefSl = Number(
            (pBuy ? pe - pe * 0.003 : pe + pe * 0.003).toFixed(pDec),
          );
          const pDefTp = Number(
            (pBuy ? pe + pe * 0.006 : pe - pe * 0.006).toFixed(pDec),
          );
          if (!(posUnder.sl > 0)) {
            tradeItems.push({
              label: "Add stop loss",
              icon: "sltp" as MenuGlyphName,
              action: () => openDraftField("pos", t, "sl", pDefSl),
            });
          }
          if (!(posUnder.tp > 0)) {
            tradeItems.push({
              label: "Add take profit",
              icon: "sltp" as MenuGlyphName,
              action: () => openDraftField("pos", t, "tp", pDefTp),
            });
          }
          tradeItems.push({ separator: true, label: "" });
        } else if (ordUnder) {
          const t = ordUnder.ticket;
          const e0 = ordUnder.price_open;
          const isBuy = ordUnder.type_label.toLowerCase().startsWith("buy");
          const dec = priceDecimals(e0);
          // Sensible default distances (~0.3% / 0.6%); the user then drags
          // the new line to finalize.
          const defSl = Number(
            (isBuy ? e0 - e0 * 0.003 : e0 + e0 * 0.003).toFixed(dec),
          );
          const defTp = Number(
            (isBuy ? e0 + e0 * 0.006 : e0 - e0 * 0.006).toFixed(dec),
          );
          if (!(ordUnder.sl > 0)) {
            tradeItems.push({
              label: "Add stop loss",
              icon: "sltp" as MenuGlyphName,
              action: () => openDraftField("order", t, "sl", defSl),
            });
          }
          if (!(ordUnder.tp > 0)) {
            tradeItems.push({
              label: "Add take profit",
              icon: "sltp" as MenuGlyphName,
              action: () => openDraftField("order", t, "tp", defTp),
            });
          }
          tradeItems.push({
            label: "Cancel order",
            icon: "pending" as MenuGlyphName,
            danger: true,
            action: () =>
              void runQuickAction(
                () => api.tradingCancelOrder({ ticket: t }),
                `Cancelled #${t}`,
              ),
          });
          tradeItems.push({ separator: true, label: "" });
        }
      }

      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        items: [
          ...tradeItems,
          {
            label: "Jump to latest candle",
            icon: "jump" as MenuGlyphName,
            action: () => {
              const chart = chartRef.current;
              const series = seriesRef.current;
              const realCount = candleCountRef.current;
              if (!chart || !series || realCount === 0) return;
              const visibleCount = Math.min(realCount, INITIAL_VISIBLE_BARS);
              const visibleStart = realCount - visibleCount - 0.5;
              const visibleEnd = realCount - 0.5 + visibleCount * 0.2;
              chart
                .timeScale()
                .setVisibleLogicalRange({ from: visibleStart, to: visibleEnd });
              // Also refit the price axis to the newly-visible
              // candles so the view recentres on the current price.
              // Without this the price axis keeps its previous (often
              // far-away) range and the candles look squished into a
              // tiny vertical band. Same toggle pattern as the
              // fresh-load fitContent: clear any stale priceRange
              // provider, flip autoScale on to recompute, then let
              // the visibility-gated lock effect snap autoScale back
              // off — but here we're outside that effect, so do a
              // single-rAF lock locally so the user can still drag
              // the price axis afterwards.
              series.applyOptions({
                autoscaleInfoProvider: (base: () => unknown) => base() as never,
              });
              const priceScale = chart.priceScale("right");
              priceScale.applyOptions({ autoScale: true });
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  priceScale.applyOptions({ autoScale: false });
                });
              });
            },
          },
          // --- Trading submenu — visible with trading.read; the order-
          // placement items below need trading.write. ---
          ...(canTradeReadRef.current
            ? [
                { separator: true, label: "" },
                {
                  label: "Trading",
                  icon: "trading" as MenuGlyphName,
                  children: [
                    {
                      // Open the Trading panel modal (positions / orders /
                      // history) — same modal the header chip opens.
                      label: "Trading panel…",
                      icon: "panel" as MenuGlyphName,
                      action: () => dispatch(setTradingPanelOpen(true)),
                    },
                    // Order-placement items — trading.write only.
                    ...(canTradeWriteRef.current
                      ? [
                    { separator: true, label: "" },
                    // Pending orders at the clicked price — opens a draft
                    // (adjust volume + expiry, drag the line) that only
                    // places on Submit. limit vs stop auto-derived from
                    // cursor price vs live market.
                    ...(ccPrice != null
                      ? [
                          {
                            label: `Buy ${buyKind} ${vol} @ ${ccPrice.toFixed(2)}`,
                            icon: "pending" as MenuGlyphName,
                            action: () =>
                              setPendingDraft({
                                side: "buy",
                                kind: buyKind,
                                price: ccPrice,
                                volume: tradeVolumeRef.current,
                                expiry: "gtc",
                                expireAt: "",
                              }),
                          },
                          {
                            label: `Sell ${sellKind} ${vol} @ ${ccPrice.toFixed(2)}`,
                            icon: "pending" as MenuGlyphName,
                            action: () =>
                              setPendingDraft({
                                side: "sell",
                                kind: sellKind,
                                price: ccPrice,
                                volume: tradeVolumeRef.current,
                                expiry: "gtc",
                                expireAt: "",
                              }),
                          },
                        ]
                      : []),
                    // Panic "close all" — market-closes every open position
                    // (across symbols). Only shown when something is open.
                    ...(tradingPositionsRef.current.length > 0
                      ? [
                          { separator: true, label: "" },
                          {
                            label: `Close all positions (${tradingPositionsRef.current.length})`,
                            icon: "running" as MenuGlyphName,
                            danger: true,
                            action: () => void closeAllPositions(),
                          },
                        ]
                      : []),
                    { separator: true, label: "" },
                    {
                      // Show/hide the docked one-click panel — last item.
                      label: oneClickPanelOpenRef.current
                        ? "✓ One-click trading"
                        : "One-click trading",
                      icon: "oneclick" as MenuGlyphName,
                      action: () => dispatch(toggleOneClickPanel()),
                    },
                        ]
                      : []),
                  ],
                },
                {
                  // --- View submenu — overlay layer toggles. Labels show
                  // ✓ when visible so the menu doubles as status. ---
                  label: "View",
                  icon: "view" as MenuGlyphName,
                  children: [
                    {
                      label: showTradingTradesRef.current
                        ? "✓ Show running trades"
                        : "Show running trades",
                      icon: "running" as MenuGlyphName,
                      action: () =>
                        dispatch(
                          setShowTradingTrades({
                            tabId,
                            show: !showTradingTradesRef.current,
                          }),
                        ),
                    },
                    {
                      // SL/TP only meaningful when trades are shown.
                      label: showTradingSlTpRef.current
                        ? "✓ Show SL / TP"
                        : "Show SL / TP",
                      icon: "sltp" as MenuGlyphName,
                      action: () =>
                        dispatch(
                          setShowTradingSlTp({
                            tabId,
                            show: !showTradingSlTpRef.current,
                          }),
                        ),
                      disabled: !showTradingTradesRef.current,
                    },
                    {
                      label: showHistoryOrdersRef.current
                        ? "✓ Show history orders"
                        : "Show history orders",
                      icon: "history" as MenuGlyphName,
                      action: () =>
                        dispatch(
                          setShowHistoryOrders({
                            tabId,
                            show: !showHistoryOrdersRef.current,
                          }),
                        ),
                    },
                  ],
                },
              ]
            : []),
          { separator: true, label: "" },
          {
            // --- Image submenu — chart capture. ---
            label: "Image",
            icon: "image" as MenuGlyphName,
            children: [
              {
                label: "Copy chart image",
                icon: "copy" as MenuGlyphName,
                action: () => {
                  void copyToClipboard();
                },
              },
              {
                label: "Save chart as image…",
                icon: "save" as MenuGlyphName,
                action: () => {
                  void saveAsImage();
                },
              },
            ],
          },
          { separator: true, label: "" },
          {
            // Full theme editor (preset picker + color pickers + grid).
            label: "Properties…",
            icon: "properties" as MenuGlyphName,
            action: () => {
              dispatch(setChartColorsDialogOpen(true));
            },
          },
        ],
      });
    };
    ctxTarget.addEventListener("contextmenu", onContextMenu);
    return () => ctxTarget.removeEventListener("contextmenu", onContextMenu);
  }, [symbol]);

  // Track cursor position over the chart so we can preview an
  // in-progress drawing (fib or position — either between two clicks,
  // or during a press-drag before release).
  const [hoverPoint, setHoverPoint] = useState<
    { time: number; price: number } | null
  >(null);
  useEffect(() => {
    const wantsHover =
      activeDrawingTool === "fibonacci" ||
      activeDrawingTool === "diagonal" ||
      activeDrawingTool === "path" ||
      activeDrawingTool === "long" ||
      activeDrawingTool === "short" ||
      activeDrawingTool === "priceRange";
    if (!wantsHover) {
      setHoverPoint(null);
      return;
    }
    const container = containerRef.current;
    if (!container) return;

    // DOM mousemove rather than chart.subscribeCrosshairMove — same
    // reason as the click handler above (crosshairMove only fires within
    // the data range, so the preview wouldn't track into the empty
    // right-extension).
    const onMove = (e: MouseEvent) => {
      const chart = chartRef.current;
      const series = seriesRef.current;
      if (!chart || !series) return;
      const rect = container.getBoundingClientRect();
      const yCoord = e.clientY - rect.top;
      let price = series.coordinateToPrice(yCoord) as number | null;
      if (price != null) {
        const y2 = yCoord < rect.height * 0.5 ? yCoord + 50 : yCoord - 50;
        const p2 = series.coordinateToPrice(y2);
        if (p2 != null) {
          const slope = (p2 - price) / (y2 - yCoord);
          priceScaleCalibRef.current = { slope, intercept: price - slope * yCoord };
        }
      } else {
        const calib = priceScaleCalibRef.current;
        if (calib) price = calib.slope * yCoord + calib.intercept;
      }
      if (price == null) return;
      const x = e.clientX - rect.left;
      // Logical axis → time, same as pointAt, so the preview crosshair sits
      // exactly where the committed anchor will render — including in the
      // empty future area right after a session open (issue #109).
      const psWidth = chart.priceScale("right").width();
      if (x > rect.width - psWidth) return;
      const logical = chart.timeScale().coordinateToLogical(x);
      if (logical == null) return;
      const time = logicalToTime(logical as number, candlesRef.current);
      if (time == null) return;
      setHoverPoint({ time, price });
    };
    const onLeave = () => setHoverPoint(null);

    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseleave", onLeave);
    return () => {
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseleave", onLeave);
    };
  }, [activeDrawingTool]);

  // --- Replay start picker ---
  //
  // Armed by the Replay toolbar button. The chart stays live and untruncated
  // while a vertical line tracks the cursor, SNAPPED to the bar under it, and
  // the next click starts the replay there (TradingView's flow).
  //
  // The snap is what makes it usable: the line has to sit on the bar you will
  // actually get, not wherever the pixel was, and both directions of the map
  // go through the LOGICAL axis so the line lands where the bar renders even
  // in the post-session-open case (issue #109).
  const [replayPick, setReplayPick] = useState<{ x: number; time: number } | null>(
    null,
  );
  useEffect(() => {
    if (!replaySelecting) {
      setReplayPick(null);
      return;
    }
    const container = containerRef.current;
    if (!container) return;

    const barAt = (clientX: number): { x: number; time: number } | null => {
      const chart = chartRef.current;
      if (!chart) return null;
      const rect = container.getBoundingClientRect();
      const x = clientX - rect.left;
      const psWidth = chart.priceScale("right").width();
      if (x < 0 || x > rect.width - psWidth) return null;
      const ts = chart.timeScale();
      const logical = ts.coordinateToLogical(x);
      if (logical == null) return null;
      const raw = logicalToTime(logical as number, candlesRef.current);
      if (raw == null) return null;
      const snapped = clampToBar(candlesRef.current, raw);
      if (snapped == null) return null;
      // Map the snapped bar back to a coordinate so the line sits ON the bar.
      const snappedX = ts.logicalToCoordinate(
        timeToLogical(snapped, candlesRef.current) as Logical,
      );
      return { x: snappedX == null ? x : (snappedX as number), time: snapped };
    };

    const onMove = (e: MouseEvent) => setReplayPick(barAt(e.clientX));
    const onLeave = () => setReplayPick(null);

    // A pan ends in a click too, so require the press and release to land in
    // roughly the same place before treating it as "pick this bar" — the same
    // threshold the drawing tools use to tell a click from a drag-place.
    // Without it, dragging the chart sideways to find the moment you want
    // starts the replay wherever you let go.
    let downX: number | null = null;
    let downY = 0;
    const onDown = (e: MouseEvent) => {
      downX = e.clientX;
      downY = e.clientY;
    };
    const onClick = (e: MouseEvent) => {
      const travelled =
        downX == null
          ? Infinity
          : Math.hypot(e.clientX - downX, e.clientY - downY);
      downX = null;
      if (travelled > FIB_DRAG_THRESHOLD_PX) return;
      const hit = barAt(e.clientX);
      if (!hit) return;
      e.preventDefault();
      e.stopPropagation();
      dispatch(enterReplay({ tabId, cursorTime: hit.time, entry: "picker" }));
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(cancelReplaySelect({ tabId }));
    };

    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseleave", onLeave);
    container.addEventListener("mousedown", onDown, true);
    // Capture phase: start the replay before any drawing-tool click handler
    // downstream can treat this as a placement.
    container.addEventListener("click", onClick, true);
    window.addEventListener("keydown", onKey);
    return () => {
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseleave", onLeave);
      container.removeEventListener("mousedown", onDown, true);
      container.removeEventListener("click", onClick, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [replaySelecting, dispatch, tabId]);

  // Drag-to-rearrange: while dragging, document mousemove updates the
  // anchored object live in Redux; mouseup clears the drag.
  //
  // Position drag targets:
  //   - "entry"/"sl"/"tp"  → vertical drag of the corresponding price line
  //   - "right" / "left"   → horizontal drag of the right / left edge
  //   - "tp-left"/"tp-right"/"sl-left"/"sl-right" → DIAGONAL corner
  //     handles, drag both the price line AND the time edge at once.
  // Draft SL/TP: draggable PREVIEW lines that only commit to the broker on
  // Submit (MT5-phone style). null = no draft.
  //
  // ONE draft carries BOTH levels because the common case — a one-click
  // market order, which has neither — needs both set, and making the user
  // place them in two separate round trips (each its own broker modify)
  // was the friction this replaced. Either side may be null: a trade
  // missing only a TP drafts only a TP.
  const [draftSlTp, setDraftSlTp] = useState<{
    scope: "pos" | "order";
    ticket: number;
    sl: number | null;
    tp: number | null;
    // Which levels the user has actually COMMITTED to, as opposed to the
    // proposals we seeded. Only these are submitted.
    //
    // Clicking a trade with neither level set used to seed both at ~0.3% /
    // 0.6% and send both, so a trader who wanted only a stop silently got a
    // take-profit they never chose, at a price we invented. A level counts as
    // chosen once it is dragged, or when it is asked for by name from the
    // right-click menu ("Add stop loss").
    touched: { sl: boolean; tp: boolean };
  } | null>(null);
  const [draftSubmitting, setDraftSubmitting] = useState(false);
  // Seed one level of a draft, MERGING into an open draft for the same
  // trade so "Add stop loss" then "Add take profit" builds one submission
  // rather than replacing the first line with the second.
  const openDraftField = (
    scope: "pos" | "order",
    ticket: number,
    kind: "sl" | "tp",
    price: number,
  ) =>
    setDraftSlTp((d) =>
      d && d.scope === scope && d.ticket === ticket
        ? { ...d, [kind]: price, touched: { ...d.touched, [kind]: true } }
        : {
            scope,
            ticket,
            sl: null,
            tp: null,
            [kind]: price,
            // Asked for by name, so it is a choice, not a proposal — it
            // submits even if the user never drags it.
            touched: { sl: kind === "sl", tp: kind === "tp" },
          },
    );
  // What a draft level is worth in money, for an OPEN position.
  //
  // P&L is linear in price, so MT5's own live figure can be rescaled instead
  // of us reimplementing contract sizes and quote-currency conversion on the
  // client (which we would get wrong for exactly the symbols that matter —
  // gold, indices, JPY crosses). `profit` is the value at `price_current`, so
  // the value at `price` is that scaled by the distance ratio from entry.
  // This is the same trick the existing SL/TP drag already uses.
  //
  // Returns null rather than a guess when it cannot be derived honestly:
  // pending orders have no live P&L at all, and a position sitting exactly
  // at its entry gives no ratio to scale by.
  const draftPnl = (
    scope: "pos" | "order",
    ticket: number,
    price: number,
  ): number | null => {
    if (scope !== "pos") return null;
    const p = tradingPositions.find((x) => x.ticket === ticket);
    if (!p || p.profit == null) return null;
    const denom = p.price_current - p.price_open;
    if (!(Math.abs(denom) > 1e-9)) return null;
    return (p.profit * (price - p.price_open)) / denom;
  };

  // Starting levels for a draft: ~0.3% / 0.6% off the reference price, a
  // 1:2 R:R the user then drags to where they actually want it.
  const draftLevels = (ref: number, isBuy: boolean) => {
    const dec = priceDecimals(ref);
    return {
      sl: Number((isBuy ? ref - ref * 0.003 : ref + ref * 0.003).toFixed(dec)),
      tp: Number((isBuy ? ref + ref * 0.006 : ref - ref * 0.006).toFixed(dec)),
    };
  };
  // Left-clicking a live trade's line opens its manage bar: draft lines for
  // whichever of SL / TP is missing, plus the actions that used to be
  // right-click-only — close/cancel the trade, and remove a level it already
  // has. The right-click menu is not discoverable for a new trader, and
  // "cancel this order" is not something anyone should have to hunt for.
  //
  // Opens even when the trade has BOTH levels: there is nothing to draft
  // then, but there is still a trade to cancel and levels to remove.
  const openSlTpDraft = (scope: "pos" | "order", ticket: number): boolean => {
    if (!canTradeWriteRef.current) return false;
    const trade =
      scope === "pos"
        ? tradingPositionsRef.current.find((x) => x.ticket === ticket)
        : tradingOrdersRef.current.find((x) => x.ticket === ticket);
    if (!trade) return false;
    const needSl = !(trade.sl > 0);
    const needTp = !(trade.tp > 0);
    const isBuy =
      scope === "pos"
        ? (trade as TradingPosition).type === 0
        : trade.type_label.toLowerCase().startsWith("buy");
    // For an OPEN position the reference is the CURRENT price, not entry:
    // it is live and price has moved, so an entry-based level can land on
    // the wrong side of the market and be rejected outright.
    const ref =
      scope === "pos"
        ? (trade as TradingPosition).price_current || trade.price_open
        : trade.price_open;
    const lv = draftLevels(ref, isBuy);
    setDraftSlTp({
      scope,
      ticket,
      sl: needSl ? lv.sl : null,
      tp: needTp ? lv.tp : null,
      // Seeded, not chosen. Both lines are visible to be dragged, but a level
      // the user leaves alone is never sent.
      touched: { sl: false, tp: false },
    });
    return true;
  };
  // Draft pending order: "Buy/Sell limit/stop here" opens a draggable
  // preview where the volume + expiry can be set before Submit.
  const [pendingDraft, setPendingDraft] = useState<{
    side: "buy" | "sell";
    kind: "limit" | "stop";
    price: number;
    volume: number;
    // Time-in-force, mirroring the desktop terminal.
    expiry: "gtc" | "day" | "specified_day" | "specified";
    // Local wall-clock for specified/specified_day. <input type="date">
    // value ("YYYY-MM-DD") or datetime-local ("YYYY-MM-DDTHH:mm").
    expireAt: string;
  } | null>(null);
  const [pendingSubmitting, setPendingSubmitting] = useState(false);

  type SelectedDrawingKind =
    | "fib"
    | "hline"
    | "vline"
    | "diagonal"
    | "path"
    | "position"
    | "text"
    | "priceRange";
  const dragRef = useRef<DragTarget | null>(null);
  const lockedPanRef = useRef<{ lastX: number } | null>(null);

  // Thicken (or restore) a whole group's entry/SL/TP lines.
  const applyGroupHL = (
    scope: "pos" | "order",
    ticket: number,
    on: boolean,
  ) => {
    const map = tradingLineByKeyRef.current;
    for (const k of tradingGroupKeys(scope, ticket)) {
      const e = map.get(k);
      if (!e) continue;
      try {
        e.line.applyOptions({
          lineWidth: clampPriceLineWidth(on ? e.baseWidth + 1 : e.baseWidth),
        });
      } catch {
        // line removed underneath us — ignore
      }
    }
  };
  // Set (or clear) the highlighted group, restoring the previous one.
  const setHighlightGroup = (
    g: { scope: "pos" | "order"; ticket: number } | null,
  ) => {
    const cur = hlGroupRef.current;
    if (
      cur &&
      (!g || cur.ticket !== g.ticket || cur.scope !== g.scope)
    ) {
      applyGroupHL(cur.scope, cur.ticket, false);
    }
    hlGroupRef.current = g;
    if (g) applyGroupHL(g.scope, g.ticket, true);
  };

  // Close the trade manage bar on a click that has nothing to do with it.
  //
  // It used to sit there until Dismiss was pressed, so clicking away on the
  // chart left a bar hanging over a trade the user had moved on from — and,
  // worse, one still wired to that ticket's Close button.
  //
  // Kept OUT of the effect below (which clears the line highlight) because
  // this one has more exceptions: the bar's own controls, every trading line
  // strip — including the draft lines being dragged, which carry the same
  // data attribute — and the context menu, whose "Add stop loss" row builds
  // on the open draft and would otherwise land on one we had just discarded.
  //
  // A ref, not `draftSlTp` in the deps: dragging a draft line rewrites that
  // state on every mousemove, and re-subscribing a window listener per frame
  // to learn something this cheap is not worth it.
  const draftOpenRef = useRef(false);
  useEffect(() => {
    draftOpenRef.current = draftSlTp != null;
  }, [draftSlTp]);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!draftOpenRef.current) return;
      const t = e.target as Element | null;
      if (t && typeof t.closest === "function") {
        if (
          t.closest("[data-draft-bar]") ||
          t.closest("[data-trading-strip]") ||
          t.closest("[data-chart-menu]")
        )
          return;
      }
      setDraftSlTp(null);
    };
    window.addEventListener("mousedown", onDown, true);
    return () => window.removeEventListener("mousedown", onDown, true);
  }, []);

  // Lose the group highlight on any interaction that isn't clicking a
  // trading line: a click anywhere else (capture phase, so it fires even
  // before the line's own handler), or Escape.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!hlGroupRef.current) return;
      const t = e.target as Element | null;
      if (t && typeof t.closest === "function" && t.closest("[data-trading-strip]"))
        return;
      setHighlightGroup(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && hlGroupRef.current) setHighlightGroup(null);
    };
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      // Locked-drawing pan: user dragged on a locked drawing — move the chart
      // instead of moving the drawing. Runs before the drag-drawing path.
      const pan = lockedPanRef.current;
      if (pan) {
        const chart = chartRef.current;
        const container = containerRef.current;
        if (chart && container) {
          const range = chart.timeScale().getVisibleLogicalRange();
          if (range) {
            const barsPerPx = (range.to - range.from) / container.clientWidth;
            const dx = e.clientX - pan.lastX;
            // scrollToPosition with animated=false calls _internal_setRightOffset
            // synchronously, so dragLoop reads fresh timeToCoordinate values in
            // the same frame — eliminates SVG lag during locked-drawing pan.
            chart.timeScale().scrollToPosition(
              chart.timeScale().scrollPosition() - dx * barsPerPx,
              false,
            );
          }
          pan.lastX = e.clientX;
        }
        return;
      }

      const drag = dragRef.current;
      const chart = chartRef.current;
      const series = seriesRef.current;
      const container = containerRef.current;
      if (!drag || !chart || !series || !container) return;

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      // Endpoint drags read time off the logical axis (exact inverse of the
      // renderer's timeToLogical), NOT coordinateToTime. With coordinateToTime
      // the whitespace grid (nominal tf) and the renderer's future
      // extrapolation (last bar spacing) disagree right after a session open,
      // so an endpoint dragged into the empty right-hand area crawls at
      // ~tf/gap of the cursor speed — on every timeframe (issue #109).
      const logicalX = chart.timeScale().coordinateToLogical(x) as number | null;
      const time =
        logicalX != null ? logicalToTime(logicalX, candlesRef.current) : null;
      let price = series.coordinateToPrice(y) as number | null;
      if (price == null) {
        const calib = priceScaleCalibRef.current;
        if (calib) price = calib.slope * y + calib.intercept;
      }

      if (drag.type === "draft") {
        if (price == null) return;
        // `field` set = one line of the draft SL/TP pair; absent = the
        // draft pending-order entry.
        if (drag.field) {
          const f = drag.field;
          const px = price;
          // Dragging commits the level: it is now the user's number, not our
          // seeded proposal, so Submit will include it.
          setDraftSlTp((d) =>
            d ? { ...d, [f]: px, touched: { ...d.touched, [f]: true } } : d,
          );
          return;
        }
        setPendingDraft((d) => {
          if (!d) return d;
          // Auto limit/stop from price vs live market, like the menu.
          const bidAsk = liveBidAskRef.current;
          let kind = d.kind;
          if (bidAsk) {
            kind =
              d.side === "buy"
                ? price <= bidAsk.ask
                  ? "limit"
                  : "stop"
                : price >= bidAsk.bid
                  ? "limit"
                  : "stop";
          }
          return { ...d, price, kind };
        });
        return;
      }

      if (drag.type === "trading") {
        if (price == null) return;
        tradingMovedRef.current = true;
        const map = tradingLineByKeyRef.current;
        if (drag.kind === "order-entry") {
          // Entry drag carries the SL/TP along by the same delta.
          const dp = price - drag.initial.entry;
          const newSl = drag.initial.sl > 0 ? drag.initial.sl + dp : 0;
          const newTp = drag.initial.tp > 0 ? drag.initial.tp + dp : 0;
          map.get(`order-entry:${drag.ticket}`)?.line.applyOptions({ price });
          if (newSl > 0)
            map
              .get(`order-sl:${drag.ticket}`)
              ?.line.applyOptions({ price: newSl });
          if (newTp > 0)
            map
              .get(`order-tp:${drag.ticket}`)
              ?.line.applyOptions({ price: newTp });
          drag.preview = { price, sl: newSl, tp: newTp };
        } else {
          // Direct SL/TP drag — live-update the P&L label too, scaling
          // MT5's exact value at the original price by the distance ratio
          // (P&L is linear in price): pnl = anchorPnl × (p−entry)/(anchor−entry).
          const denom = drag.anchorPrice - drag.initial.entry;
          const pnl =
            drag.anchorPnl != null && Math.abs(denom) > 1e-9
              ? (drag.anchorPnl * (price - drag.initial.entry)) / denom
              : null;
          const label = drag.kind.endsWith("tp") ? "TP" : "SL";
          map.get(`${drag.kind}:${drag.ticket}`)?.line.applyOptions({
            price,
            title: pnl != null ? `${label} ${fmtPnl(pnl)}` : label,
          });
          drag.preview = { price, sl: 0, tp: 0 };
        }
        return;
      }

      if (drag.type === "fib") {
        const partial =
          drag.anchor === "body"
            ? bodyDragPartial(
                drag.initial,
                logicalX,
                price,
                candlesRef.current,
              )
            : fibDragPartial(drag, time as number | null, price);
        if (partial) dispatch(updateFib({ tabId, id: drag.id, ...partial }));
      } else if (drag.type === "diagonal") {
        const partial = diagonalDragPartial(drag, time as number | null, price);
        if (partial) dispatch(updateDiagonalLine({ tabId, id: drag.id, ...partial }));
      } else if (drag.type === "path") {
        if (time != null && price != null) {
          dispatch(
            updatePathVertex({
              tabId,
              id: drag.id,
              index: drag.vertexIndex,
              time: time as number,
              price,
            }),
          );
        }
      } else if (drag.type === "hline") {
        if (price != null)
          dispatch(updateHorizontalLine({ tabId, id: drag.id, price }));
      } else if (drag.type === "vline") {
        if (time != null)
          dispatch(
            updateVerticalLine({ tabId, id: drag.id, time: time as number }),
          );
      } else if (drag.type === "position") {
        const partial =
          drag.anchor === "body"
            ? positionBodyDragPartial(
                drag,
                logicalX,
                price,
                candlesRef.current,
              )
            : positionDragPartial(drag, time as number | null, price);
        if (partial) dispatch(updatePosition({ tabId, id: drag.id, ...partial }));
      } else if (drag.type === "priceRange") {
        if (drag.anchor === "midline") {
          // Map the cursor price → a fraction of the box's price range, so the
          // midline sits at any % (the wave's pullback level).
          const denom = drag.endPrice - drag.startPrice;
          if (price != null && denom !== 0) {
            const frac = Math.max(
              0,
              Math.min(1, (price - drag.startPrice) / denom),
            );
            dispatch(
              updatePriceRangeBox({ tabId, id: drag.id, midlineFraction: frac }),
            );
          }
        } else if (drag.anchor === "arrow") {
          // Map the cursor time → a fraction of the box's time span, so the
          // centre arrow slides left/right out of the way of the candles.
          const denom = drag.endTime - drag.startTime;
          if (time != null && denom !== 0) {
            const frac = Math.max(
              0,
              Math.min(1, ((time as number) - drag.startTime) / denom),
            );
            dispatch(
              updatePriceRangeBox({ tabId, id: drag.id, arrowFraction: frac }),
            );
          }
        } else {
          const partial =
            drag.anchor === "body"
              ? bodyDragPartial(
                  drag.initial,
                  logicalX,
                  price,
                  candlesRef.current,
                )
              : priceRangeDragPartial(drag, time as number | null, price);
          if (partial)
            dispatch(updatePriceRangeBox({ tabId, id: drag.id, ...partial }));
        }
      } else if (drag.type === "text") {
        // Single-anchor body drag: translate by the cursor's logical-bar
        // delta (gap-safe via shiftTimeByLogical) + price delta, so the
        // label tracks the cursor from where it was grabbed.
        const logical = chart.timeScale().coordinateToLogical(x);
        const init = drag.initial;
        if (logical != null && price != null && init) {
          const dLogical = (logical as number) - init.anchorLogical;
          const newTime = shiftTimeByLogical(
            init.time,
            dLogical,
            candlesRef.current,
          );
          if (newTime != null) {
            dispatch(
              updateTextLabel({
                tabId,
                id: drag.id,
                time: newTime,
                price: init.price + (price - init.anchorPrice),
              }),
            );
          }
        } else if (logical != null && price != null) {
          // No snapshot (shouldn't happen) — fall back to snap-to-pointer.
          const newTime = logicalToTime(logical as number, candlesRef.current);
          if (newTime != null)
            dispatch(updateTextLabel({ tabId, id: drag.id, time: newTime, price }));
        }
      }
    };
    const onUp = () => {
      const drag = dragRef.current;
      if (drag && drag.type === "trading" && !tradingMovedRef.current) {
        // Bare click, no drag — don't fire a no-op modify. Treat it as
        // "manage this trade" and open its bar.
        //
        // From ANY of the trade's lines, not just the entry. This was once
        // entry-only, on the reasoning that a click on an SL/TP line is a
        // grab that happened not to move and a popup there would fight the
        // drag being started. That no longer holds: the bar is where a level
        // is now REMOVED and where the trade is closed, so the SL line is
        // exactly where someone reaches to manage a stop. Nothing is fought
        // either way — we are already inside the "did not move" branch, so a
        // real drag never reaches here.
        draggingTradingRef.current = false;
        openSlTpDraft(
          drag.kind.startsWith("pos-") ? "pos" : "order",
          drag.ticket,
        );
      } else if (drag && drag.type === "trading") {
        // Commit the dragged price to the broker, then unfreeze the
        // rebuild so the next SSE tick reconciles to the real values.
        const { ticket, kind, preview } = drag;
        void (async () => {
          try {
            const res =
              kind === "order-entry"
                ? await api.tradingModifyPending({
                    ticket,
                    price: preview.price,
                    sl: preview.sl > 0 ? preview.sl : undefined,
                    tp: preview.tp > 0 ? preview.tp : undefined,
                  })
                : kind === "order-sl"
                  ? await api.tradingModifyPending({ ticket, sl: preview.price })
                  : kind === "order-tp"
                    ? await api.tradingModifyPending({ ticket, tp: preview.price })
                    : kind === "pos-sl"
                      ? await api.tradingModifyOrder({ ticket, sl: preview.price })
                      : await api.tradingModifyOrder({ ticket, tp: preview.price });
            dispatch(
              res.ok
                ? pushToast({ kind: "success", title: `Updated #${ticket}` })
                : pushToast({
                    kind: "error",
                    title: "Update failed",
                    message: res.human_msg,
                  }),
            );
          } catch (err) {
            dispatch(
              pushToast({
                kind: "error",
                title: "Update failed",
                message: err instanceof ApiError ? err.message : "Network error",
              }),
            );
          } finally {
            draggingTradingRef.current = false;
          }
        })();
      }
      // Highlight stays on the clicked group (click-to-select); it only
      // changes when another group is clicked.
      dragRef.current = null;
      lockedPanRef.current = null;
      // Re-enable chart pan after any drag concludes.
      setIsDragging(false);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dispatch, tabId]);

  const startLockedPan = (nativeEvent: globalThis.MouseEvent) => {
    lockedPanRef.current = { lastX: nativeEvent.clientX };
    // The SVG mousedown bubbles natively to the container, triggering
    // startDragLoop automatically — no synthetic dispatch needed here.
  };

  const startDrag =
    (target: Exclude<DragTarget, { type: "trading" } | { type: "draft" }>) =>
    (e: React.MouseEvent) => {
    // Left button only — right-click is reserved for the context menu.
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    // Text is a single-anchor body drag: snapshot the anchor + mousedown
    // cursor (logical + price), mirroring startBodyDrag, so the label tracks
    // the cursor instead of snapping its anchor to the pointer.
    let resolved = target;
    if (resolved.type === "text") {
      const chart = chartRef.current;
      const series = seriesRef.current;
      const container = containerRef.current;
      const lbl = textLabels.find((l) => l.id === resolved.id);
      if (chart && series && container && lbl) {
        const rect = container.getBoundingClientRect();
        const anchorLogical = chart
          .timeScale()
          .coordinateToLogical(e.clientX - rect.left);
        const anchorPrice = series.coordinateToPrice(e.clientY - rect.top);
        if (anchorLogical != null && anchorPrice != null) {
          resolved = {
            ...resolved,
            initial: {
              time: lbl.time,
              price: lbl.price,
              anchorLogical: anchorLogical as number,
              anchorPrice,
            },
          };
        }
      }
    }
    dragRef.current = resolved;
    setIsDragging(true);
    // Grabbing a handle also selects the drawing so a keyboard
    // Delete/Backspace can remove it without an extra click.
    const kind: SelectedDrawingKind =
      target.type === "fib"
        ? "fib"
        : target.type === "hline"
          ? "hline"
          : target.type === "vline"
            ? "vline"
            : target.type === "diagonal"
              ? "diagonal"
              : target.type === "path"
                ? "path"
                : target.type === "text"
                  ? "text"
                  : target.type === "priceRange"
                    ? "priceRange"
                    : "position";
    dispatch(
      setSelectedDrawing({ tabId, selected: { kind, id: target.id } }),
    );
  };

  // Start dragging a live trading line (position SL/TP or pending
  // entry/SL/TP). Freezes the rebuild effect for the duration so the
  // native line follows the cursor without SSE snapping it back.
  const startTradingDrag =
    (target: Extract<DragTarget, { type: "trading" }>) =>
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      e.preventDefault();
      dragRef.current = target;
      draggingTradingRef.current = true;
      tradingMovedRef.current = false;
      setHighlightGroup({
        scope: target.kind.startsWith("pos") ? "pos" : "order",
        ticket: target.ticket,
      });
      setIsDragging(true);
    };

  // Invisible full-width grab strips at each draggable trading line's
  // price, rendered in the SVG overlay. Only in default (no drawing
  // tool) mode so they don't intercept drawing clicks. The visible line
  // is the native price line; these just provide the drag hit-area.
  // --- Draft SL/TP (preview that only commits on Submit) ---
  const startDraftDrag =
    (field?: "sl" | "tp") => (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      e.preventDefault();
      dragRef.current = { type: "draft", field };
      setIsDragging(true);
    };
  const cancelDraft = () => setDraftSlTp(null);
  // Clear a level the broker is already holding. MT5 treats 0 as "no level"
  // on both TRADE_ACTION_SLTP and TRADE_ACTION_MODIFY, and the API models
  // allow it (ge=0), so this is a plain modify — not a special verb.
  const removeDraftLevel = async (field: "sl" | "tp") => {
    const d = draftSlTp;
    if (!d || draftSubmitting) return;
    setDraftSubmitting(true);
    try {
      // Written out rather than computed: a computed key widens the body to
      // an index signature and loses the typed sl/tp contract.
      const body =
        field === "sl"
          ? { ticket: d.ticket, sl: 0 }
          : { ticket: d.ticket, tp: 0 };
      const res =
        d.scope === "pos"
          ? await api.tradingModifyOrder(body)
          : await api.tradingModifyPending(body);
      dispatch(
        res.ok
          ? pushToast({
              kind: "success",
              title: `${field.toUpperCase()} removed from #${d.ticket}`,
            })
          : pushToast({
              kind: "error",
              title: "Update failed",
              message: res.human_msg,
            }),
      );
    } catch (err) {
      dispatch(
        pushToast({
          kind: "error",
          title: "Update failed",
          message: err instanceof ApiError ? err.message : "Network error",
        }),
      );
    } finally {
      setDraftSubmitting(false);
    }
  };
  // Close a running position / cancel a pending order straight from the bar.
  // Dismiss the bar first: the trade is about to stop existing, and leaving
  // its manage bar on screen implies it is still there.
  const closeDraftTrade = () => {
    const d = draftSlTp;
    if (!d) return;
    setDraftSlTp(null);
    void runQuickAction(
      d.scope === "pos"
        ? () => api.tradingCloseOrder({ ticket: d.ticket })
        : () => api.tradingCancelOrder({ ticket: d.ticket }),
      d.scope === "pos" ? `Closed #${d.ticket}` : `Cancelled #${d.ticket}`,
    );
  };
  const submitDraft = async () => {
    const d = draftSlTp;
    if (!d || draftSubmitting) return;
    // ONLY the levels the user actually chose. A seeded line they ignored is
    // a suggestion on screen, never an order to the broker.
    const sl = d.touched.sl ? d.sl : null;
    const tp = d.touched.tp ? d.tp : null;
    if (sl == null && tp == null) return;
    setDraftSubmitting(true);
    try {
      // Both levels go in ONE modify — two calls would leave the trade in a
      // half-applied state if the second were rejected.
      const body = {
        ticket: d.ticket,
        ...(sl != null ? { sl } : {}),
        ...(tp != null ? { tp } : {}),
      };
      const res =
        d.scope === "pos"
          ? await api.tradingModifyOrder(body)
          : await api.tradingModifyPending(body);
      const what =
        sl != null && tp != null ? "SL/TP" : sl != null ? "SL" : "TP";
      dispatch(
        res.ok
          ? pushToast({
              kind: "success",
              title: `${what} set on #${d.ticket}`,
            })
          : pushToast({
              kind: "error",
              title: "Update failed",
              message: res.human_msg,
            }),
      );
      if (res.ok) setDraftSlTp(null);
    } catch (err) {
      dispatch(
        pushToast({
          kind: "error",
          title: "Update failed",
          message: err instanceof ApiError ? err.message : "Network error",
        }),
      );
    } finally {
      setDraftSubmitting(false);
    }
  };
  // Esc cancels the draft.
  useEffect(() => {
    if (!draftSlTp) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDraftSlTp(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [draftSlTp]);

  // Draggable dashed preview line for the active draft SL/TP.
  // --- Draft PENDING order (set volume + expiry, then Submit) ---
  const cancelPendingDraft = () => setPendingDraft(null);
  // Set the draft volume AND mirror it to the remembered trade volume
  // (ui.tradeVolume, persisted) so the next pending order / one-click trade
  // seeds from the same lot — no re-adjusting every time. Updates the draft for
  // any finite value (so the field can be cleared mid-edit) but only remembers a
  // positive lot.
  const commitDraftVolume = (v: number) => {
    if (!isFinite(v)) return;
    setPendingDraft((d) => (d ? { ...d, volume: v } : d));
    if (v > 0) dispatch(setTradeVolume(v));
  };
  // Resolve the draft's time-in-force into the API's expire_type/expire_at.
  // For specified/specified_day the user picks a LOCAL wall-clock; we send
  // it as a UTC epoch and the backend shifts it onto the broker's clock.
  const resolveExpiry = (
    d: NonNullable<typeof pendingDraft>,
  ): { expire_type: PendingOrderBody["expire_type"]; expire_at?: number } | null => {
    if (d.expiry === "gtc") return { expire_type: "gtc" };
    if (d.expiry === "day") return { expire_type: "day" };
    if (!d.expireAt) return null; // specified modes need a date/time
    // specified_day uses <input type=date> → expire at end of that local day.
    const iso = d.expiry === "specified_day" ? `${d.expireAt}T23:59:59` : d.expireAt;
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return null;
    return { expire_type: d.expiry, expire_at: Math.floor(dt.getTime() / 1000) };
  };
  const submitPendingDraft = async () => {
    const d = pendingDraft;
    if (!d || pendingSubmitting || !(d.volume > 0)) return;
    const exp = resolveExpiry(d);
    if (!exp) {
      dispatch(
        pushToast({
          kind: "warning",
          title: "Pick an expiry date",
          message: "Choose the date/time the order should expire.",
        }),
      );
      return;
    }
    setPendingSubmitting(true);
    try {
      const outcome = await api.tradingPlacePending({
        symbol,
        side: d.side,
        kind: d.kind,
        volume: d.volume,
        price: d.price,
        expire_type: exp.expire_type,
        expire_at: exp.expire_at,
      });
      if (outcome.kind === "filled") {
        dispatch(
          pushToast({
            kind: "success",
            title: `${d.side === "buy" ? "Buy" : "Sell"} ${d.kind} ${d.volume} ${symbol} @ ${d.price} placed`,
          }),
        );
        setPendingDraft(null);
      } else if (outcome.kind === "rejected") {
        dispatch(
          pushToast({
            kind: "error",
            title: "Order rejected",
            message: outcome.result.human_msg,
          }),
        );
      } else {
        dispatch(
          pushToast({ kind: "warning", title: "Blocked", message: outcome.message }),
        );
      }
    } catch (e) {
      dispatch(
        pushToast({
          kind: "error",
          title: "Order failed",
          message: e instanceof ApiError ? e.message : "Network error",
        }),
      );
    } finally {
      setPendingSubmitting(false);
    }
  };
  // Esc cancels the pending draft.
  useEffect(() => {
    if (!pendingDraft) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPendingDraft(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingDraft]);

  // Dashed draggable preview line for the draft pending order.
  // Context menu shown on right-click of a drawing. Items are computed
  // per-drawing in the onContextMenu handler so each can offer its own
  // actions (currently just Delete, but architected for future
  // additions like colour/duplicate/bring-to-front).
  const [contextMenu, setContextMenu] = useState<
    { x: number; y: number; items: MenuItem[] } | null
  >(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Viewport-aware menu placement: flip above the cursor when it would
  // overflow the bottom, and clamp/flip left near the right edge so the
  // menu (and its right-flyout submenus) stay fully visible. Measured
  // after render in a layout effect (before paint → no flicker).
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    openLeft: boolean;
  } | null>(null);
  useLayoutEffect(() => {
    if (!contextMenu) {
      setMenuPos(null);
      return;
    }
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pad = 8;
    const SUBMENU_W = 224; // approximate flyout width
    let left = contextMenu.x;
    let top = contextMenu.y;
    if (left + rect.width > window.innerWidth - pad) {
      left = Math.max(pad, contextMenu.x - rect.width);
    }
    if (top + rect.height > window.innerHeight - pad) {
      top = Math.max(pad, contextMenu.y - rect.height);
    }
    const openLeft = left + rect.width + SUBMENU_W > window.innerWidth - pad;
    setMenuPos({ top, left, openLeft });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu]);
  // Which fib's per-drawing level-visibility editor is open (null = none).
  const [fibLevelsEditorId, setFibLevelsEditorId] = useState<string | null>(
    null,
  );

  const showMenu = (items: MenuItem[]) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, items });
  };

  // Render a menu item list, recursing into `children` as a hover
  // flyout submenu. Leaf clicks run the action + close the menu;
  // parent (submenu) rows just reveal their child list on hover.
  // Items shown when right-clicking a fib's handle. "Edit settings"
  // opens the shared levels dialog (same one the toolbar's right-click
  // → Edit settings opens) so the user can change levels / colors /
  // opacity right from the fib they were inspecting.
  const fibMenuItems = (fibId: string): MenuItem[] => [
    {
      label: "Edit settings",
      action: () => dispatch(setFibLevelsDialogOpen(true)),
    },
    {
      // Per-drawing level visibility — only affects this fib.
      label: "Levels…",
      action: () => setFibLevelsEditorId(fibId),
    },
    {
      label: "Delete",
      action: () => dispatch(removeFib({ tabId, id: fibId })),
      danger: true,
    },
  ];

  // Per-position right-click menu (from the tool's interaction module).
  const positionMenuItems = (posId: string): MenuItem[] =>
    posMenuItems(dispatch, tabId, posId);

  // Per-price-range right-click menu (from the tool's interaction module).
  const priceRangeMenuItems = (id: string): MenuItem[] =>
    prMenuItems(dispatch, tabId, id);

  // Close the menu when the user clicks anywhere else (or right-clicks
  // somewhere new). Listeners attach next tick so the click that opened
  // the menu doesn't immediately close it.
  useEffect(() => {
    if (!contextMenu) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    };
    const id = window.setTimeout(() => {
      document.addEventListener("click", close);
      document.addEventListener("contextmenu", close);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("click", close);
      document.removeEventListener("contextmenu", close);
    };
  }, [contextMenu]);

  // MA series — diff maLines array: remove orphan series, create missing ones,
  // update data. One effect handles both lifecycle and data so the series map
  // stays in sync with the candles ref in a single pass.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const maLinesKey = JSON.stringify(indicators?.maLines);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const lines = indicators?.maLines ?? [];
    const enabledIds = new Set(lines.filter((l) => l.enabled).map((l) => l.id));

    // Remove series that no longer exist or were disabled.
    for (const id of Object.keys(maSeriesRef.current)) {
      if (!enabledIds.has(id)) {
        chart.removeSeries(maSeriesRef.current[id]);
        delete maSeriesRef.current[id];
      }
    }

    // Create / update enabled lines.
    for (const line of lines) {
      if (!line.enabled) continue;
      if (!maSeriesRef.current[line.id]) {
        maSeriesRef.current[line.id] = chart.addLineSeries({
          priceScaleId: "right",
          color: line.color,
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: true,
          crosshairMarkerVisible: false,
        });
      }
      maSeriesRef.current[line.id].applyOptions({ color: line.color });
      if (candles.length > 0) {
        const data =
          line.type === "Anchored"
            ? computeAnchoredMA(candles, line.anchorDate ?? "", sessionBoundaries, line.endDate)
            : line.type === "EMA"
              ? computeEMA(candles, line.period)
              : computeSMA(candles, line.period);
        maSeriesRef.current[line.id].setData(data);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maLinesKey, candles, chartType, sessionBoundaries]);

  // RSI — TWO independent series in one bottom pane: the period RSI and the
  // anchor-time RSI. They share the pane + the 70/50/30 reference lines.
  // Series are recreated only when structural settings change (which is
  // enabled OR overbought/oversold, since the reference lines are baked at
  // creation); candles-only / color-only / anchorDate changes update in place.
  const anchorRsi = indicators?.anchorRsi;
  const rsiOb = indicators?.rsi.overbought ?? 70;
  const rsiOs = indicators?.rsi.oversold ?? 30;
  const rsiAnyEnabled = !!(indicators?.rsi.enabled || anchorRsi?.enabled);
  const rsiStructKey = `${indicators?.rsi.enabled}|${anchorRsi?.enabled}|${rsiOb}|${rsiOs}`;
  const rsiStructKeyRef = useRef(rsiStructKey);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const structChanged = rsiStructKey !== rsiStructKeyRef.current;
    rsiStructKeyRef.current = rsiStructKey;

    // Build a 0–100 RSI line series; `withLines` adds the shared OB/OS/50
    // reference lines (only one of the two series owns them, to avoid dupes).
    const makeRsiSeries = (color: string, withLines: boolean) => {
      const s = chart.addLineSeries({
        priceScaleId: "rsi",
        color,
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: true,
        crosshairMarkerVisible: false,
      });
      s.applyOptions({
        autoscaleInfoProvider: () => ({
          priceRange: { minValue: 0, maxValue: 100 },
        }),
      });
      if (withLines) {
        s.createPriceLine({ price: rsiOb, color: "rgba(239, 68, 68, 0.55)", lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: `${rsiOb}` });
        s.createPriceLine({ price: 50, color: "rgba(148, 163, 184, 0.35)", lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: false, title: "" });
        s.createPriceLine({ price: rsiOs, color: "rgba(34, 197, 94, 0.55)", lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: `${rsiOs}` });
      }
      return s;
    };

    // Structural change → rebuild both series from scratch.
    if (structChanged) {
      if (rsiSeriesRef.current) { chart.removeSeries(rsiSeriesRef.current); rsiSeriesRef.current = null; }
      if (anchorRsiSeriesRef.current) { chart.removeSeries(anchorRsiSeriesRef.current); anchorRsiSeriesRef.current = null; }
    }

    if (!rsiAnyEnabled) {
      // Restore the main chart's bottom margin when the whole strip is off.
      const saved = scaleMargins ?? { top: 0.1, bottom: 0.1 };
      chart.priceScale("right").applyOptions({ scaleMargins: saved });
      return;
    }

    // Configure the RSI pane + push the main chart up (idempotent).
    chart.priceScale("rsi").applyOptions({
      scaleMargins: { top: 0.8, bottom: 0.02 },
      visible: true,
      borderColor: "rgba(255,255,255,0.08)",
      autoScale: true,
    });
    const saved = scaleMargins ?? { top: 0.1, bottom: 0.1 };
    chart.priceScale("right").applyOptions({
      scaleMargins: { top: saved.top, bottom: Math.max(saved.bottom, 0.22) },
    });

    // Period RSI — owns the reference lines when it's present.
    if (indicators?.rsi.enabled) {
      if (!rsiSeriesRef.current) rsiSeriesRef.current = makeRsiSeries(indicators.rsi.color, true);
      else rsiSeriesRef.current.applyOptions({ color: indicators.rsi.color });
      if (candles.length > 0) rsiSeriesRef.current.setData(computeRSI(candles, indicators.rsi.period));
    }

    // Anchor-Time RSI — draws the reference lines itself only when the period
    // RSI isn't there to provide them.
    if (anchorRsi?.enabled) {
      if (!anchorRsiSeriesRef.current) {
        anchorRsiSeriesRef.current = makeRsiSeries(anchorRsi.color, !indicators?.rsi.enabled);
      } else {
        anchorRsiSeriesRef.current.applyOptions({ color: anchorRsi.color });
      }
      if (candles.length > 0) {
        anchorRsiSeriesRef.current.setData(
          computeAnchoredRSI(candles, anchorRsi.anchorDate ?? "", sessionBoundaries, anchorRsi.endDate),
        );
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rsiStructKey,
    candles,
    indicators?.rsi.period,
    indicators?.rsi.color,
    anchorRsi?.color,
    anchorRsi?.anchorDate,
    anchorRsi?.endDate,
    sessionBoundaries,
  ]);

  // Convert a time to an X coordinate. Cross-timeframe drawings store
  // fine-grained timestamps that don't match a bar exactly on coarser
  // TFs. lightweight-charts v4 has two relevant methods:
  //   - timeToCoordinate(t): returns null for non-bar times.
  //   - logicalToCoordinate(L): returns 0 for non-integer L (only works
  //     on whole bar indices).
  // So we interpolate ourselves: bracket the time between two integer
  // logical positions, get X for each, linear-interp between them.
  const timeToX = (time: number): number | null => {
    const chart = chartRef.current;
    if (!chart) return null;
    const logical = timeToLogical(time, candles);
    const lower = Math.floor(logical);
    const upper = lower + 1;
    const frac = logical - lower;
    const ts = chart.timeScale();
    const xLower = ts.logicalToCoordinate(lower as Logical);
    if (xLower == null) return null;
    if (frac === 0) return xLower;
    const xUpper = ts.logicalToCoordinate(upper as Logical);
    if (xUpper == null) return null;
    return xLower + (xUpper - xLower) * frac;
  };


  // Position renderer — mirrors TradingView's Forecasting tool. Two
  // colored zones (green TP / red SL) span the box's time range; three
  // horizontal lines mark entry/SL/TP; the right edge has a drag
  // handle, and each price line is independently draggable. The middle
  // R:R label aggregates the numbers a discretionary trader looks at
  // first.
  // Diagonal / trend line — same anchor logic as fib (two endpoints
  // in time/price space), rendered as a single segment + two drag
  // handles. A wider transparent stroke over the line catches clicks
  // for selection / right-click menu. Preview mode (during placement)
  // skips the interactive bits and renders semi-transparent.

  // Polyline path — N vertices with straight segments. Each vertex
  // is a draggable circle; each segment has an invisible hit-line
  // that inserts a new vertex on left-click. Preview mode (during
  // placement) renders semi-transparent with no interactivity.
  // Closed-trade marker: entry arrow at exact (entryTime, entryPrice),
  // exit arrow at exact (exitTime, exitPrice), and a thin dashed
  // P&L-colored line connecting them so the eye reads the trade as
  // one round-trip.
  //
  // Why custom SVG instead of lightweight-charts' setMarkers: native
  // markers only anchor to a bar (above/below/in), so on a 4H or 1D
  // bar an `inBar` marker lands at the wick's vertical midpoint —
  // can be tens of pips away from the actual fill price. SVG with
  // priceToCoordinate(price) is pixel-accurate.
  //
  // Arrow shape: filled triangle origin-anchored on the price point.
  // Direction (buy → arrow up at entry, sell → down) communicates
  // trade side; ENTRY_COLOR + exitColor (P&L-based) communicate
  // outcome. Text label sits just to the right of the entry arrow.

  // During a press-drag, the mousedown position is the start. Drag takes
  // precedence over a prior click-anchor (the addFib commit discards it).
  const fibPreviewAnchor =
    activeDrawingTool === "fibonacci" && hoverPoint
      ? fibDragStart
        ? { time: fibDragStart.time, price: fibDragStart.price }
        : fibAnchor
      : null;
  const previewFib =
    fibPreviewAnchor && hoverPoint ? fibPreview(fibPreviewAnchor, hoverPoint) : null;

  // Path-draft preview — accumulated vertices + a "rubber band"
  // segment from the last vertex to the cursor. Rendered only while
  // the path tool is active.
  const previewPath: PathDrawing | null =
    activeDrawingTool === "path" && (pathDraft?.length ?? 0) > 0
      ? pathPreview(pathDraft as PathPoint[], hoverPoint)
      : null;

  // Same two-anchor preview for diagonal lines.
  const diagonalPreviewAnchor =
    activeDrawingTool === "diagonal" && hoverPoint
      ? fibDragStart
        ? { time: fibDragStart.time, price: fibDragStart.price }
        : diagonalAnchor
      : null;
  const previewDiagonal: DiagonalLine | null =
    diagonalPreviewAnchor && hoverPoint
      ? diagonalPreview(diagonalPreviewAnchor, hoverPoint)
      : null;

  // Same two-anchor logic for long/short positions. The preview's
  // entry/TP/SL math mirrors `buildPosition` inside the placement
  // effect; the user sees the box stretch live as they move the
  // cursor between click-1 and click-2 (or during press-drag).
  const positionPreviewAnchor =
    (activeDrawingTool === "long" || activeDrawingTool === "short") && hoverPoint
      ? fibDragStart
        ? { time: fibDragStart.time, price: fibDragStart.price }
        : positionAnchor
      : null;
  const previewPosition: Position | null =
    positionPreviewAnchor && hoverPoint
      ? positionPreview(
          activeDrawingTool === "long" ? "long" : "short",
          positionPreviewAnchor,
          hoverPoint,
        )
      : null;

  // Two-anchor preview for the price-range box (press-drag, or between
  // click-1 and click-2). Mirrors the diagonal preview.
  const priceRangePreviewAnchor =
    activeDrawingTool === "priceRange" && hoverPoint
      ? fibDragStart
        ? { time: fibDragStart.time, price: fibDragStart.price }
        : priceRangeAnchor
      : null;
  const previewPriceRange: PriceRangeBox | null =
    priceRangePreviewAnchor && hoverPoint
      ? priceRangePreview(priceRangePreviewAnchor, hoverPoint)
      : null;

  const isDrawing = activeDrawingTool != null;

  // In-chart symbol+OHLC header — TradingView-style top-left overlay.
  // Uses the latest candle's OHLC and computes change vs the previous
  // bar's close. Re-renders for free as `candles` is updated by the
  // tick stream (rolling close) and the ohlc fetch.
  // Compact datetime-local-ish string ("YYYY-MM-DDTHH:mm") → "MMM d HH:mm".
  // Tucked here (not a util) because it's the only place we need it
  // and the conversion stays trivial.
  const formatAnalysisStamp = (s: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s);
    if (!m) return s;
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    const month = months[parseInt(m[2], 10) - 1] ?? m[2];
    return `${month} ${parseInt(m[3], 10)} ${m[4]}:${m[5]}`;
  };

  const lastCandle = candles.length > 0 ? candles[candles.length - 1] : null;

  // --- GC futures panes (CVD / open interest) -------------------------------
  // Only GC has these series, so every other symbol renders exactly as before:
  // the strip isn't mounted and the chart keeps its full-bleed `inset: 0`.
  const showGCPanels = symbol === "GC" && !signalMode;
  const GC_STRIP_H = 3 * 116 + 30; // three panes + the controls row
  const gcRange = useMemo(() => {
    if (!showGCPanels || candles.length === 0) return null;
    const iso = (t: number) => new Date(t * 1000).toISOString().slice(0, 10);
    return {
      start: iso(Number(candles[0].time)),
      end: iso(Number(candles[candles.length - 1].time)),
    };
  }, [showGCPanels, candles]);
  // Daily closes drive the price/OI quadrant colouring. Taken from the candles
  // already loaded rather than a second fetch; on an intraday timeframe the last
  // write for a date wins, which is that day's close.
  const gcDailyClose = useMemo(() => {
    const m: Record<string, number> = {};
    if (!showGCPanels) return m;
    for (const c of candles) {
      m[new Date(Number(c.time) * 1000).toISOString().slice(0, 10)] = c.close;
    }
    return m;
  }, [showGCPanels, candles]);
  // The panes render one point per price bar, so their logical indexing matches
  // this chart's exactly and the range sync lands on the same moment in both.
  const gcBarTimes = useMemo(
    () => (showGCPanels ? candles.map((c) => Number(c.time)) : []),
    [showGCPanels, candles],
  );
  const prevCandle = candles.length > 1 ? candles[candles.length - 2] : null;
  const change =
    lastCandle && prevCandle ? lastCandle.close - prevCandle.close : 0;
  const changePct =
    lastCandle && prevCandle && prevCandle.close
      ? (change / prevCandle.close) * 100
      : 0;
  const changeDir =
    change > 0 ? styles.ohlcUp : change < 0 ? styles.ohlcDown : "";

  const lineCtx: LineRenderCtx = {
    series: seriesRef.current,
    timeToX,
    theme,
    selectedDrawing,
    tabId,
    startDrag,
    startLockedPan,
    showMenu,
    dispatch,
    digits: digitsFor(symbol),
    plotWidth: chartRef.current?.timeScale().width() ?? 0,
  };

  const textCtx: TextRenderCtx = {
    series: seriesRef.current,
    timeToX,
    selectedDrawing,
    tabId,
    editingId: editingText?.id ?? null,
    onEdit: (label) => setEditingText({ id: label.id, value: label.text }),
    textColor: theme.textLabelColor ?? chrome.textColor,
    defaultFontSize: theme.textLabelFontSize ?? 14,
    defaultBold: theme.textLabelBold ?? false,
    startDrag,
    startLockedPan,
    showMenu,
    dispatch,
  };

  const fibCtx: FibRenderCtx = {
    series: seriesRef.current,
    seriesRef,
    chartRef,
    containerRef,
    dragRef,
    dispatch,
    tabId,
    setIsDragging,
    timeToX,
    selectedDrawing,
    startDrag,
    startLockedPan,
    showMenu,
    fibLevels,
    fibLineWidth,
    fibLineStyle,
    fibDiagonal,
    fibShowDiagonal,
    fibLabelSide,
    fibMenuItems,
    digits: digitsFor(symbol),
    textColor: chrome.textColor,
  };

  const positionCtx: PositionRenderCtx = {
    seriesRef,
    chartRef,
    containerRef,
    dragRef,
    timeToX,
    selectedDrawing,
    startDrag,
    showMenu,
    dispatch,
    tabId,
    setIsDragging,
    positionMenuItems,
    digits: digitsFor(symbol),
  };

  const priceRangeCtx: PriceRangeRenderCtx = {
    seriesRef,
    chartRef,
    containerRef,
    dragRef,
    timeToX,
    selectedDrawing,
    startDrag,
    startLockedPan,
    showMenu,
    dispatch,
    tabId,
    setIsDragging,
    priceRangeMenuItems,
    digits: digitsFor(symbol),
    pipSize: pipSizeFor(symbol),
    barSeconds: TIMEFRAME_SECONDS[timeframe] ?? 3600,
    boxColor: priceRangeStyle.color,
    textMatch: priceRangeStyle.textMatch,
    labelColorPref: priceRangeStyle.textColor,
    labelSide: priceRangeStyle.labelSide,
    boxWidth: priceRangeStyle.boxWidth,
    labelWeight: priceRangeStyle.labelWeight,
    arrow: priceRangeStyle.arrow,
    midline: priceRangeStyle.midline,
    midlineStyle: priceRangeStyle.midlineStyle,
    midlineWidth: priceRangeStyle.midlineWidth,
  };

  const pathCtx: PathRenderCtx = {
    seriesRef,
    chartRef,
    containerRef,
    candlesRef,
    timeToX,
    selectedDrawing,
    theme,
    dispatch,
    tabId,
    startDrag,
    startLockedPan,
    showMenu,
    haloColor: chrome.bg,
  };

  const tradingStripsCtx: TradingStripsCtx = {
    seriesRef,
    chartRef,
    containerRef,
    activeDrawingTool,
    canTrade: isOwnerForOverlay,
    tradingConnected,
    showTradingTrades,
    showTradingSlTp,
    tradingPositions,
    tradingOrders,
    symbol,
    startTradingDrag,
    setHighlightGroup,
    onTradeClick: (scope, ticket) => {
      openSlTpDraft(scope, ticket);
    },
  };

  const draftCtx: DraftRenderCtx = {
    seriesRef,
    chartRef,
    containerRef,
    theme,
    startDraftDrag,
    draftSlTp,
    pendingDraft,
  };

  return (
    <div ref={wrapperRef} className={styles.container}>
      <div
        ref={containerRef}
        className={`${styles.chart} ${isDrawing ? styles.drawing : ""}`}
        style={showGCPanels ? { bottom: GC_STRIP_H } : undefined}
      />
      {showGCPanels && gcRange ? (
        <div className={styles.gcPanelStrip} style={{ height: GC_STRIP_H }}>
          <GCPanels
            tabId={tabId}
            timeframe={timeframe}
            start={gcRange.start}
            end={gcRange.end}
            dailyClose={gcDailyClose}
            barTimes={gcBarTimes}
          />
        </div>
      ) : null}
      {/* trading-style one-click trading panel, docked top-left. Only the
          active chart renders it (background tabs are hidden but mounted),
          and only for the master. */}
      {isActive && oneClickPanelOpen && canTradeWrite ? (
        <OneClickTradePanel symbol={symbol} />
      ) : null}
      {/* Replay controls. Only the active chart renders them — background
          tabs stay mounted but hidden, and two visible play loops on one
          screen would fight over the keyboard. */}
      {isActive && replayActive ? <ReplayBar tabId={tabId} /> : null}
      {/* Replay start picker: the vertical line snapped to the bar under the
          cursor, plus what to do. pointer-events:none throughout so it can't
          swallow the click it exists to advertise. */}
      {isActive && replaySelecting ? (
        <>
          {replayPick ? (
            <div
              className={replayStyles.pickLine}
              style={{ left: `${replayPick.x}px` }}
            >
              <span className={replayStyles.pickStamp}>
                {fmtLineLabel(replayPick.time)}
              </span>
            </div>
          ) : null}
          <div className={replayStyles.pickHint}>
            <span className={replayStyles.replayDot} aria-hidden />
            Click a bar to start the replay there
            <kbd className={replayStyles.kbd}>Esc</kbd>
            to cancel
          </div>
        </>
      ) : null}
      {lastCandle ? (
        <div className={styles.symbolHeader}>
          <div className={styles.symbolRow}>
            <span className={styles.symbolName}>{symbol}</span>
            <span className={styles.symbolTf}>
              {tfShortLabel(timeframe)}
            </span>
          </div>
          <div className={styles.ohlcRow}>
            <span className={styles.ohlcLabel}>
              O<span className={styles.ohlcValue}>{lastCandle.open.toFixed(digitsFor(symbol))}</span>
            </span>
            <span className={styles.ohlcLabel}>
              H<span className={styles.ohlcValue}>{lastCandle.high.toFixed(digitsFor(symbol))}</span>
            </span>
            <span className={styles.ohlcLabel}>
              L<span className={styles.ohlcValue}>{lastCandle.low.toFixed(digitsFor(symbol))}</span>
            </span>
            <span className={styles.ohlcLabel}>
              C<span className={`${styles.ohlcValue} ${changeDir}`}>{lastCandle.close.toFixed(digitsFor(symbol))}</span>
            </span>
            {prevCandle ? (
              <span className={`${styles.ohlcChange} ${changeDir}`}>
                {change >= 0 ? "+" : ""}
                {change.toFixed(digitsFor(symbol))} ({changePct >= 0 ? "+" : ""}
                {changePct.toFixed(2)}%)
              </span>
            ) : null}
          </div>
          {/* Analysis period — only when this tab has an analysis
              loaded. Different modes render slightly differently:
                static       → "Analysis: Apr 2 05:00 → Apr 2 06:00"
                live-prefetch → "Analysis: Apr 2 05:00 → Apr 2 14:23 · ● LIVE"
                live          → "Analysis: ● LIVE since Apr 2 14:23" */}
          {analyzedFor ? (
            <div className={styles.analysisRow}>
              <span className={styles.analysisLabel}>Analysis:</span>
              {analyzedFor.mode === "live" ? (
                <>
                  <span className={styles.analysisLiveDot} aria-hidden />
                  <span className={styles.analysisModeLive}>LIVE</span>
                  <span className={styles.analysisRange}>
                    since {formatAnalysisStamp(analyzedFor.start)}
                  </span>
                </>
              ) : (
                <>
                  <span className={styles.analysisRange}>
                    {formatAnalysisStamp(analyzedFor.start)} →{" "}
                    {formatAnalysisStamp(analyzedFor.end)}
                  </span>
                  {analyzedFor.mode === "live-prefetch" ? (
                    <>
                      <span className={styles.analysisLiveDot} aria-hidden />
                      <span className={styles.analysisModeLive}>LIVE</span>
                    </>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
      {(newsMarkers.length > 0 ||
        weeklyBandRuns.length > 0 ||
        fibRetracements.length > 0 ||
        previewFib ||
        horizontalLines.length > 0 ||
        verticalLines.length > 0 ||
        diagonalLines.length > 0 ||
        previewDiagonal ||
        paths.length > 0 ||
        previewPath ||
        positions.length > 0 ||
        previewPosition ||
        priceRangeBoxes.length > 0 ||
        previewPriceRange ||
        textLabels.length > 0 ||
        historyTrips.length > 0 ||
        (!activeDrawingTool &&
          isOwnerForOverlay &&
          tradingConnected &&
          showTradingTrades &&
          (tradingPositions.some((p) => p.symbol === symbol) ||
            tradingOrders.some((o) => o.symbol === symbol))) ||
        (signalMode &&
          showTradingTrades &&
          (tradingPositions.some((p) => p.symbol === symbol) ||
            tradingOrders.some((o) => o.symbol === symbol))) ||
        draftSlTp ||
        pendingDraft) && (
        <svg
          className={styles.fibOverlay}
          width="100%"
          height="100%"
          preserveAspectRatio="none"
        >
          {/* Shared arrowhead marker for paths (referenced as
              `markerEnd="url(#arrowhead-path)"`). markerUnits=
              "userSpaceOnUse" so the size is in raw px and doesn't
              scale with stroke width. */}
          <defs>
            <marker
              id="arrowhead-path"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="5"
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <polygon
                points="0,0 10,5 0,10"
                fill={theme.arrowColor ?? "#2962FF"}
              />
            </marker>
          </defs>
          {/* Weekly Behaviour session bands — rendered FIRST so
              every other drawing (fibs, paths, positions, history
              markers) sits on top and stays readable. */}
          {renderWeeklyBands(weeklyBandRuns, candles, timeToX)}
          {/* High-impact news markers — dashed verticals + a flag at the
              axis. Read-only (no hit area on the line), so they sit above
              the bands but can never steal a pointer from a drawing. */}
          {renderNewsMarkers(newsMarkers, {
            timeToX,
            plotHeight,
            hoveredTime: hoveredNewsTime,
            setHoveredTime: setHoveredNewsTime,
          })}
          {/* Trading SL/TP/entry drag strips render BEFORE the drawings so
              that when a price line coincides with a drawing (fib level,
              horizontal line, path vertex…) the DRAWING sits on top and wins
              the pointer — the user can still grab/drag the drawing. The
              active drafts below stay on top (transient interactions). */}
          {renderTradingDragStrips(tradingStripsCtx)}
          {/* Signal-mode order levels — drawn here as plain SVG lines (not
              native price lines). Every line starts at x=0 with the same
              "2 3" dotted dash pattern, so their dots align into vertical
              columns (MT5's "screen-door" texture) instead of filling each
              other's gaps into a solid band the way independently-phased
              native price lines do. Each line also gets a tiny LEFT-edge text
              label (BUY / SELL / SL / TP) with NO background — like MT5 — so
              with hundreds of orders the labels partially overlap but the
              text stays legible and you can still tell buy from sell and
              entry from SL/TP. Read-only: no hit area. */}
          {signalMode &&
            showTradingTrades &&
            seriesRef.current &&
            (() => {
              const series = seriesRef.current!;
              const longC = theme.posLongColor ?? "#26a69a";
              const shortC = theme.posShortColor ?? "#ef5350";
              const lvls: {
                key: string;
                price: number;
                color: string;
                label: string;
              }[] = [];
              // Strategies mode: colour + prefix each level with its strategy
              // (looked up by magic number), so several strategies' orders stay
              // distinguishable on one chart.
              const meta = (magic: number) =>
                strategiesMode ? stratMeta.get(magic) : undefined;
              for (const p of tradingPositions) {
                if (p.symbol !== symbol) continue;
                const isLong = p.type === 0;
                const m = meta(p.magic);
                lvls.push({
                  key: `sig-pe-${p.ticket}`,
                  price: p.price_open,
                  color: m?.color ?? (isLong ? longC : shortC),
                  label: `${m ? `${m.label} ` : ""}${isLong ? "BUY" : "SELL"}`,
                });
                if (showTradingSlTp && p.sl > 0)
                  lvls.push({ key: `sig-ps-${p.ticket}`, price: p.sl, color: theme.slColor, label: "SL" });
                if (showTradingSlTp && p.tp > 0)
                  lvls.push({ key: `sig-pt-${p.ticket}`, price: p.tp, color: theme.tpColor, label: "TP" });
              }
              for (const o of tradingOrders) {
                if (o.symbol !== symbol) continue;
                const m = meta(o.magic);
                lvls.push({
                  key: `sig-oe-${o.ticket}`,
                  price: o.price_open,
                  // Pending orders show their FULL type (BUY LIMIT / SELL STOP /
                  // …), not just BUY/SELL — otherwise a pending order is
                  // indistinguishable from a running position, which also
                  // labels BUY/SELL. type_label is "buy_limit" etc.; render it
                  // MT5-style with a space.
                  color: m?.color ?? theme.pendingOrder,
                  label: `${m ? `${m.label} ` : ""}${o.type_label.replace(/_/g, " ").toUpperCase()}`,
                });
                if (showTradingSlTp && o.sl > 0)
                  lvls.push({ key: `sig-os-${o.ticket}`, price: o.sl, color: theme.slColor, label: "SL" });
                if (showTradingSlTp && o.tp > 0)
                  lvls.push({ key: `sig-ot-${o.ticket}`, price: o.tp, color: theme.tpColor, label: "TP" });
              }
              return (
                <g>
                  {lvls.map(({ key, price, color }) => {
                    const y = series.priceToCoordinate(price);
                    if (y == null) return null;
                    return (
                      <line
                        key={key}
                        x1="0"
                        x2="100%"
                        y1={y}
                        y2={y}
                        stroke={color}
                        strokeWidth={1}
                        strokeDasharray="2 3"
                      />
                    );
                  })}
                  {/* Labels drawn AFTER the lines so text sits on top. No
                      background rect — overlapping labels stay partly readable. */}
                  {lvls.map(({ key, price, color, label }) => {
                    const y = series.priceToCoordinate(price);
                    if (y == null) return null;
                    return (
                      <text
                        key={`t-${key}`}
                        x={3}
                        y={y as number}
                        dy="0.32em"
                        fontSize={9}
                        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                        fontWeight={400}
                        fill={color}
                        style={{ pointerEvents: "none" }}
                      >
                        {label}
                      </text>
                    );
                  })}
                </g>
              );
            })()}
          {horizontalLines.map((l) => renderHorizontalLine(l, lineCtx))}
          {verticalLines.map((l) => renderVerticalLine(l, lineCtx))}
          {diagonalLines.map((l) => renderDiagonalLine(l, 1, l.id, lineCtx))}
          {previewDiagonal &&
            renderDiagonalLine(previewDiagonal, 0.5, "preview-diagonal", lineCtx)}
          {paths.map((p) => renderPath(p, 1, p.id, pathCtx))}
          {previewPath && renderPath(previewPath, 0.5, "preview-path", pathCtx)}
          {positions.map((pos) => renderPosition(pos, false, positionCtx))}
          {previewPosition && renderPosition(previewPosition, true, positionCtx)}
          {priceRangeBoxes.map((b) => renderPriceRange(b, false, priceRangeCtx))}
          {previewPriceRange &&
            renderPriceRange(previewPriceRange, true, priceRangeCtx)}
          {fibRetracements.map((fib) => renderFib(fib, 1, fib.id, fibCtx))}
          {previewFib && renderFib(previewFib, 0.5, "preview", fibCtx)}
          {textLabels.map((lbl) => renderTextLabel(lbl, textCtx))}
          {historyTrips.map((t) => renderHistoryMarker(t, seriesRef.current, timeToX, timeframe))}
          {renderDraftSlTp(draftCtx)}
          {renderPendingDraft(draftCtx)}
        </svg>
      )}
      {/* Inline text-label editor — overlaid HTML input at the label's
          anchor. Commits on blur / Enter; Escape cancels (and removes the
          label if it was never given any text). */}
      {editingText &&
        (() => {
          const lbl = textLabels.find((l) => l.id === editingText.id);
          if (!lbl) return null;
          const x = timeToX(lbl.time);
          const yc = seriesRef.current?.priceToCoordinate(lbl.price);
          if (x == null || yc == null) return null;
          // Mirror the renderer's effective styling so the editor preview
          // matches the committed label.
          const fontSize = lbl.fontSize ?? theme.textLabelFontSize ?? 14;
          const bold = lbl.bold ?? theme.textLabelBold ?? false;
          const editColor =
            lbl.color ?? theme.textLabelColor ?? chrome.textColor;
          // Match the SVG selection box (textLabelRenderer) rectangle so
          // the box doesn't shift between place / select / edit.
          const boxH = fontSize + 10;
          const commit = () => {
            const v = editingText.value.trim();
            if (v === "") {
              dispatch(removeTextLabel({ tabId, id: editingText.id }));
            } else {
              dispatch(
                updateTextLabel({ tabId, id: editingText.id, text: editingText.value }),
              );
            }
            setEditingText(null);
          };
          const cancel = () => {
            // Discard the draft. A label that was never committed (still
            // empty in the store) is removed entirely.
            if (!lbl.text.trim())
              dispatch(removeTextLabel({ tabId, id: editingText.id }));
            setEditingText(null);
          };
          return (
            <input
              autoFocus
              className={styles.textEditor}
              style={{
                left: x - 4,
                top: yc - fontSize - 4,
                height: boxH,
                lineHeight: `${boxH - 2}px`,
                fontSize,
                fontWeight: bold ? "bold" : "normal",
                color: editColor,
              }}
              value={editingText.value}
              placeholder="Text"
              onChange={(e) =>
                setEditingText({ id: editingText.id, value: e.target.value })
              }
              onBlur={commit}
              onFocus={(e) => e.currentTarget.select()}
              onKeyDown={(e) => {
                // Don't let chart-level shortcuts (Delete/Esc/etc.) fire
                // while typing.
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  commit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancel();
                }
              }}
            />
          );
        })()}
      {/* Draft pending-order bar — set volume + expiry, then Submit. */}
      {pendingDraft
        ? (() => {
            const yc = seriesRef.current?.priceToCoordinate(pendingDraft.price);
            if (yc == null) return null;
            return (
              <div
                className={styles.draftBar}
                style={{ top: Math.max(8, yc + 8) }}
              >
                <span className={styles.draftLabel}>
                  {pendingDraft.side === "buy" ? "Buy" : "Sell"}{" "}
                  {pendingDraft.kind}
                </span>
                <span className={styles.draftPrice}>
                  {pendingDraft.price.toFixed(digitsFor(symbol))}
                </span>
                <span className={styles.draftField}>
                  <button
                    type="button"
                    className={styles.draftStep}
                    onClick={() =>
                      commitDraftVolume(
                        Math.max(
                          0.01,
                          Math.round((pendingDraft.volume - 0.01) * 100) / 100,
                        ),
                      )
                    }
                  >
                    −
                  </button>
                  <input
                    className={styles.draftVol}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={pendingDraft.volume}
                    onChange={(e) => commitDraftVolume(Number(e.target.value))}
                  />
                  <button
                    type="button"
                    className={styles.draftStep}
                    onClick={() =>
                      commitDraftVolume(
                        Math.round((pendingDraft.volume + 0.01) * 100) / 100,
                      )
                    }
                  >
                    +
                  </button>
                </span>
                <select
                  className={styles.draftExpiry}
                  value={pendingDraft.expiry}
                  onChange={(e) =>
                    setPendingDraft((d) =>
                      d
                        ? {
                            ...d,
                            expiry: e.target
                              .value as typeof d.expiry,
                          }
                        : d,
                    )
                  }
                >
                  <option value="gtc">GTC</option>
                  <option value="day">Today</option>
                  <option value="specified_day">Specified day</option>
                  <option value="specified">Specified</option>
                </select>
                {pendingDraft.expiry === "specified_day" ||
                pendingDraft.expiry === "specified" ? (
                  <DateTimePicker
                    className={styles.draftExpiry}
                    mode={
                      pendingDraft.expiry === "specified" ? "datetime" : "date"
                    }
                    value={pendingDraft.expireAt}
                    onChange={(v) =>
                      setPendingDraft((d) => (d ? { ...d, expireAt: v } : d))
                    }
                    ariaLabel="Expiry date"
                    placeholder="Pick date"
                  />
                ) : null}
                <button
                  type="button"
                  className={styles.draftSubmit}
                  disabled={pendingSubmitting}
                  onClick={() => void submitPendingDraft()}
                >
                  {pendingSubmitting ? "…" : "Submit"}
                </button>
                <button
                  type="button"
                  className={styles.draftCancel}
                  onClick={cancelPendingDraft}
                >
                  Cancel
                </button>
              </div>
            );
          })()
        : null}
      {/* Draft SL/TP confirm bar — only commits to the broker on Submit. */}
      {draftSlTp
        ? (() => {
            const sr = seriesRef.current;
            const db = draftSlTp;
            // The live trade this bar is managing. It can vanish under us —
            // the position closes, the order fills — in which case there is
            // nothing left to manage and the bar should go.
            const trade =
              db.scope === "pos"
                ? tradingPositions.find((x) => x.ticket === db.ticket)
                : tradingOrders.find((x) => x.ticket === db.ticket);
            if (!trade) return null;
            // Levels the broker is ALREADY holding, as opposed to drafts.
            // Only one of the two can apply per side: a level is either set
            // (removable) or missing (draftable).
            const liveSl = db.sl == null && trade.sl > 0 ? trade.sl : null;
            const liveTp = db.tp == null && trade.tp > 0 ? trade.tp : null;
            // Anchor under the LOWEST line in play so the bar never covers
            // one. Falls back to the entry, which is the only line a trade
            // with both levels set and nothing drafted still shows.
            const ys: number[] = [];
            for (const v of [db.sl, db.tp, liveSl, liveTp, trade.price_open]) {
              if (v == null || !(v > 0)) continue;
              const y = sr?.priceToCoordinate(v);
              if (y != null) ys.push(y as number);
            }
            if (ys.length === 0) return null;
            const yc = Math.max(...ys);
            const dg = digitsFor(symbol);
            const canSubmit = db.touched.sl || db.touched.tp;
            return (
              <div
                className={styles.draftBar}
                data-draft-bar="1"
                style={{ top: Math.max(8, yc + 8) }}
              >
                {(["sl", "tp"] as const).map((f) => {
                  const label = f === "sl" ? "Stop loss" : "Take profit";
                  const draft = f === "sl" ? db.sl : db.tp;
                  const live = f === "sl" ? liveSl : liveTp;
                  // A level already at the broker: show it with a way to
                  // clear it. Some traders run a stop with no target (or the
                  // reverse) and need to take one off without touching the
                  // other.
                  if (live != null) {
                    return (
                      <span key={f} className={styles.draftGroup}>
                        <span className={styles.draftLabel}>{label}</span>
                        <span className={styles.draftPrice}>
                          {live.toFixed(dg)}
                        </span>
                        <button
                          type="button"
                          className={styles.draftRemove}
                          disabled={draftSubmitting}
                          title={`Remove ${f.toUpperCase()}`}
                          aria-label={`Remove ${label}`}
                          onClick={() => void removeDraftLevel(f)}
                        >
                          ✕
                        </button>
                      </span>
                    );
                  }
                  if (draft == null) return null;
                  const pnl = draftPnl(db.scope, db.ticket, draft);
                  const chosen = db.touched[f];
                  return (
                    <span
                      key={f}
                      className={`${styles.draftGroup} ${
                        chosen ? "" : styles.draftProposed
                      }`}
                    >
                      <span className={styles.draftLabel}>{label}</span>
                      <span className={styles.draftPrice}>
                        {draft.toFixed(dg)}
                      </span>
                      {pnl != null && chosen && (
                        <span
                          className={`${styles.draftPnl} ${
                            pnl < 0 ? styles.draftPnlLoss : styles.draftPnlGain
                          }`}
                        >
                          {fmtPnl(pnl)}
                        </span>
                      )}
                      {/* Untouched = a suggestion we drew, not a number the
                          user picked. Say so, so nobody presses Submit
                          expecting this exact price to be sent. */}
                      {!chosen && (
                        <span className={styles.draftHint}>drag to set</span>
                      )}
                    </span>
                  );
                })}
                <button
                  type="button"
                  className={styles.draftSubmit}
                  disabled={draftSubmitting || !canSubmit}
                  title={
                    canSubmit
                      ? undefined
                      : "Drag a stop loss or take profit line first"
                  }
                  onClick={() => void submitDraft()}
                >
                  {draftSubmitting ? "…" : "Submit"}
                </button>
                {/* Close / cancel, on the bar rather than buried in the
                    right-click menu — a trader should not have to discover a
                    hidden menu to get out of a trade. */}
                <button
                  type="button"
                  className={styles.draftDanger}
                  disabled={draftSubmitting}
                  onClick={closeDraftTrade}
                >
                  {db.scope === "pos" ? "Close" : "Cancel order"}
                </button>
                {/* Dismiss, NOT "Cancel" — next to "Cancel order" that word
                    would read as cancelling the trade. */}
                <button
                  type="button"
                  className={styles.draftCancel}
                  onClick={cancelDraft}
                >
                  Dismiss
                </button>
              </div>
            );
          })()
        : null}
      {status === "loading" && <div className={styles.status}>Loading…</div>}
      {status === "failed" && (
        <div className={`${styles.status} ${styles.error}`}>
          <span>{error ?? "Failed to load OHLC"}</span>
          <button
            type="button"
            className={styles.retryButton}
            onClick={() => dispatch(retryOhlc({ tabId }))}
          >
            Retry
          </button>
        </div>
      )}
      {isLoadingMore && status === "succeeded" && (
        <div className={styles.loadingMore}>Loading older…</div>
      )}
      {activeDrawingTool === "fibonacci" && (
        <div className={styles.drawingHint}>
          {fibAnchor
            ? "Click second point to complete fib"
            : "Click or press-and-drag to draw fib"}
        </div>
      )}
      {activeDrawingTool === "horizontal" && (
        <div className={styles.drawingHint}>Click to place horizontal line</div>
      )}
      {activeDrawingTool === "vertical" && (
        <div className={styles.drawingHint}>Click to place vertical line</div>
      )}
      {activeDrawingTool === "diagonal" && (
        <div className={styles.drawingHint}>
          {diagonalAnchor
            ? "Click second point to complete trend line"
            : "Click or press-and-drag to draw trend line"}
        </div>
      )}
      {activeDrawingTool === "path" && (
        <div className={styles.drawingHint}>
          {(pathDraft?.length ?? 0) === 0
            ? "Click to add path vertices"
            : `${pathDraft?.length ?? 0} point${(pathDraft?.length ?? 0) === 1 ? "" : "s"} — Esc / double-click to finish`}
        </div>
      )}
      {activeDrawingTool === "long" && (
        <div className={styles.drawingHint}>
          {positionAnchor
            ? "Click TP to complete long (default 1:3 R:R)"
            : "Click entry then TP, or press-and-drag to draw long"}
        </div>
      )}
      {activeDrawingTool === "short" && (
        <div className={styles.drawingHint}>
          {positionAnchor
            ? "Click TP to complete short (default 1:3 R:R)"
            : "Click entry then TP, or press-and-drag to draw short"}
        </div>
      )}
      {activeDrawingTool === "priceRange" && (
        <div className={styles.drawingHint}>
          {priceRangeAnchor
            ? "Click the second point to measure the range"
            : "Click two points, or press-and-drag to measure price / pips / %"}
        </div>
      )}
      {activeDrawingTool === "text" && (
        <div className={styles.drawingHint}>Click to place a text annotation</div>
      )}
      {contextMenu && (
        <div
          ref={menuRef}
          data-chart-menu="1"
          className={`${styles.contextMenu} ${menuPos?.openLeft ? styles.openLeft : ""}`}
          style={{
            top: menuPos?.top ?? contextMenu.y,
            left: menuPos?.left ?? contextMenu.x,
          }}
        >
          {renderMenuItems(contextMenu.items, () => setContextMenu(null))}
        </div>
      )}

      {/* Per-fib level visibility editor. Opened from a fib's
          right-click "Levels…"; toggles write the fib's `hiddenLevels`
          override via updateFib (this drawing only). */}
      {(() => {
        const editingFib = fibLevelsEditorId
          ? fibRetracements.find((f) => f.id === fibLevelsEditorId)
          : undefined;
        return (
          <FibDrawingLevelsDialog
            open={!!editingFib}
            levels={fibLevels}
            hidden={editingFib?.hiddenLevels ?? []}
            onChange={(hiddenLevels) => {
              if (editingFib)
                dispatch(
                  updateFib({ tabId, id: editingFib.id, hiddenLevels }),
                );
            }}
            hideDiagonal={editingFib?.hideDiagonal ?? false}
            onToggleDiagonal={(hideDiagonal) => {
              if (editingFib)
                dispatch(
                  updateFib({ tabId, id: editingFib.id, hideDiagonal }),
                );
            }}
            labelSide={editingFib?.labelSide ?? "right"}
            onLabelSide={(labelSide) => {
              if (editingFib)
                dispatch(
                  updateFib({ tabId, id: editingFib.id, labelSide }),
                );
            }}
            onClose={() => setFibLevelsEditorId(null)}
          />
        );
      })()}
    </div>
  );
}
